import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../css/onboarding.css';

const ONBOARDING_VERSION = 1;
const TOUR_STATE_ENDPOINT = '/tours/state';
const SUGGESTION_DELAY_MS = 750;
const TOUR_MODAL_IDS = new Set(['lexiusTourWelcomeModal', 'lexiusTourCenterModal', 'lexiusTourAuthModal']);
const HELP_TOUR_SURFACES = [
  {
    selector: '[data-tour="document-edition-workspace"] .artifact-difference__entry',
    tourId: 'document-edition'
  },
  { selector: '.tool-workspace__panel--review', tourId: 'document-review' },
  { selector: '#alertsModal', tourId: 'alerts' },
  { selector: '#userFilesSelectModal', tourId: 'files' },
  { selector: '#chatModal', tourId: 'lawyer' },
  { selector: '#audioCallModal', tourId: 'voice-calls' },
  { selector: '#documentSummaryModal', tourId: 'virtual-assistant' },
  { selector: '#intelligentSummaryModal', tourId: 'intelligent-analysis' },
  {
    selector: '#libraryModal',
    tourId: 'notes',
    missingDocumentMessage: 'Abre cualquier documento antes de iniciar este recorrido.'
  },
  { selector: '#markmapModal', tourId: 'document', closeSurface: true }
];
const FEATURE_MENU_INLINE_ICON = '<span class="lexius-tour-feature-menu-icon" aria-hidden="true"><i class="anticon anticon-ellipsis"></i></span>';
const SEARCH_RESULT_EXTRACT_SELECTOR = '[data-tour="search-result-extract"], .search-extract-text';
const SEARCH_RESULT_ANALYSIS_SELECTOR = '[data-tour="search-result-analysis"], .smart-result-summary-btn';
const SEARCH_RESULT_OPEN_SELECTOR = '[data-tour="search-result-open"], .open-expedient-btn';

let activeRun = null;
let lastLauncher = null;
let autoPromptPending = false;
let autoPromptHandled = false;
let autoPromptTimer = null;
let manualTourRequest = false;
let tourStartPending = false;
let lastUserInteractionAt = Date.now();
let returnFocusElement = null;
let activeSearchSurface = null;
let activeSearchResultsSurface = null;
let tourResizeFrame = null;
let activeSuggestion = null;
let pendingSuggestion = null;
let documentContextFrame = null;
let chatResponseFrame = null;
let documentEditionFrame = null;
let activeDocumentEditionWorkspace = null;
let documentReviewFrame = null;
let searchResultsContextFrame = null;
let tourState = createEmptyTourState();
let tourStateSubject = '';
let tourStateLoaded = false;
let tourStateLoadPromise = null;
let tourStateLoadSubject = '';
let tourStateRefreshTimer = null;
let tourScrollFrame = null;
let tourResetPending = false;
let tourPreferencePending = false;
let activeDocumentFeatureSurface = null;
let activeTourDisableConfirmation = null;
let tourDisableConfirmationHideTimer = null;

function wait(milliseconds) {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

function withFeatureMenuIcon(description) {
  return description.replace('tres puntos', `tres puntos ${FEATURE_MENU_INLINE_ICON}`);
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function readCookie(name) {
  const prefix = `${name}=`;
  const entry = String(document.cookie || '')
    .split(';')
    .map(item => item.trim())
    .find(item => item.startsWith(prefix));
  if (!entry) return '';
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch (error) {
    return entry.slice(prefix.length);
  }
}

function parseJwtPayload(token) {
  try {
    const normalizedToken = String(token || '').replace(/^Bearer\s+/i, '');
    const payload = normalizedToken.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = window.atob(normalized + padding);
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
}

function getAuthState() {
  const ready = typeof window.user_not_logged_in === 'boolean';
  const payload = parseJwtPayload(readCookie('id_token'));
  const expiresAt = payload && Number(payload.exp || 0) * 1000;
  const tokenLooksValid = !!(
    payload
    && String(payload.sub || '').trim()
    && (!expiresAt || expiresAt > Date.now())
  );

  if (!ready) {
    return { status: 'loading', authenticated: false, paid: false, groups: [], subject: '' };
  }

  if (window.user_not_logged_in === true || !tokenLooksValid) {
    return { status: 'anonymous', authenticated: false, paid: false, groups: [], subject: '' };
  }

  const groups = Array.isArray(payload['cognito:groups']) ? payload['cognito:groups'] : [];
  return {
    status: 'authenticated',
    authenticated: true,
    paid: groups.includes('Premium'),
    unlimitedTrial: groups.includes('PruebaIlimitada'),
    groups,
    subject: String(payload.sub).trim(),
    email: String(payload.email || window.user_email || '').trim()
  };
}

function waitForAuthState(timeoutMilliseconds = 7000) {
  const immediate = getAuthState();
  if (immediate.status !== 'loading') return Promise.resolve(immediate);

  return new Promise(resolve => {
    const startedAt = Date.now();
    let settled = false;

    const finish = state => {
      if (settled) return;
      settled = true;
      window.removeEventListener('lexius-auth-ready', check);
      window.clearInterval(interval);
      resolve(state);
    };

    const check = () => {
      const state = getAuthState();
      if (state.status !== 'loading') {
        finish(state);
      } else if (Date.now() - startedAt >= timeoutMilliseconds) {
        finish({ status: 'anonymous', authenticated: false, paid: false, groups: [], subject: '' });
      }
    };

    const interval = window.setInterval(check, 100);
    window.addEventListener('lexius-auth-ready', check);
    check();
  });
}

function waitForSyncState(timeoutMilliseconds = 2500) {
  if (typeof window.sync_enabled === 'boolean') {
    return Promise.resolve(window.sync_enabled === true);
  }

  return new Promise(resolve => {
    const startedAt = Date.now();
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener('sync-status-updated', check);
      window.clearInterval(interval);
      resolve(window.sync_enabled === true);
    };
    const check = () => {
      if (typeof window.sync_enabled === 'boolean' || Date.now() - startedAt >= timeoutMilliseconds) {
        finish();
      }
    };
    const interval = window.setInterval(check, 100);
    window.addEventListener('sync-status-updated', check);
    check();
  });
}

function createEmptyTourState() {
  return {
    version: ONBOARDING_VERSION,
    tours_enabled: true,
    tours_preference_version: 0,
    auto_prompt_status: null,
    tours: {}
  };
}

function normalizeTourPreferenceVersion(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : 0;
}

function normalizeTourState(payload) {
  const source = payload?.state && typeof payload.state === 'object' ? payload.state : payload;
  if (!source || Number(source.version) !== ONBOARDING_VERSION) return createEmptyTourState();

  const tours = {};
  if (source.tours && typeof source.tours === 'object' && !Array.isArray(source.tours)) {
    Object.entries(source.tours).forEach(([tourId, value]) => {
      if (!value || typeof value !== 'object') return;
      const suggested = value.suggested === true || value.completed === true;
      const completed = value.completed === true;
      if (!suggested && !completed) return;
      tours[tourId] = {
        suggested,
        completed,
        suggested_at: value.suggested_at || null,
        completed_at: value.completed_at || null,
        updated_at: value.updated_at || null
      };
    });
  }

  let autoPromptStatus = ['dismissed', 'completed'].includes(source.auto_prompt_status)
    ? source.auto_prompt_status
    : null;
  if (tours.overview?.completed) autoPromptStatus = 'completed';
  return {
    version: ONBOARDING_VERSION,
    tours_enabled: source.tours_enabled !== false,
    tours_preference_version: normalizeTourPreferenceVersion(source.tours_preference_version),
    auto_prompt_status: autoPromptStatus,
    tours
  };
}

function resolveTourPreference(currentState, incomingState) {
  const currentVersion = currentState.tours_preference_version;
  const incomingVersion = incomingState.tours_preference_version;
  const useIncoming = incomingVersion > currentVersion
    || (incomingVersion === 0 && currentVersion === 0);
  const source = useIncoming ? incomingState : currentState;
  return {
    tours_enabled: source.tours_enabled !== false,
    tours_preference_version: source.tours_preference_version
  };
}

function replaceTourState(currentState, incomingState) {
  const current = normalizeTourState(currentState);
  const incoming = normalizeTourState(incomingState);
  return {
    ...incoming,
    ...resolveTourPreference(current, incoming)
  };
}

function mergeTourStates(currentState, incomingState) {
  const current = normalizeTourState(currentState);
  const incoming = normalizeTourState(incomingState);
  const tours = { ...current.tours };
  Object.entries(incoming.tours).forEach(([tourId, value]) => {
    const previous = tours[tourId] || {};
    tours[tourId] = {
      suggested: previous.suggested === true || value.suggested === true || value.completed === true,
      completed: previous.completed === true || value.completed === true,
      suggested_at: previous.suggested_at || value.suggested_at || null,
      completed_at: previous.completed_at || value.completed_at || null,
      updated_at: value.updated_at || previous.updated_at || null
    };
  });

  const autoPromptStatus = current.auto_prompt_status === 'completed'
    || incoming.auto_prompt_status === 'completed'
    ? 'completed'
    : (incoming.auto_prompt_status || current.auto_prompt_status || null);
  const preference = resolveTourPreference(current, incoming);
  return {
    version: ONBOARDING_VERSION,
    ...preference,
    auto_prompt_status: autoPromptStatus,
    tours
  };
}

function tourStateMatches(authState) {
  return !!(
    authState?.authenticated
    && authState.subject
    && tourStateSubject === authState.subject
  );
}

function toursAreEnabled(authState) {
  if (!tourStateMatches(authState) || !tourStateLoaded) return true;
  return tourState.tours_enabled !== false;
}

function resetTourState(subject = '') {
  tourState = createEmptyTourState();
  tourStateSubject = subject;
  tourStateLoaded = false;
}

function reconcileVisibleTourState(authState) {
  if (!tourStateMatches(authState)) return;

  const toursEnabled = toursAreEnabled(authState);
  refreshHelpTourAvailability(authState);
  if (!toursEnabled) {
    if (activeTourDisableConfirmation) {
      closeTourDisableConfirmation(false, { restoreFocus: false });
    }
    if (autoPromptTimer) {
      window.clearInterval(autoPromptTimer);
      autoPromptTimer = null;
    }
    autoPromptHandled = true;
    autoPromptPending = false;
    cancelPendingSuggestion();
    if (activeSuggestion && activeSuggestion.recording !== true) {
      dismissFeatureSuggestion({ restoreFocus: false });
    }
    if (activeRun && activeRun.recording !== true) void finishActiveTour(false);

    const welcome = document.getElementById('lexiusTourWelcomeModal');
    if (welcome?.classList.contains('show')) void hideModal(welcome);
  }

  if (
    toursEnabled
    &&
    pendingSuggestion
    && hasTourBeenSuggestedOrCompleted(authState, pendingSuggestion.tourId)
  ) cancelPendingSuggestion();
  if (
    toursEnabled
    &&
    activeSuggestion
    && activeSuggestion.recording !== true
    && getTourProgress(authState, activeSuggestion.tourId).completed
  ) dismissFeatureSuggestion({ restoreFocus: false });

  const welcome = document.getElementById('lexiusTourWelcomeModal');
  if (readAutoDecision(authState) && welcome?.classList.contains('show')) {
    autoPromptHandled = true;
    autoPromptPending = false;
    void hideModal(welcome);
  }

  const center = document.getElementById('lexiusTourCenterModal');
  if (center?.classList.contains('show')) renderTourCenter(authState);
}

function applyTourState(authState, nextState, options = {}) {
  if (
    !authState?.authenticated
    || !authState.subject
    || getAuthState().subject !== authState.subject
  ) return false;
  if (tourStateSubject && tourStateSubject !== authState.subject) resetTourState(authState.subject);
  tourStateSubject = authState.subject;
  tourState = options.replace === true
    ? replaceTourState(tourState, nextState)
    : mergeTourStates(tourState, nextState);
  tourStateLoaded = true;
  reconcileVisibleTourState(authState);
  window.dispatchEvent(new CustomEvent('lexius-tour-state-updated', {
    detail: { state: tourState }
  }));
  return true;
}

async function ensureTourStateLoaded(authState, options = {}) {
  if (!authState?.authenticated || !authState.subject) {
    resetTourState();
    return true;
  }

  if (tourStateSubject !== authState.subject) resetTourState(authState.subject);
  if (tourStateLoaded && options.force !== true) return true;
  if (tourStateLoadPromise && tourStateLoadSubject === authState.subject) {
    if (options.force === true) {
      await tourStateLoadPromise;
      if (getAuthState().subject !== authState.subject) return false;
      return ensureTourStateLoaded(authState, {
        force: true,
        replace: options.replace === true
      });
    }
    return tourStateLoadPromise;
  }

  const expectedSubject = authState.subject;
  const requestPromise = (async () => {
    try {
      const response = await fetch(`${TOUR_STATE_ENDPOINT}?version=${ONBOARDING_VERSION}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`tour_state_${response.status}`);
      const state = await response.json();
      if (getAuthState().subject !== expectedSubject) return false;
      applyTourState(authState, state, { replace: options.replace === true });
      return true;
    } catch (error) {
      console.error('No se pudo cargar el estado de los recorridos:', error);
      return false;
    }
  })();
  let trackedPromise;
  trackedPromise = requestPromise.finally(() => {
    if (tourStateLoadPromise === trackedPromise) {
      tourStateLoadPromise = null;
      tourStateLoadSubject = '';
    }
  });
  tourStateLoadPromise = trackedPromise;
  tourStateLoadSubject = expectedSubject;
  return trackedPromise;
}

async function persistTourEvent(authState, tourId, eventName) {
  const expectedSubject = authState?.subject || '';
  const loaded = await ensureTourStateLoaded(authState);
  if (
    !loaded
    || !expectedSubject
    || getAuthState().subject !== expectedSubject
    || !toursAreEnabled(authState)
  ) {
    return { ok: false, claimed: false };
  }

  try {
    const response = await fetch(TOUR_STATE_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        version: ONBOARDING_VERSION,
        tour_id: tourId,
        event: eventName
      })
    });
    if (!response.ok) throw new Error(`tour_state_${response.status}`);
    const result = await response.json();
    if (getAuthState().subject !== expectedSubject) {
      return { ok: false, claimed: false };
    }
    if (!applyTourState(authState, result.state)) {
      return { ok: false, claimed: false };
    }
    return { ok: true, claimed: result.claimed === true };
  } catch (error) {
    console.error('No se pudo guardar el estado del recorrido:', error);
    return { ok: false, claimed: false };
  }
}

function scheduleTourStateRefresh(delay = 150, attempt = 0, replace = false) {
  if (tourStateRefreshTimer) window.clearTimeout(tourStateRefreshTimer);
  tourStateRefreshTimer = window.setTimeout(async () => {
    tourStateRefreshTimer = null;
    const authState = await waitForAuthState();
    if (!authState.authenticated) return;
    const refreshed = await ensureTourStateLoaded(authState, { force: true, replace });
    if (
      !refreshed
      && attempt < 2
      && getAuthState().subject === authState.subject
    ) {
      scheduleTourStateRefresh(Math.min(2000, 400 * (2 ** attempt)), attempt + 1, replace);
    }
  }, delay);
}

function readAutoDecision(authState) {
  if (!tourStateMatches(authState) || !tourStateLoaded || !tourState.auto_prompt_status) return null;
  return {
    status: tourState.auto_prompt_status,
    version: ONBOARDING_VERSION
  };
}

function saveAutoDecision(authState, status) {
  return persistTourEvent(
    authState,
    'overview',
    status === 'completed' ? 'completed' : 'auto_prompt_dismissed'
  );
}

function readTourProgress(authState) {
  if (!tourStateMatches(authState)) return createEmptyTourState();
  return tourState;
}

async function claimTourSuggestion(authState, tourId) {
  if (!toursAreEnabled(authState)) return false;
  const current = readTourProgress(authState).tours[tourId] || {};
  if (current.suggested || current.completed) return false;
  const result = await persistTourEvent(authState, tourId, 'suggested');
  return result.ok
    && result.claimed
    && getAuthState().subject === authState.subject
    && !getTourProgress(authState, tourId).completed;
}

function markTourCompleted(authState, tourId) {
  return persistTourEvent(authState, tourId, 'completed');
}

function getTourProgress(authState, tourId) {
  const progress = readTourProgress(authState).tours[tourId] || {};
  const overviewDecision = tourId === 'overview' ? readAutoDecision(authState) : null;
  return {
    suggested: progress.suggested === true,
    completed: progress.completed === true || overviewDecision?.status === 'completed'
  };
}

function hasTourBeenSuggestedOrCompleted(authState, tourId) {
  const progress = getTourProgress(authState, tourId);
  return progress.suggested || progress.completed;
}

function tourStillNeedsSuggestion(tourId) {
  const authState = getAuthState();
  if (authState.status === 'loading') return true;
  return authState.authenticated
    && toursAreEnabled(authState)
    && !hasTourBeenSuggestedOrCompleted(authState, tourId);
}

function isVisible(element) {
  if (!element || !element.isConnected) return false;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0
    && rect.height > 0
    && rect.right > 0
    && rect.left < window.innerWidth;
}

function findVisible(selector, root = document) {
  return Array.from(root.querySelectorAll(selector)).find(isVisible) || null;
}

function findDisplayedTourElement(selector) {
  return Array.from(document.querySelectorAll(selector)).find(element => {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }) || null;
}

function resolveTarget(target, allowHidden = false) {
  try {
    if (!target) return null;
    let element = null;
    if (typeof target === 'function') element = target() || null;
    else if (typeof target === 'string') element = allowHidden ? document.querySelector(target) : findVisible(target);
    else element = target instanceof Element ? target : null;
    if (!element || !element.isConnected) return null;
    return allowHidden || isVisible(element) ? element : null;
  } catch (error) {
    return null;
  }
}

function pageHasDocument() {
  if (window.shared_chat_document === true) return false;
  return window.is_document === true || window.is_law === true || window.is_user_document === true;
}

function latestCompletedChatResponse({ visibleOnly = false } = {}) {
  const responses = Array.from(document.querySelectorAll('#chatModal [data-tour="chat-response"]'));
  return responses.reverse().find(response => !visibleOnly || isVisible(response)) || null;
}

function pageHasChatResponse() {
  return !!latestCompletedChatResponse();
}

function latestDocumentEditionWorkspace({ visibleOnly = false, latestResponseOnly = false } = {}) {
  const latestResponse = latestResponseOnly
    ? latestCompletedChatResponse({ visibleOnly })
    : null;
  const root = latestResponseOnly
    ? latestResponse?.closest('[data-message-ref]')
    : document.querySelector('#chatModal');
  if (!root) return null;
  const workspaces = Array.from(root.querySelectorAll(
    '[data-tour="document-edition-workspace"]'
  ));
  return workspaces.reverse().find(workspace => !visibleOnly || isVisible(workspace)) || null;
}

function pageHasDocumentEditionWorkspace() {
  return !!latestDocumentEditionWorkspace({ visibleOnly: true });
}

function pageHasDocumentEditionSuggestionWorkspace() {
  return !!latestDocumentEditionWorkspace({ visibleOnly: true, latestResponseOnly: true });
}

function documentEditionTarget(selector) {
  const workspace = activeDocumentEditionWorkspace?.isConnected
    && isVisible(activeDocumentEditionWorkspace)
    ? activeDocumentEditionWorkspace
    : latestDocumentEditionWorkspace({ visibleOnly: true });
  return workspace ? findVisible(selector, workspace) : null;
}

function pageHasDocumentReviewWorkspace() {
  return !!findVisible('[data-tour="document-review-workspace"]');
}

function isCallLogChat() {
  return !!document.querySelector('#chatModal [data-tour="chat-call-log-notice"]');
}

function hasTourPlanAccess(authState, tourId) {
  return !!(
    authState
    && (authState.paid || (['files', 'user-file-folders'].includes(tourId) && authState.unlimitedTrial))
  );
}

function chatResponseTarget(selector) {
  const response = latestCompletedChatResponse({ visibleOnly: true });
  return response ? findVisible(selector, response) : null;
}

function pageHasSearchResults() {
  if (window.is_search_results !== true) return false;
  return !!findVisible('[data-tour="search-result-group"]');
}

function isTouchLayout() {
  return window.matchMedia && window.matchMedia('(max-width: 991.98px)').matches;
}

function isCompactTourLayout() {
  if (!window.matchMedia) return window.innerWidth <= 575;
  return window.matchMedia('(max-width: 575.98px)').matches
    || window.matchMedia('(pointer: coarse) and (max-height: 575px)').matches;
}

function isTabletTourLayout() {
  if (isCompactTourLayout()) return false;
  if (!window.matchMedia) return window.innerWidth <= 1199;
  return window.matchMedia('(max-width: 1199.98px)').matches
    || window.matchMedia('(pointer: coarse) and (max-width: 1366px)').matches;
}

function isResponsiveTourLayout() {
  return isCompactTourLayout() || isTabletTourLayout();
}

function responsiveLayoutKey() {
  const tourLayout = isCompactTourLayout()
    ? 'tour-compact'
    : (isTabletTourLayout() ? 'tour-tablet' : 'tour-regular');
  return [
    isTouchLayout() ? 'touch' : 'wide',
    isMobileChatLayout() ? 'chat-mobile' : 'chat-wide',
    tourLayout
  ].join(':');
}

function prefersReducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function setTourUiActive(active) {
  const nextValue = active === true;
  if (window.LEXIUS_TOUR_UI_ACTIVE === nextValue) return;
  window.LEXIUS_TOUR_UI_ACTIVE = nextValue;
  window.dispatchEvent(new CustomEvent(nextValue ? 'lexius-tour-ui-opened' : 'lexius-tour-ui-closed'));
}

function hasOpenTourModal() {
  return Array.from(document.querySelectorAll('.lexius-tour-modal')).some(modal => {
    return modal.classList.contains('show') || modal.classList.contains('is-visible');
  });
}

function refreshTourUiState() {
  setTourUiActive(!!activeRun || !!activeSuggestion || tourStartPending || hasOpenTourModal());
}

function isMeaningfulFocus(element) {
  return !!(
    element
    && element !== document.body
    && element !== document.documentElement
    && element.isConnected
    && isVisible(element)
    && !element.closest('.lexius-tour-modal, .driver-popover, .lexius-tour-suggestion')
  );
}

function rememberTourReturnFocus() {
  if (returnFocusElement) return;
  if (isMeaningfulFocus(document.activeElement)) returnFocusElement = document.activeElement;
}

function focusTourFallback() {
  if (isMeaningfulFocus(document.activeElement)) {
    returnFocusElement = null;
    return;
  }
  const fallback = (returnFocusElement && isVisible(returnFocusElement) && returnFocusElement)
    || (lastLauncher && isVisible(lastLauncher) && lastLauncher)
    || findVisible('[data-tour="settings-trigger"]')
    || findVisible('[data-testid="open-chat"]')
    || findVisible('#main-container');
  returnFocusElement = null;
  if (!fallback || typeof fallback.focus !== 'function') return;
  try { fallback.focus({ preventScroll: true }); } catch (error) { }
}

function scrollMainContentToTop() {
  const mainContent = document.querySelector('.right-side-container');
  if (!mainContent || typeof mainContent.scrollTo !== 'function') return;
  mainContent.scrollTo({
    top: 0,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth'
  });
}

function getModalInstance(element) {
  if (!element || !window.bootstrap || !window.bootstrap.Modal) return null;
  if (typeof window.bootstrap.Modal.getOrCreateInstance === 'function') {
    return window.bootstrap.Modal.getOrCreateInstance(element);
  }
  return new window.bootstrap.Modal(element);
}

function waitForBootstrapEvent(element, eventName, timeoutMilliseconds = 700) {
  if (!element) return Promise.resolve();
  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      element.removeEventListener(eventName, finish);
      resolve();
    };
    element.addEventListener(eventName, finish, { once: true });
    window.setTimeout(finish, timeoutMilliseconds);
  });
}

async function showModal(element) {
  const instance = getModalInstance(element);
  if (!instance || !element) return null;
  rememberTourReturnFocus();
  setTourUiActive(true);
  if (!element.classList.contains('show')) {
    const shown = waitForBootstrapEvent(element, 'shown.bs.modal');
    instance.show();
    await shown;
  }
  return instance;
}

async function hideModal(element) {
  if (!element) return;
  const instance = getModalInstance(element);
  if (!instance) return;
  const isHiding = element.classList.contains('show') || window.getComputedStyle(element).display !== 'none';
  if (!isHiding) return;
  const hidden = waitForBootstrapEvent(element, 'hidden.bs.modal');
  if (element.classList.contains('show')) instance.hide();
  await hidden;
  refreshTourUiState();
}

function installModals() {
  if (document.getElementById('lexiusTourCenterModal')) return;
  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal fade lexius-tour-modal lexius-tour-welcome" id="lexiusTourWelcomeModal"
      tabindex="-1" aria-labelledby="lexiusTourWelcomeTitle" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="lexius-tour-welcome__body">
            <div class="lexius-tour-welcome__icon" aria-hidden="true"><i class="anticon anticon-compass"></i></div>
            <h2 id="lexiusTourWelcomeTitle">¿Quieres conocer lo esencial?</h2>
            <p>Este recorrido opcional tiene pasos breves y te muestra dónde encontrar las funciones principales. No activará ninguna función. Puedes repetirlo después desde Ajustes, en Ayuda y recorridos.</p>
            <div class="lexius-tour-modal__actions">
              <button type="button" class="btn btn-outline-secondary" data-tour-auto-dismiss>Ahora no</button>
              <button type="button" class="btn btn-primary" data-tour-auto-start>Comenzar recorrido</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade lexius-tour-modal lexius-tour-auth" id="lexiusTourAuthModal"
      tabindex="-1" aria-labelledby="lexiusTourAuthTitle" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="lexius-tour-auth__body">
            <div class="lexius-tour-auth__icon" aria-hidden="true"><i class="anticon anticon-user"></i></div>
            <h2 id="lexiusTourAuthTitle">Accede para ver los recorridos</h2>
            <p>Inicia sesión o crea una cuenta y comienza la prueba gratuita para conocer las funciones dentro de la aplicación.</p>
            <div class="lexius-tour-modal__actions">
              <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Ahora no</button>
              <button type="button" class="btn btn-outline-primary login-button">Iniciar sesión</button>
              <button type="button" class="btn btn-primary register-button">Comenzar prueba gratuita</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade lexius-tour-modal lexius-tour-center" id="lexiusTourCenterModal"
      tabindex="-1" aria-labelledby="lexiusTourCenterTitle" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <div>
              <h2 class="modal-title" id="lexiusTourCenterTitle">Recorridos guiados</h2>
              <p class="lexius-tour-modal__subtitle">Elige un recorrido para conocer cada función paso a paso y a tu ritmo, puedes cerrarlo en cualquier momento y repetirlo cuando quieras desde Ajustes.</p>
            </div>
            <button type="button" class="lexius-tour-modal__close" data-bs-dismiss="modal" aria-label="Cerrar recorridos">
              <i class="anticon anticon-close" aria-hidden="true"></i>
            </button>
          </div>
          <div class="modal-body">
            <section class="lexius-tour-preference" data-tour-preference data-enabled="true">
              <div class="lexius-tour-preference__icon" aria-hidden="true">
                <i class="anticon anticon-compass"></i>
              </div>
              <div class="lexius-tour-preference__copy">
                <div class="lexius-tour-preference__heading">
                  <h3>Recorridos y sugerencias</h3>
                  <span class="lexius-tour-preference__status" data-tour-preference-status role="status">Activados</span>
                </div>
                <p data-tour-preference-description>Los recorridos y sus sugerencias opcionales están disponibles en todos tus dispositivos.</p>
                <p class="lexius-tour-preference__feedback" data-tour-preference-feedback role="status" aria-live="polite"></p>
              </div>
              <button type="button" class="btn btn-outline-secondary lexius-tour-preference__toggle"
                data-tour-preference-toggle aria-pressed="true">
                Desactivar recorridos
              </button>
            </section>
            <div class="lexius-tour-grid" data-tour-grid></div>
            <div class="lexius-tour-reset">
              <div class="lexius-tour-reset__copy">
                <h3>¿Quieres empezar de nuevo?</h3>
                <p>Elimina las marcas de recorridos sugeridos y completados para que vuelvan a aparecer como nuevos.</p>
                <p class="lexius-tour-reset__feedback" data-tour-reset-feedback role="status" aria-live="polite"></p>
              </div>
              <button type="button" class="btn btn-danger" data-tour-reset>
                Eliminar progreso de recorridos
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="lexius-tour-modal lexius-tour-disable-confirmation" id="lexiusTourDisableConfirmation"
      aria-hidden="true" hidden>
      <div class="lexius-tour-disable-confirmation__backdrop" data-tour-disable-cancel></div>
      <div class="lexius-tour-disable-confirmation__dialog" role="dialog" aria-modal="true"
        aria-labelledby="lexiusTourDisableTitle"
        aria-describedby="lexiusTourDisableIntro lexiusTourDisableImpact" tabindex="-1">
        <button type="button" class="lexius-tour-disable-confirmation__close" data-tour-disable-dismiss
          aria-label="Cerrar y mantener los recorridos">
          <i class="anticon anticon-close" aria-hidden="true"></i>
        </button>
        <div class="lexius-tour-disable-confirmation__header">
          <span class="lexius-tour-disable-confirmation__compass" aria-hidden="true">
            <i class="anticon anticon-compass"></i>
          </span>
          <div>
            <p class="lexius-tour-disable-confirmation__eyebrow">Ayuda dentro de LEXIUS</p>
            <h2 id="lexiusTourDisableTitle">¿Desactivar los recorridos guiados?</h2>
          </div>
        </div>
        <p class="lexius-tour-disable-confirmation__intro" id="lexiusTourDisableIntro">
          Antes de decidir, recuerda cómo pueden ayudarte mientras trabajas.
        </p>
        <ul class="lexius-tour-disable-confirmation__benefits" aria-label="Beneficios de los recorridos guiados">
          <li>
            <span class="lexius-tour-disable-confirmation__benefit-icon" aria-hidden="true">
              <i class="anticon anticon-compass"></i>
            </span>
            <div>
              <strong>Aprende paso a paso</strong>
              <span>Ubica las funciones clave con explicaciones breves y a tu ritmo.</span>
            </div>
          </li>
          <li>
            <span class="lexius-tour-disable-confirmation__benefit-icon" aria-hidden="true">
              <i class="anticon anticon-safety"></i>
            </span>
            <div>
              <strong>Explora sin alterar tu trabajo</strong>
              <span>Los recorridos no activan funciones ni modifican tus documentos.</span>
            </div>
          </li>
          <li>
            <span class="lexius-tour-disable-confirmation__benefit-icon" aria-hidden="true">
              <i class="anticon anticon-bulb"></i>
            </span>
            <div>
              <strong>Recibe ayuda en el momento adecuado</strong>
              <span>Las sugerencias opcionales aparecen al entrar por primera vez a una función.</span>
            </div>
          </li>
        </ul>
        <div class="lexius-tour-disable-confirmation__impact" id="lexiusTourDisableImpact">
          <i class="anticon anticon-sync" aria-hidden="true"></i>
          <p><strong>Si continúas,</strong> dejarás de ver recorridos y sugerencias en todos tus dispositivos.
            Conservaremos tu progreso y podrás reactivarlos cuando quieras desde Ajustes.</p>
        </div>
        <div class="lexius-tour-disable-confirmation__actions">
          <button type="button" class="btn btn-primary" data-tour-disable-cancel>Mantener recorridos</button>
          <button type="button" class="btn btn-outline-secondary" data-tour-disable-confirm>
            Desactivar recorridos
          </button>
        </div>
      </div>
    </div>
    <div class="lexius-tour-status" role="status" aria-live="polite" data-tour-status></div>
    <aside class="lexius-tour-suggestion" id="lexiusTourSuggestion" role="region"
      aria-labelledby="lexiusTourSuggestionTitle" aria-live="polite" aria-atomic="true" hidden>
      <button type="button" class="lexius-tour-suggestion__close" data-tour-suggestion-dismiss
        aria-label="Cerrar sugerencia de recorrido">
        <i class="anticon anticon-close" aria-hidden="true"></i>
      </button>
      <div class="lexius-tour-suggestion__icon" aria-hidden="true">
        <i class="anticon anticon-compass"></i>
      </div>
      <div class="lexius-tour-suggestion__content">
        <p class="lexius-tour-suggestion__eyebrow" data-tour-suggestion-eyebrow>Recorrido opcional</p>
        <h2 id="lexiusTourSuggestionTitle" data-tour-suggestion-title></h2>
        <p class="lexius-tour-suggestion__description" data-tour-suggestion-description></p>
        <div class="lexius-tour-suggestion__actions">
          <button type="button" class="btn btn-outline-secondary" data-tour-suggestion-dismiss>No, gracias</button>
          <button type="button" class="btn btn-primary" data-tour-suggestion-start>Ver recorrido</button>
        </div>
        <button type="button" class="lexius-tour-suggestion__disable" data-tour-suggestion-disable>
          Desactivar recorridos
        </button>
      </div>
    </aside>
  `);
}

function announce(message) {
  const status = document.querySelector('[data-tour-status]');
  if (!status) return;
  status.textContent = '';
  window.requestAnimationFrame(() => { status.textContent = message; });
}

function planGateSteps(modalSelector, featureName, description) {
  return [
    {
      title: `${featureName}: disponible con un plan`,
      description: `${description} Puedes ver cómo funciona en este recorrido. Al salir, selecciona Ver planes para consultar las opciones disponibles.`
    }
  ];
}

function sharedDocumentContextStep() {
  if (window.shared_chat_document !== true) return null;
  return {
    title: 'Documento compartido en modo lectura',
    description: 'El documento compartido que estás viendo no puede usarse para análisis ni generación. Abre un contenido de tu biblioteca o de Mis archivos, o inicia la función sin documentos cuando esa opción esté disponible.'
  };
}

function buildOverviewSteps() {
  return [
    {
      title: 'Tu espacio de trabajo',
      description: 'LEXIUS reúne la biblioteca jurídica, la búsqueda y las herramientas de asistencia en una sola pantalla.'
    },
    {
      target: '#main-container',
      tabletTarget: () => findVisible(
        '#main-container h1, #main-container h2, #main-container h3, #main-container h4'
      ) || findVisible('#main-container'),
      title: 'Biblioteca jurídica',
      description: 'Esta área muestra las carpetas de la biblioteca, los resultados o el contenido que abras. Desde una carpeta, selecciona otra carpeta para avanzar o un archivo para consultar su contenido.'
    },
    {
      target: () => findVisible('[data-tour="navigation-controls"]'),
      title: 'Muévete por LEXIUS',
      description: 'Usa las flechas para volver o avanzar por las pantallas que visitaste. El botón de inicio regresa a la raíz de la biblioteca.'
    },
    {
      target: () => isTouchLayout()
        ? findVisible('[data-tour="mobile-navigation"]')
        : findVisible('#search-form'),
      title: 'Búsqueda y navegación',
      description: 'Busca dentro de la ubicación actual y consulta tus búsquedas recientes. En pantallas pequeñas, abre primero el panel de navegación.'
    },
    {
      target: '[data-testid="open-chat"]',
      title: 'Abogado Virtual',
      description: 'Consulta al Abogado Virtual para analizar temas jurídicos, redactar borradores o trabajar con documentos.'
    },
    {
      target: () => findVisible('[data-tour="feature-menu"]'),
      title: 'Menú de funciones',
      description: 'Este menú reúne las acciones disponibles para la página o el documento que estás viendo, además de los accesos a archivos, alertas y generadores.'
    },
    {
      target: () => findVisible('[data-tour="settings-trigger"]'),
      title: 'Ajustes y recorridos',
      description: 'Personaliza la interfaz y administra tus datos desde Ajustes. Allí también puedes repetir cualquier recorrido.'
    }
  ];
}

function buildSearchSteps() {
  const resultSection = () => findVisible('[data-testid="search-results"], #jurisprudence-container, #laws-container');
  return [
    {
      target: () => isTouchLayout() ? findVisible('#search-form') : findVisible('#main-container'),
      title: 'Explora y busca',
      description: 'El área principal puede mostrar carpetas, resultados o un documento. Usa la biblioteca para explorar o el formulario de búsqueda; en pantallas pequeñas, el panel de navegación reúne ambas opciones.'
    },
    {
      target: '#searchbx',
      title: 'Escribe tu búsqueda',
      description: 'Introduce al menos 3 caracteres. La búsqueda se realiza dentro de la ubicación que tengas abierta.'
    },
    {
      target: '#analyze-count',
      optional: true,
      title: 'Comprueba el alcance',
      description: 'Aquí puedes confirmar la carpeta en la que buscas y cuántas sentencias o cuántos textos legales se analizarán.'
    },
    {
      target: '#dateFromInput',
      optional: true,
      title: 'Acota por fecha',
      description: 'Cuando estos campos estén disponibles, elige una fecha inicial o final para limitar los resultados.'
    },
    {
      target: '[data-tour="search-history"]',
      title: 'Búsquedas recientes',
      description: 'Aquí puedes repetir o eliminar búsquedas anteriores.'
    },
    !isTouchLayout() && {
      target: resultSection,
      optional: true,
      title: 'Resultados por tipo',
      description: 'Los resultados se separan por fuente. En Jurisprudencia y Legislación puedes ordenarlos por relevancia o fecha; abre cualquier resultado para consultar su contenido.'
    },
    {
      title: 'Continúa con un documento',
      description: 'Abre una sentencia, un texto legal o uno de tus archivos para consultar las herramientas disponibles para ese contenido.'
    }
  ].filter(Boolean);
}

function buildSearchResultsSteps() {
  const isAlertResults = window.is_alert_results === true;
  const resultSources = ['Jurisprudencia', 'Legislación'];
  const hasCorporatePlan = window.user_has_corporate_plan === true;
  if (hasCorporatePlan && window.user_files_search_available === true) {
    resultSources.push('Mis Archivos');
  }
  if (hasCorporatePlan && window.corporate_documents_available === true) {
    resultSources.push('Archivos Corporativos');
  }
  const resultSourcesDescription = resultSources.length > 1
    ? `${resultSources.slice(0, -1).join(', ')} y ${resultSources[resultSources.length - 1]}`
    : resultSources[0];
  const firstResultGroup = () => findVisible('[data-tour="search-result-group"]');
  const firstResultGroupHeading = () => {
    const group = firstResultGroup();
    return group ? findVisible('.search-type, h2, h3', group) || group : null;
  };
  const searchAnalysisMobileTarget = () => {
    const analysis = findVisible('[data-tour="search-results-analysis"]');
    return analysis
      ? findVisible('[data-tour="search-results-analysis-actions"], h3', analysis) || analysis
      : null;
  };
  const firstOrderControl = () => findVisible('[data-tour="search-results-order"], #order_by, #order_by_laws');
  const resultTarget = selector => () => resolveSearchResultTourTarget(selector);
  const revealResultTarget = selector => () => revealSearchResultTourTarget(selector);
  const resultWrappers = [
    '#search_results_wrapper',
    '#search_results_laws_wrapper',
    '#search_results_user_documents_wrapper',
    '#search_results_corporate_documents_wrapper'
  ];
  const firstPagination = () => findVisible(resultWrappers.flatMap(wrapper => [
    `${wrapper} .dt-paging`,
    `${wrapper} .dataTables_paginate`,
    `${wrapper} .dt-info`,
    `${wrapper} .dt-length`
  ]).join(', '));
  const suggestedSearchesTarget = () => findVisible('[data-tour="search-suggested-questions"]');
  const legislationResultsTarget = () => {
    const group = findVisible('[data-tour-search-results-legislation], #laws-container');
    return group ? findVisible('.search-type, h2, h3', group) || group : null;
  };

  const steps = [
    {
      target: isAlertResults ? '.alert-run-banner' : null,
      title: isAlertResults ? 'Resultados de la alerta' : 'Tus resultados de búsqueda',
      description: isAlertResults
        ? 'El aviso identifica la alerta ejecutada, su consulta, carpeta, frecuencia y cantidad de hallazgos. Debajo aparecen solo las fuentes con coincidencias.'
        : `LEXIUS muestra solo las fuentes con coincidencias: ${resultSourcesDescription}. La consulta y sus filtros permanecen en el panel de búsqueda.`
    },
    {
      target: '[data-tour="search-results-analysis"]',
      mobileTarget: searchAnalysisMobileTarget,
      optional: true,
      title: isAlertResults ? 'Resumen de la alerta' : 'Análisis general de la búsqueda',
      description: `Cuando está disponible, este bloque sintetiza ${isAlertResults ? 'los hallazgos de la alerta' : 'el conjunto de hallazgos'}. Sus controles permiten copiar el análisis, descargarlo en PDF o Word y verlo como mapa mental.`
    },
    {
      target: firstResultGroup,
      mobileTarget: firstResultGroupHeading,
      title: 'Resultados separados por fuente',
      description: 'Cada sección reúne un tipo de contenido y administra su propia lista. Así puedes distinguir sentencias, textos legales y archivos sin mezclarlos.'
    },
    {
      target: firstOrderControl,
      optional: true,
      title: 'Ordena cada sección',
      description: 'Jurisprudencia y Legislación se ordenan por separado. Elige entre los resultados más relevantes, los más recientes o los más antiguos.'
    },
    {
      target: resultTarget(SEARCH_RESULT_EXTRACT_SELECTOR),
      reveal: revealResultTarget(SEARCH_RESULT_EXTRACT_SELECTOR),
      optional: true,
      title: 'Comprueba por qué coincide',
      description: 'Los extractos muestran los fragmentos relacionados con tu consulta junto con los datos del documento. Selecciona uno para abrir el documento y saltar a esa coincidencia; el círculo señala relevancia alta (color verde), media (color amarillo) o baja (color rojo). En pantallas pequeñas, LEXIUS despliega automáticamente las columnas ocultas durante este recorrido.'
    },
    {
      target: resultTarget(SEARCH_RESULT_ANALYSIS_SELECTOR),
      reveal: revealResultTarget(SEARCH_RESULT_ANALYSIS_SELECTOR),
      optional: true,
      title: 'Analiza un resultado',
      description: 'Este botón analiza únicamente los extractos encontrados para ese documento, no el texto completo. Para analizar todo el contenido, ábrelo y usa el Asistente Virtual.'
    },
    {
      target: resultTarget(SEARCH_RESULT_OPEN_SELECTOR),
      reveal: revealResultTarget(SEARCH_RESULT_OPEN_SELECTOR),
      title: 'Abre el documento completo',
      description: 'Selecciona Abrir para consultar el texto completo y acceder a las herramientas disponibles para ese documento.'
    },
    {
      target: firstPagination,
      optional: true,
      title: 'Recorre más coincidencias',
      description: 'El contador indica cuántos resultados hay. Usa la paginación de cada sección y el selector de cantidad para cambiar cuántos se muestran a la vez.'
    },
    {
      target: suggestedSearchesTarget,
      optional: true,
      title: 'Explora búsquedas sugeridas',
      description: 'Cuando están disponibles, estas sugerencias te ayudan a continuar la investigación con consultas relacionadas. Selecciona una para realizar una nueva búsqueda.'
    },
    {
      target: legislationResultsTarget,
      optional: true,
      title: 'Consulta también la Legislación',
      description: 'Cuando hay coincidencias en textos legales, LEXIUS las presenta en esta sección independiente para que puedas revisarlas después de la jurisprudencia.'
    }
  ];

  return steps.map(step => ({
    ...step,
    avoidStickyPageHeader: true
  }));
}

function responsiveSearchResultChildRow(row) {
  const possibleChildRow = row?.nextElementSibling;
  return possibleChildRow?.matches('tr.child') ? possibleChildRow : null;
}

function isResponsiveSearchResultRowExpanded(row) {
  return !!(
    row
    && (
      row.classList.contains('dtr-expanded')
      || row.classList.contains('parent')
      || responsiveSearchResultChildRow(row)
    )
  );
}

function responsiveSearchResultDetails(selector, group) {
  if (!group) return null;
  const sourceTarget = Array.from(group.querySelectorAll(selector)).find(target => (
    !target.closest('tr.child, .dtr-details')
  ));
  const row = sourceTarget?.closest('tbody tr');
  const cell = sourceTarget?.closest('td, th');
  const table = row?.closest('table.dataTable');
  if (!sourceTarget || !row || !cell || !table || row.classList.contains('child')) return null;

  const hiddenByResponsive = cell.classList.contains('dtr-hidden')
    || (
      table.classList.contains('collapsed')
      && window.getComputedStyle(cell).display === 'none'
    );
  if (!hiddenByResponsive) return null;

  const control = findVisible(
    'td.dtr-control, th.dtr-control, td.control, th.control',
    row
  );
  if (!control) return null;

  const childRow = responsiveSearchResultChildRow(row);
  return {
    row,
    control,
    childTarget: childRow ? findVisible(selector, childRow) : null
  };
}

function resolveSearchResultTourTarget(selector) {
  if (!isResponsiveTourLayout()) return findVisible(selector);
  const group = findVisible('[data-tour="search-result-group"]');
  if (!group) return null;
  const responsiveDetails = responsiveSearchResultDetails(selector, group);
  if (responsiveDetails) {
    return responsiveDetails.childTarget || responsiveDetails.control;
  }
  return findVisible(selector, group);
}

function revealSearchResultTourTarget(selector) {
  if (!isResponsiveTourLayout()) return;
  const group = findVisible('[data-tour="search-result-group"]');
  const responsiveDetails = responsiveSearchResultDetails(selector, group);
  if (
    !responsiveDetails
    || responsiveDetails.childTarget
    || isResponsiveSearchResultRowExpanded(responsiveDetails.row)
  ) return;

  activeSearchResultsSurface?.expandedRows.add(responsiveDetails.row);
  responsiveDetails.control.click();
}

function buildDocumentSteps(authState) {
  const hideMenu = { beforeHighlight: hideDocumentTourTransientSurfaces };
  const revealMenu = { reveal: revealDocumentMenuForTour };
  const revealMenuOptions = selector => ({
    reveal: () => revealDocumentMenuForTour(selector)
  });
  const lastDocumentGenerator = () => {
    const generators = Array.from(document.querySelectorAll('[data-tour="document-generator"]'));
    for (let index = generators.length - 1; index >= 0; index -= 1) {
      if (isVisible(generators[index])) return generators[index];
    }
    return generators[generators.length - 1] || null;
  };
  const callDescription = authState.paid
    ? 'Inicia una llamada usando este documento como contexto.'
    : 'Las llamadas pueden usar este documento como contexto y están disponibles con un plan.';
  return [
    {
      target: '#page-title',
      title: 'Contenido abierto',
      description: 'Aquí ves el nombre de la sentencia, texto legal o archivo que estás consultando.',
      ...hideMenu
    },
    {
      target: '[data-tour="selection-analysis-popup"]',
      mobileTarget: '.r6o-editor[data-lexius-tour-preview="true"]',
      tabletTarget: '[data-tour="selection-analysis-popup"]',
      keepAnnotationContextVisible: true,
      optional: true,
      title: 'Notas y Análisis Inteligente',
      description: 'LEXIUS ha resaltado un fragmento de ejemplo. Cuando selecciones texto, este panel te permite copiarlo o escribir y guardar una nota vinculada al fragmento. Pulsa el botón del robot para abrir Análisis Inteligente y trabajar únicamente con el texto seleccionado.',
      reveal: revealDocumentAnnotationForTour
    },
    {
      target: () => findVisible('.search-btn-expedient'),
      optional: true,
      title: 'Busca dentro del contenido',
      description: 'Usa este buscador para localizar palabras dentro del documento abierto y recorrer las coincidencias.',
      ...hideMenu
    },
    {
      target: () => findVisible('[data-tour="feature-menu"]'),
      title: 'Acciones del documento',
      description: withFeatureMenuIcon('El botón de tres puntos abre las acciones que están disponibles para este tipo de contenido.'),
      ...hideMenu
    },
    {
      target: '[data-tour="document-summary"]',
      optional: true,
      title: 'Síntesis completa',
      description: 'El Asistente Virtual sintetiza el documento completo. El resultado se puede copiar, descargar en PDF o Word, o ver como mapa mental.',
      ...revealMenuOptions('[data-tour="document-summary"]')
    },
    {
      target: '[data-tour="document-chat"]',
      optional: true,
      title: 'Chat sobre este documento',
      description: 'Inicia un chat usando este contenido como contexto. Si ya tienes un chat activo, LEXIUS puede ofrecerte agregar el documento a esa conversación.',
      ...revealMenuOptions('[data-tour="document-chat"]')
    },
    {
      target: '[data-tour="document-call"]',
      optional: true,
      title: 'Llamada con este documento',
      description: callDescription,
      ...revealMenuOptions('[data-tour="document-call"]')
    },
    {
      target: lastDocumentGenerator,
      optional: true,
      title: 'Generadores de contenido',
      description: 'Desde este menú puedes usar el documento como contexto para los generadores que estén disponibles en tu cuenta.',
      ...revealMenuOptions('[data-tour="document-generator"]')
    },
    {
      target: '[data-tour="document-download"]',
      optional: true,
      title: 'Descarga el contenido',
      description: 'Cuando el formato y el país lo permiten, descarga una versión en PDF o Word, o el archivo original.',
      ...revealMenuOptions('[data-tour="document-download"]')
    },
    {
      target: '[data-tour="document-link"]',
      optional: true,
      title: 'Comparte el enlace',
      description: 'En sentencias y textos legales puedes copiar el enlace para compartir el contenido con otros usuarios.',
      ...revealMenu
    },
    {
      target: () => findVisible('[data-tour="notes-library"]'),
      optional: true,
      title: 'Tus notas',
      description: 'Abre Notas para buscar tus anotaciones y volver al texto marcado. El Modo de Alto Rendimiento deshabilita esta función.',
      ...hideMenu
    }
  ];
}

function buildVirtualAssistantSteps() {
  const showPage = { reveal: showDocumentFeaturePageForTour };
  const showModal = { reveal: showDocumentFeatureModalForTour };
  return [
    {
      target: () => findVisible('[data-tour="feature-menu"]'),
      title: 'Dónde abrir el Asistente Virtual',
      description: withFeatureMenuIcon('Abre una sentencia, un texto legal, un archivo o una transcripción y pulsa el menú de tres puntos. El recorrido no generará una síntesis ni consumirá tu cuota.'),
      ...showPage
    },
    {
      target: '[data-tour="document-summary"]',
      optional: true,
      title: 'Sintetiza el contenido completo',
      description: 'Selecciona Obtener síntesis con el Asistente Virtual. A diferencia del Análisis Inteligente, esta opción procesa el contenido completo que tienes abierto, no solo un fragmento o los extractos de una búsqueda.',
      reveal: () => showDocumentFeatureMenuForTour('[data-tour="document-summary"]')
    },
    {
      target: '[data-tour="virtual-assistant-header"]',
      title: 'Asistente Virtual',
      description: 'La síntesis se genera para la sentencia, el texto legal, el archivo o la transcripción que abriste. Espera a que termine el indicador de carga antes de usar el resultado.',
      ...showModal
    },
    {
      target: '[data-tour="virtual-assistant-result"]',
      title: 'Revisa la síntesis',
      description: 'Aquí aparece la síntesis del contenido completo. Contrástala con el documento original y verifica nombres, fechas, citas y conclusiones antes de incorporarla a tu trabajo.',
      ...showModal
    },
    {
      target: '[data-tour="virtual-assistant-actions"]',
      title: 'Copia, descarga o crea un mapa',
      description: 'Estos controles copian la síntesis completa, la descargan en PDF o Word, o la convierten en un mapa mental. Actúan sobre el resultado del Asistente Virtual, no sobre el archivo original.',
      ...showModal
    },
    {
      target: () => findDisplayedTourElement('[data-tour="virtual-assistant-research"]'),
      optional: true,
      title: 'Consulta la investigación',
      description: 'Cuando esta sección aparece, puedes desplegarla para recorrer las actividades de investigación y razonamiento registradas durante la generación.',
      ...showModal
    },
    {
      target: () => findDisplayedTourElement('[data-tour="virtual-assistant-next-actions"]'),
      optional: true,
      title: 'Continúa con el mismo documento',
      description: 'Desde el resultado puedes iniciar un chat sobre el documento. Según las funciones disponibles en tu cuenta, también puedes usarlo como contexto para una llamada o un generador de contenido.',
      ...showModal
    }
  ];
}

function buildIntelligentAnalysisSteps() {
  const showSelection = { reveal: showDocumentFeatureSelectionForTour };
  const showModal = { reveal: showDocumentFeatureModalForTour };
  return [
    {
      target: '[data-tour="selection-analysis-popup"]',
      mobileTarget: '.r6o-editor[data-lexius-tour-preview="true"]',
      tabletTarget: '[data-tour="selection-analysis-popup"]',
      optional: true,
      title: 'Selecciona el fragmento',
      description: 'En el documento abierto, selecciona el texto que quieres estudiar y pulsa el botón del robot. Análisis Inteligente trabajará únicamente con ese fragmento, no con el documento completo.',
      ...showSelection
    },
    {
      target: '[data-tour="intelligent-analysis-header"]',
      title: 'Análisis Inteligente',
      description: 'Esta función examina el fragmento seleccionado en el documento abierto. No equivale a una síntesis del documento completo; para eso utiliza el Asistente Virtual.',
      ...showModal
    },
    {
      target: () => findDisplayedTourElement('#intelligentSummarySearchWarning'),
      optional: true,
      title: 'Comprueba el alcance',
      description: 'Cuando el análisis proviene de una búsqueda, este aviso confirma que la IA recibió los extractos coincidentes y no el texto íntegro del documento.',
      ...showModal
    },
    {
      target: '[data-tour="intelligent-analysis-result"]',
      title: 'Lee el análisis en contexto',
      description: 'Aquí aparece el análisis del fragmento seleccionado. Interprétalo junto con ese texto y verifica cualquier afirmación importante en el documento completo.',
      ...showModal
    },
    {
      target: '[data-tour="intelligent-analysis-actions"]',
      title: 'Copia o exporta el análisis',
      description: 'Puedes copiar el análisis, descargarlo en PDF o Word, o abrirlo como mapa mental. Estas acciones exportan el análisis generado, no el documento fuente.',
      ...showModal
    },
    {
      target: () => findDisplayedTourElement('[data-tour="intelligent-analysis-research"]'),
      optional: true,
      title: 'Revisa la investigación',
      description: 'Si el modelo registró actividades de investigación y razonamiento, esta sección permite consultarlas paso a paso.',
      ...showModal
    },
    {
      target: () => findDisplayedTourElement('[data-tour="intelligent-analysis-full-document"]'),
      optional: true,
      title: '¿Necesitas todo el documento?',
      description: 'Usa este acceso al Asistente Virtual cuando necesites sintetizar el contenido completo en lugar del fragmento analizado.',
      ...showModal
    }
  ];
}

function buildNotesSteps() {
  const showSelection = { reveal: showDocumentFeatureSelectionForTour };
  const showPage = { reveal: showDocumentFeaturePageForTour };
  const showModal = { reveal: showDocumentFeatureModalForTour };
  return [
    {
      target: '.r6o-editor[data-lexius-tour-preview="true"]',
      mobileTarget: '.r6o-editor[data-lexius-tour-preview="true"]',
      tabletTarget: '.r6o-editor[data-lexius-tour-preview="true"]',
      keepAnnotationContextVisible: true,
      optional: true,
      title: 'Selecciona texto y escribe tu nota',
      description: 'Selecciona un fragmento de una sentencia, texto legal o archivo. Escribe el comentario en este panel: al guardarlo, la nota queda vinculada exactamente al texto seleccionado.',
      ...showSelection
    },
    {
      target: '.r6o-editor[data-lexius-tour-preview="true"] .r6o-footer',
      keepAnnotationContextVisible: true,
      optional: true,
      title: 'Guarda o descarta los cambios',
      description: 'Aceptar guarda la nota; Cancelar cierra el panel sin guardarla. Copiar lleva el fragmento al portapapeles y el botón del robot abre Análisis Inteligente para esa misma selección.',
      ...showSelection
    },
    {
      target: () => findVisible('[data-tour="notes-library"]'),
      title: 'Abre tu biblioteca de notas',
      description: 'El botón del libro abre Notas desde la navegación. Allí puedes consultar anotaciones de distintos documentos sin tener que recordar dónde las creaste.',
      ...showPage
    },
    {
      target: '[data-tour="notes-library-header"]',
      title: 'Biblioteca de notas',
      description: 'Las notas se agrupan por documento. Selecciona el título de un fragmento para volver al texto marcado; si pertenece a otro documento, LEXIUS abre primero ese contenido.',
      ...showModal
    },
    {
      target: '[data-tour="notes-library-overview"]',
      title: 'Administra tus anotaciones',
      description: 'Desde cada grupo puedes eliminar sus notas y desde la parte superior puedes eliminar todas. Las eliminaciones requieren confirmación cuando afectan toda la biblioteca.',
      ...showModal
    },
    {
      target: '[data-tour="notes-library-search"]',
      title: 'Busca dentro de tus notas',
      description: 'Escribe una palabra o frase para filtrar los grupos visibles por el texto del documento, el fragmento, las etiquetas o el contenido de la nota.',
      ...showModal
    },
    {
      target: '[data-tour="notes-library-list"]',
      title: 'Revisa y vuelve al fragmento',
      description: 'Cada entrada muestra el fragmento anotado y tus comentarios. Puedes ampliar grupos con varias notas, borrar una entrada concreta o regresar al pasaje resaltado.',
      ...showModal
    },
    {
      title: 'Disponibilidad y sincronización',
      description: 'El Modo de Alto Rendimiento deshabilita las notas. Si activas la sincronización, tus notas se guardan en el servidor y quedan disponibles en los dispositivos que usan tu cuenta.'
    }
  ];
}

function buildLawyerSteps(authState) {
  const callLog = isCallLogChat();
  const callDescription = authState.paid
    ? 'Elige una voz e inicia una conversación. Puedes usar documentos como contexto; al finalizar, el registro queda en tus chats.'
    : 'Las llamadas permiten conversar por voz y usar documentos como contexto. Esta función está disponible con un plan.';
  const collaborationDescription = authState.paid
    ? 'Invita colaboradores por correo. Todos comparten el hilo y sus documentos; usa @Lexius cuando quieras una respuesta del Abogado Virtual.'
    : 'Crear invitaciones requiere un plan. Si otra persona te invita, puedes participar en el hilo y trabajar con sus documentos; usa @Lexius cuando quieras una respuesta del Abogado Virtual.';

  const revealChat = { reveal: showLawyerChatSurfaceForTour };
  const revealSidebar = { reveal: showLawyerChatSidebarForTour };
  const closeSidebar = { beforeHighlight: closeChatSidebarForTour };

  return [
    {
      target: '[data-tour="chat-header"]',
      title: 'Abogado Virtual',
      description: 'Puedes hacer consultas legales, pedir análisis, revisar documentos y solicitar borradores. Verifica siempre la información recibida.',
      ...revealChat,
      ...closeSidebar
    },
    {
      target: '[data-tour="chat-history"]',
      title: 'Historial de chats',
      description: 'Busca y abre conversaciones anteriores. Según tus permisos, puedes fijar o renombrar el chat, descargarlo en PDF o borrarlo; los colaboradores invitados también pueden abandonar el hilo compartido.',
      ...revealSidebar
    },
    {
      target: '[data-tour="chat-new"]',
      title: 'Nuevo chat',
      description: 'Inicia aquí una conversación nueva sin documentos asociados.',
      ...revealSidebar
    },
    {
      target: '[data-tour="chat-call"]',
      title: 'Llamadas de voz',
      description: callDescription,
      ...revealSidebar
    },
    {
      target: '[data-testid="chat-scroll-area"]',
      title: 'Conversación',
      description: callLog
        ? 'Esta zona conserva la transcripción de la llamada. El registro es de solo lectura, pero puedes consultar y descargar sus mensajes.'
        : 'Las respuestas y los mensajes de la conversación aparecen en esta zona.',
      ...revealChat,
      ...closeSidebar
    },
    !callLog && {
      target: '[data-testid="chat-composer"]',
      title: 'Escribe tu consulta',
      description: 'Escribe el mensaje y usa el botón de envío. Si abriste el chat desde un documento, ese contenido se muestra como contexto.',
      ...revealChat,
      ...closeSidebar
    },
    callLog && {
      target: '[data-tour="chat-call-log-notice"]',
      title: 'Registro de solo lectura',
      description: 'No puedes enviar mensajes en una transcripción de llamada. Usa Nuevo chat para continuar por texto sin modificar este registro.',
      ...revealChat,
      ...closeSidebar
    },
    {
      target: '[data-tour="chat-documents"]',
      optional: true,
      title: 'Documentos del chat',
      description: 'Aquí ves los documentos asociados a la conversación. Los que agregues después de iniciar el chat se incorporan con tu próximo mensaje; puedes quitarlos antes de enviarlo.',
      ...revealChat,
      ...closeSidebar
    },
    {
      target: '[data-testid="chat-collaboration-trigger"]',
      optional: true,
      title: 'Chat colaborativo',
      description: collaborationDescription,
      ...revealChat
    },
    {
      target: () => findVisible('[data-tour="feature-menu"]'),
      title: 'Chatea sobre sentencias, textos legales o tus propios archivos.',
      description: withFeatureMenuIcon('Desde la página principal, abre cualquier sentencia, texto legal o archivo y pulsa este menú de tres puntos. Elige Chat sobre este documento en el Abogado Virtual; si ya tienes una conversación, podrás agregar el nuevo documento sin empezar de cero. Para subir documentos propios, selecciona Mis archivos en este mismo menú: no hay un área de carga dentro del Abogado Virtual.'),
      reveal: showLawyerMainPageForTour
    }
  ].filter(Boolean);
}

function buildVoiceCallsSteps(authState) {
  if (!authState.paid) {
    return planGateSteps(
      '#audioCallModal',
      'Llamadas con el Abogado Virtual',
      'Con un plan puedes conversar por voz, aportar documentos como contexto y pedir búsquedas o la revisión del texto completo durante la llamada.'
    );
  }

  return [
    {
      target: '[data-tour="voice-call-header"]',
      title: 'Una llamada de demostración',
      description: 'Este recorrido muestra datos de ejemplo y no conecta una llamada, solicita acceso al micrófono ni guarda información. Todo el contenido de demostración desaparece al finalizar o salir.'
    },
    {
      target: '[data-lexius-tour-demo-call-context]',
      title: 'Prepara el contexto',
      description: 'Puedes iniciar sin documentos o abrir primero sentencias, textos legales y archivos para agregarlos. Durante una llamada real el contexto queda bloqueado, para que sepas exactamente qué material está usando el Abogado Virtual.'
    },
    {
      target: '[data-lexius-tour-demo-call-voice]',
      title: 'Elige y prueba una voz',
      description: 'Selecciona la voz antes de llamar y escucha una muestra si está disponible. La voz “Lucía” que ves aquí es solo un ejemplo y no modifica tu selección real.'
    },
    {
      target: '[data-lexius-tour-demo-call-controls]',
      title: 'Inicia y termina la llamada',
      description: 'El botón del teléfono inicia la conexión y puede pedir permiso para usar el micrófono; el botón rojo termina la llamada. El estado y el contador confirman cuándo la conversación está en curso. Estos controles son ilustrativos.'
    },
    {
      target: '[data-lexius-tour-demo-call-search-target]',
      title: 'Búsqueda jurídica en vivo',
      description: 'Puedes pedir por voz una búsqueda de jurisprudencia o legislación. LEXIUS muestra la consulta, el progreso y los resultados encontrados mientras la conversación continúa.'
    },
    {
      target: '[data-lexius-tour-demo-call-document-target]',
      title: 'Revisión del texto completo',
      description: 'También puedes pedir que el Abogado Virtual consulte el texto completo de un resultado. Esta tarjeta identifica el documento revisado y permite abrirlo después para verificar la explicación.'
    }
  ];
}

function buildChatResponseSteps() {
  const callLog = isCallLogChat();
  return [
    {
      target: () => chatResponseTarget('[data-tour="chat-response-content"]'),
      title: 'Tu respuesta está lista',
      description: callLog
        ? 'Este mensaje forma parte de la transcripción de una llamada. Revisa su contenido y verifica siempre la información y las fuentes antes de usarla.'
        : 'Lee la respuesta completa: puede incluir títulos, listas, tablas, enlaces o documentos generados. Verifica siempre la información y las fuentes antes de usarla.',
      beforeHighlight: closeChatSidebarForTour
    },
    {
      target: () => chatResponseTarget('[data-tour="chat-response-actions"]'),
      title: 'Acciones de la respuesta',
      description: callLog
        ? 'Los botones sobre este mensaje permiten copiarlo o descargarlo en PDF o Word.'
        : 'Los botones sobre la respuesta permiten copiarla completa, descargarla en PDF o Word y abrir un mapa mental. En un chat colaborativo también puedes responder directamente a este mensaje.',
      beforeHighlight: closeChatSidebarForTour
    },
    {
      target: () => chatResponseTarget('[data-tour="chat-response-research"]'),
      optional: true,
      title: 'Investigación y razonamiento',
      description: 'Cuando aparece esta sección, puedes desplegarla y recorrer las actividades de investigación y razonamiento realizadas para preparar la respuesta.',
      beforeHighlight: closeChatSidebarForTour
    },
    {
      target: () => chatResponseTarget('[data-tour="chat-response-review"]'),
      optional: true,
      title: 'Revisión del documento',
      description: 'Esta tarjeta muestra qué falta por decidir y qué está listo para aplicar. En tus documentos, puedes revisar las recomendaciones o actualizar el documento desde aquí. En jurisprudencia y legislación, solo consulta los hallazgos: la fuente no se modifica.',
      beforeHighlight: closeChatSidebarForTour
    },
    {
      target: () => chatResponseTarget('.sources-group-trigger, a.doc-reference'),
      optional: true,
      title: 'Consulta las fuentes',
      description: 'Selecciona una referencia o un grupo de fuentes para revisar los documentos relacionados. En una computadora también puedes previsualizar una referencia al pasar el cursor.',
      beforeHighlight: closeChatSidebarForTour
    },
    {
      target: () => chatResponseTarget('[data-tour="chat-response-document"]'),
      optional: true,
      title: 'Documento generado',
      description: 'Si la respuesta incluye un documento, estos controles copian o descargan únicamente ese documento en PDF o Word. Los botones sobre la respuesta actúan sobre la respuesta completa.',
      beforeHighlight: closeChatSidebarForTour
    },
    {
      target: '[data-tour="chat-response-suggestions"]',
      optional: true,
      title: 'Preguntas sugeridas',
      description: 'Elige una sugerencia para llevarla al cuadro de texto. Puedes revisarla o editarla antes de enviarla.',
      beforeHighlight: closeChatSidebarForTour
    },
    !callLog && {
      target: '[data-testid="chat-composer"]',
      title: 'Continúa la conversación',
      description: 'Escribe tu propia pregunta de seguimiento para continuar en el mismo chat y conservar el contexto de la conversación y sus documentos asociados.',
      beforeHighlight: closeChatSidebarForTour
    },
    callLog && {
      target: '[data-tour="chat-call-log-notice"]',
      title: 'La llamada queda como registro',
      description: 'Las transcripciones de llamadas son de solo lectura. Para hacer una pregunta de seguimiento, inicia un nuevo chat de texto.',
      beforeHighlight: closeChatSidebarForTour
    }
  ].filter(Boolean);
}

function revealDocumentEditionDifferencesForTour() {
  const button = documentEditionTarget('[data-testid="artifact-diff-open"]');
  if (
    button
    && button.getAttribute('aria-expanded') !== 'true'
    && !button.disabled
  ) button.click();
}

function buildDocumentEditionSteps() {
  const closeSidebar = { beforeHighlight: closeChatSidebarForTour };
  const revealDifferences = {
    reveal: revealDocumentEditionDifferencesForTour,
    ...closeSidebar
  };
  const targetOrPanel = selector => (
    documentEditionTarget(selector)
    || documentEditionTarget('[data-testid="artifact-diff-panel"]')
    || documentEditionTarget('[data-tour="document-edition-comparison"]')
  );
  const workspace = activeDocumentEditionWorkspace?.isConnected
    ? activeDocumentEditionWorkspace
    : latestDocumentEditionWorkspace({ visibleOnly: true });
  const comparisonState = workspace?.getAttribute('data-document-edition-comparison-state');
  const comparisonHasChanges = comparisonState === 'changes';
  const steps = [
    {
      target: () => documentEditionTarget('[data-tour="document-edition-overview"]'),
      title: 'Edita sin perder el trabajo anterior',
      description: 'Para hacer un cambio, pídelo en este mismo chat e indica qué quieres modificar y qué debe mantenerse. Por ejemplo: «En la cláusula quinta, cambia el plazo de 30 a 45 días y conserva el resto». LEXIUS guardará el resultado como una nueva versión.',
      ...closeSidebar
    },
    {
      target: () => documentEditionTarget('[data-tour="document-edition-versions"]'),
      title: 'Consulta todas las versiones',
      description: 'v1 es el documento inicial. Cada número siguiente identifica una nueva versión. Selecciona una versión disponible para verla, copiarla o descargarla; abrirla no modifica ni elimina las demás.',
      ...closeSidebar
    },
    {
      target: () => documentEditionTarget('[data-tour="document-edition-comparison"]'),
      title: 'Comprueba qué cambió',
      description: 'Selecciona Ver diferencias para comparar la versión abierta con la versión en la que se basó. La comparación es solo para revisar: no modifica ninguna de las dos.',
      ...closeSidebar
    }
  ];
  if (comparisonHasChanges) steps.push({
    target: () => targetOrPanel('[data-tour="document-edition-summary"]'),
    title: 'Distingue lo añadido y lo eliminado',
    description: 'LEXIUS marca el texto añadido y el eliminado dentro del documento completo. El texto sin marcas permanece visible para que puedas valorar cada ajuste en su contexto.',
    ...revealDifferences
  }, {
    target: () => targetOrPanel('[data-tour="document-edition-navigator"]'),
    title: 'Revisa los cambios uno por uno',
    description: 'Cada cambio muestra el texto de Antes y de Ahora. Usa Anterior y Siguiente para recorrerlos sin perder tu lugar en el documento.',
    ...revealDifferences
  });
  if (comparisonState === 'unchanged') steps.push({
    target: () => targetOrPanel('[data-tour="document-edition-empty"]'),
    title: 'No hay cambios de texto',
    description: 'Si ambas versiones tienen el mismo texto, LEXIUS te lo indicará aquí. La nueva versión se conserva en el historial, pero no hay cambios que revisar.',
    ...revealDifferences
  });
  steps.push({
    target: () => targetOrPanel('[data-testid="artifact-diff-open"]'),
    title: 'Sigue trabajando en el mismo chat',
    description: 'Selecciona Volver al documento. Si necesitas otro ajuste, descríbelo aquí; LEXIUS guardará el resultado como una nueva versión para que puedas revisarlo y compararlo.',
    ...revealDifferences
  });
  return steps;
}

function buildDocumentReviewSteps() {
  const closeSidebar = { beforeHighlight: closeChatSidebarForTour };
  return [
    {
      target: '[data-tour="document-review-overview"]',
      title: 'Qué muestra la revisión',
      description: 'Aquí ves qué documento se revisó y un resumen de los hallazgos y sus fuentes. Si entras desde Revisar, aparecen todos los hallazgos; usa Por decidir cuando quieras centrarte en las recomendaciones pendientes.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-pending-guide"]',
      optional: true,
      title: 'Recomendaciones pendientes',
      description: 'Esta guía reúne solo los hallazgos que todavía necesitan una decisión. Selecciona cualquier título para desplazarte suavemente hasta su tarjeta. Cuando estén disponibles, los controles inferiores permiten aprobar o no aplicar de una vez las recomendaciones restantes.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-finding"]',
      optional: true,
      title: 'Hallazgos, uno por uno',
      description: 'Cada tarjeta explica un punto que merece atención. Las etiquetas indican su prioridad y si la señal está respaldada o todavía debe verificarse; debajo encontrarás la explicación y el siguiente paso sugerido.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-evidence"]',
      optional: true,
      title: 'Texto que respalda el hallazgo',
      description: 'Abre Evidencia documental para ver el fragmento del documento relacionado con el hallazgo. Si aparece Ver contexto completo, úsalo para comprobar el pasaje dentro de su ubicación original.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-legal-bases"]',
      optional: true,
      title: 'Fundamento jurídico',
      description: 'Aquí puedes consultar la norma o fuente jurídica utilizada y leer por qué se relaciona con el hallazgo. Abre el contexto de la fuente cuando necesites verificarla con más detalle.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-interpretation"]',
      optional: true,
      title: 'Otras interpretaciones',
      description: 'Esta sección muestra otra forma razonable de entender el punto revisado. Te ayuda a valorar matices y a decidir con más información, especialmente cuando el riesgo no es concluyente.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-note"]',
      optional: true,
      title: 'Añade una nota si hace falta',
      description: 'La nota es opcional. Úsala para explicar a la IA un dato o una comprobación que deba tener en cuenta. Se guarda cuando eliges una decisión.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-actions"]',
      optional: true,
      title: 'Decide qué hacer',
      description: 'En tus documentos, aprueba solo las recomendaciones que quieras aplicar. Puedes decidirlas una por una o resolver las restantes de una vez después de ver sus títulos; el botón del final aplica únicamente las aprobadas. En jurisprudencia y legislación, las decisiones solo organizan la revisión y la fuente nunca se modifica.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-aspects"]',
      optional: true,
      title: 'Qué aspectos se revisaron',
      description: 'Despliega esta lista para conocer los temas que LEXIUS comprobó, como legalidad, obligaciones o coherencia. Es un resumen del alcance de la revisión, no una lista de problemas.',
      ...closeSidebar
    },
    {
      target: '[data-tour="document-review-guidance"]',
      title: 'Una ayuda para tu criterio',
      description: 'La revisión organiza señales, fuentes y posibles decisiones para facilitar tu trabajo. Antes de actuar, comprueba los pasajes y, si el contenido es institucional, contrástalos con la publicación oficial vigente.',
      ...closeSidebar
    }
  ];
}

function buildFilesSteps(authState) {
  if (!hasTourPlanAccess(authState, 'files')) {
    return planGateSteps(
      '#userFilesSelectModal',
      'Mis archivos',
      'Con un plan puedes subir documentos, imágenes, audio, video, presentaciones u hojas de cálculo y trabajar con ellos dentro de LEXIUS.'
    );
  }
  return [
    {
      title: 'Dónde abrir Mis archivos',
      description: withFeatureMenuIcon('Abre el menú de tres puntos de la aplicación y selecciona Mis archivos.')
    },
    {
      target: '#userFilesSelectModal .file-type-selection',
      title: 'Elige el tipo de archivo',
      description: 'Puedes subir Word, PDF, imágenes, audio, video, PowerPoint u hojas de cálculo.'
    },
    {
      target: () => findVisible('#userFilesSelectModal .drop-zone'),
      title: 'Selecciona el archivo',
      description: 'Arrastra el archivo a la zona correspondiente o tócala para elegirlo desde el dispositivo.'
    },
    {
      target: '#submitFiles',
      title: 'Sube el archivo',
      description: 'Después de elegirlo, pulsa Subir. Los audios y videos se transcriben; si contienen varias voces, activa Identificar Interlocutores antes de subirlos para separar y organizar cada participación.'
    },
    {
      target: '#uploadTargetToggleCard',
      optional: true,
      title: 'Elige quién tendrá acceso',
      description: 'Los administradores corporativos pueden guardar el archivo en Mis archivos, donde es privado, o en Archivos corporativos para compartirlo con la organización.'
    },
    {
      target: '#user-files-search-toggle-card',
      optional: true,
      title: 'Autoriza el uso por la IA',
      description: 'Activa esta opción si quieres que búsquedas, chats y llamadas consulten tus archivos personales cuando sea útil. Las síntesis no los usan y los archivos corporativos se administran por separado.'
    },
    {
      target: '#user-files-container',
      title: 'Biblioteca de archivos',
      description: 'Tus archivos subidos aparecen aquí. Puedes abrir, renombrar o eliminar cada archivo y aplicar las funciones disponibles.'
    },
    {
      target: '#fileActionsBar',
      title: 'Trabaja con varios archivos',
      description: 'Selecciona uno o varios archivos para iniciar un chat o una llamada, obtener una síntesis, crear video, presentación, página o canción, y eliminar los archivos que tengas permitido administrar.'
    }
  ];
}

function buildUserFileFoldersSteps(authState) {
  if (!hasTourPlanAccess(authState, 'user-file-folders')) {
    return planGateSteps(
      '#userFilesSelectModal',
      'Carpetas',
      'Organiza tus archivos por caso, cliente o tema.'
    );
  }
  return [
    {
      target: '[data-tour="folders-files"]',
      mobileTarget: '[data-tour="folders-file"]',
      title: 'Organiza tus archivos en carpetas',
      description: document.querySelector('[data-tour="folders-preview"]')?.dataset.demoFile === 'true'
        ? 'Agrupa tus archivos por caso, cliente o tema. Usaremos un archivo de ejemplo temporal.'
        : 'Organiza estos archivos de tu biblioteca por caso, cliente o tema.',
      reveal: () => showUserFileFoldersPreview('library')
    },
    {
      target: '[data-tour="folders-create"]',
      title: 'Crea una carpeta',
      description: 'Pulsa Nueva carpeta, escribe un nombre y elige Crear carpeta.',
      reveal: () => showUserFileFoldersPreview('create')
    },
    {
      target: '[data-tour="folders-file"]',
      title: 'Selecciona tus archivos',
      description: 'Marca uno o varios archivos que quieras organizar.',
      reveal: () => showUserFileFoldersPreview('select')
    },
    {
      target: '[data-tour="folders-move"]',
      title: 'Mueve archivos a una carpeta',
      description: 'Pulsa Mover a carpeta, elige el destino y confirma con Mover aquí.',
      reveal: () => showUserFileFoldersPreview('move')
    },
    {
      target: '[data-tour="folders-files"]',
      mobileTarget: '[data-tour="folders-file"]',
      title: 'Abre tu carpeta',
      description: 'Abre una carpeta para ver y buscar sus archivos. La flecha te lleva a Todos mis archivos.',
      reveal: () => showUserFileFoldersPreview('open')
    },
    {
      target: '[data-tour="folders-filter"]',
      title: 'Encuentra tus archivos',
      description: 'Todos muestra tu biblioteca completa. Sin carpeta reúne los archivos pendientes de organizar.',
      reveal: () => showUserFileFoldersPreview('find')
    },
    {
      target: '[data-tour="folders-menu"]',
      title: 'Administra tus carpetas',
      description: 'Usa el menú de la carpeta para cambiar su nombre o eliminarla. Sus archivos se conservan.',
      reveal: () => showUserFileFoldersPreview('manage')
    }
  ];
}

function cloneUserFileTourElement(source) {
  const clone = source.cloneNode(true);
  // Keep the real appearance, without IDs, file actions or delegated handlers.
  [clone, ...clone.querySelectorAll('*')].forEach(element => {
    Array.from(element.attributes).forEach(attribute => {
      if (/^(id|name|for|href|draggable|autofocus|aria-labelledby|aria-describedby)$|^data-|^on/i.test(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    });
    element.classList.remove('document-checkbox', 'startAILawyerDocumentChat');
    if (element.matches('button, input, a, [tabindex]')) element.tabIndex = -1;
    if (element.matches('input')) element.readOnly = true;
  });
  return clone;
}

function showUserFileFoldersPreview(stage) {
  const preview = document.querySelector('[data-tour="folders-preview"]');
  if (!preview) return;
  preview.dataset.stage = stage;
  const selected = stage === 'select' || stage === 'move';
  const moved = ['open', 'find', 'manage'].includes(stage);
  preview.querySelector('[data-tour="folders-create"]').hidden = stage !== 'create';
  preview.querySelector('[data-tour="folders-move"]').hidden = stage !== 'move';
  preview.querySelector('[data-tour="folders-menu"]').hidden = stage !== 'manage';
  preview.querySelector('[data-tour="folders-selection"]').hidden = !selected;
  preview.querySelector('[data-tour="folders-tile"]').hidden = ['library', 'create', 'open'].includes(stage);
  preview.querySelector('[data-tour="folders-filter"]').hidden = stage === 'open';
  preview.querySelector('[data-tour="folders-back"]').hidden = stage !== 'open';
  preview.querySelector('[data-tour="folders-current"]').textContent = stage === 'open' ? 'Caso de ejemplo' : 'Todos mis archivos';
  preview.querySelector('[data-tour="folders-search"]').placeholder = stage === 'open'
    ? 'Buscar en «Caso de ejemplo»' : 'Buscar en todos mis archivos';
  const cards = Array.from(preview.querySelectorAll('.file-card'));
  cards.forEach((card, index) => {
    card.hidden = stage === 'open' && index > 1;
    card.querySelector('input[type="checkbox"]').checked = selected && index < 2;
  });
  const count = moved ? Math.min(cards.length, 2) : 0;
  preview.querySelector('.ufo-folder-count').textContent = `${count} ${count === 1 ? 'archivo' : 'archivos'}`;
}

async function prepareUserFileFoldersSurface(authState) {
  const available = hasTourPlanAccess(authState, 'user-file-folders');
  const cleanupModal = await prepareFeatureModalSurface('#userFilesSelectModal', available);
  if (!available) return cleanupModal;
  const modalBody = document.querySelector('#userFilesSelectModal .modal-dialog .modal-body');
  if (!modalBody) return cleanupModal;
  const modal = modalBody.closest('.modal');
  const modalScrollTop = modal.scrollTop;
  const scrollTop = modalBody.scrollTop;
  const preview = document.createElement('section');
  preview.className = 'lexius-tour-folders-preview';
  preview.dataset.tour = 'folders-preview';
  const cleanup = async () => {
    preview.remove();
    modalBody.classList.remove('lexius-tour-folders-body');
    await cleanupModal();
    modalBody.scrollTop = scrollTop;
    modal.scrollTop = modalScrollTop;
  };
  try {
    // Startup may still be loading the library when the tour opens from Settings.
    const startedAt = Date.now();
    while ((window.userFilesIsLoading || window.userLibraryRenderedFiles === null)
      && Date.now() - startedAt < 5000) await wait(50);
    const sourceCards = Array.from(document.querySelectorAll('#user-files .file-card')).slice(0, 3);
    preview.dataset.demoFile = String(sourceCards.length === 0);
    preview.innerHTML = `
      <div class="ufo-section-head"><strong>Carpetas</strong><small>Vista previa · Sin cambios en tu biblioteca</small></div>
      <nav class="ufo-bar" data-tour="folders-location" aria-label="Ubicación en tus archivos">
        <div class="ufo-crumbs">
          <span class="ufo-crumb" data-tour="folders-back" hidden><i class="anticon anticon-left" aria-hidden="true"></i></span>
          <span class="ufo-crumb"><i class="anticon anticon-folder-open" aria-hidden="true"></i><span data-tour="folders-current">Todos mis archivos</span></span>
        </div>
        <button type="button" class="btn btn-outline-primary btn-sm"><i class="anticon anticon-folder-add" aria-hidden="true"></i>Nueva carpeta</button>
      </nav>
      <div data-tour="folders-create" class="ufo-dialog lexius-tour-folders-dialog" hidden></div>
      <div class="ufo-folder" data-tour="folders-tile" hidden>
        <button type="button" class="ufo-folder-open"><i class="anticon anticon-folder" aria-hidden="true"></i><span class="ufo-folder-text"><span class="ufo-folder-name">Caso de ejemplo</span><span class="ufo-folder-count">0 archivos</span></span></button>
        <button type="button" class="ufo-more" aria-label="Más opciones de Caso de ejemplo"><i class="anticon anticon-ellipsis" aria-hidden="true"></i></button>
      </div>
      <div class="ufo-menu lexius-tour-folders-menu" data-tour="folders-menu" hidden>
        <button type="button"><i class="anticon anticon-edit" aria-hidden="true"></i>Cambiar nombre</button>
        <button type="button" class="is-danger"><i class="anticon anticon-delete" aria-hidden="true"></i>Eliminar carpeta</button>
      </div>
      <div class="ufo-section-head" data-tour="folders-filter"><span>Archivos</span><span class="ufo-filter"><button type="button" aria-pressed="true">Todos</button><button type="button" aria-pressed="false">Sin carpeta</button></span></div>
      <div class="ufo-selection" data-tour="folders-selection" hidden><strong></strong><button type="button" class="btn btn-primary btn-sm">Mover a carpeta</button></div>
      <div data-tour="folders-move" class="ufo-dialog lexius-tour-folders-dialog" hidden></div>
      <input class="form-control" type="search" data-tour="folders-search" placeholder="Buscar en todos mis archivos" aria-label="Buscar archivos">
      <div class="files-library" data-tour="folders-files"></div>`;
    const fileList = preview.querySelector('[data-tour="folders-files"]');
    sourceCards.forEach(source => {
      const card = cloneUserFileTourElement(source);
      card.style.removeProperty('display');
      card.classList.remove('ufo-dragging');
      card.querySelector('.ufo-chip-slot')?.remove();
      fileList.appendChild(card);
    });
    if (!sourceCards.length) {
      fileList.innerHTML = `<div class="file-card pdf" data-demo-file>
        <div class="file-card-checkbox"><input type="checkbox" class="form-check-input" aria-label="Seleccionar Contrato de ejemplo.pdf"></div>
        <div class="file-type-badge">pdf</div>
        <div class="file-card-icon"><i class="anticon anticon-file-pdf" aria-hidden="true"></i></div>
        <div class="file-card-content"><div class="file-card-title">Contrato de ejemplo.pdf</div><small>Archivo temporal de ejemplo</small></div>
      </div>`;
    }
    fileList.firstElementChild.dataset.tour = 'folders-file';
    const count = Math.min(fileList.children.length, 2);
    preview.querySelector('[data-tour="folders-selection"] strong').textContent = `${count} ${count === 1 ? 'archivo seleccionado' : 'archivos seleccionados'}`;
    const createForm = cloneUserFileTourElement(document.querySelector('#userFileFolderNameDialog .modal-content'));
    createForm.querySelector('.modal-title').textContent = 'Nueva carpeta';
    createForm.querySelector('input').value = 'Caso de ejemplo';
    createForm.querySelector('input').classList.remove('is-invalid');
    createForm.querySelector('.ufo-err').textContent = '';
    createForm.querySelector('.btn-primary').textContent = 'Crear carpeta';
    preview.querySelector('[data-tour="folders-create"]').appendChild(createForm);
    const moveDialog = cloneUserFileTourElement(document.querySelector('#userFileMoveDialog .modal-content'));
    moveDialog.querySelector('.modal-title').textContent = `Mover ${count} ${count === 1 ? 'archivo' : 'archivos'}`;
    moveDialog.querySelector('.ufo-dest-list').nextElementSibling.replaceChildren();
    moveDialog.querySelector('.ufo-dest-list').innerHTML = `
      <li><button type="button" class="ufo-dest" role="radio" aria-checked="true"><i class="anticon anticon-folder" aria-hidden="true"></i><span class="ufo-dest-name">Caso de ejemplo</span></button></li>
      <li><button type="button" class="ufo-dest" role="radio" aria-checked="false"><i class="anticon anticon-inbox" aria-hidden="true"></i><span class="ufo-dest-name">Sin carpeta</span></button></li>`;
    moveDialog.querySelector('.btn-primary').disabled = false;
    preview.querySelector('[data-tour="folders-move"]').appendChild(moveDialog);
    preview.querySelectorAll('button, input').forEach(element => { element.tabIndex = -1; });
    ['click', 'change', 'submit', 'keydown'].forEach(type => preview.addEventListener(type, event => {
      if (type === 'keydown' && event.key === 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
    }, true));
    modalBody.appendChild(preview);
    modalBody.classList.add('lexius-tour-folders-body');
    showUserFileFoldersPreview('library');
    return cleanup;
  } catch (error) {
    await cleanup();
    throw error;
  }
}

function buildVideoSteps(authState) {
  if (!authState.paid) {
    return planGateSteps(
      '#videoPodcastModal',
      'Video Podcast',
      'Con un plan puedes convertir el contexto seleccionado en un video narrado y personalizar sus voces, formato visual, música y subtítulos.'
    );
  }
  return [
    {
      title: 'Dónde abrir Video Podcast',
      description: withFeatureMenuIcon('Abre el menú de tres puntos y selecciona Video Podcast.'),
    },
    sharedDocumentContextStep(),
    {
      target: '#videoPodcastContext',
      title: 'Contexto del video',
      description: 'Abre primero una sentencia, texto legal o archivo para usarlo como contexto, o inicia el generador sin documentos.'
    },
    {
      target: '#videoPodcastInstructions',
      title: 'Define el enfoque',
      description: 'Indica cómo quieres que se genere el contenido: por ejemplo, como resumen o explicación detallada.'
    },
    {
      target: () => document.getElementById('slidesOption')?.closest('.col-12'),
      tabletTarget: () => document.getElementById('slidesOption')?.closest('.form-check'),
      title: 'Elige el formato visual',
      description: 'Acompaña el video con una presentación o con un mapa mental generado por IA.'
    },
    {
      target: '#vidpod-slideTemplatesSection',
      mobileTarget: () => findVisible(
        '#vidpod-slideTemplatesSection .vidpod-slide-template-card.selected, '
        + '#vidpod-slideTemplatesSection .vidpod-slide-template-card, '
        + '#vidpod-slideTemplatesSection > label'
      ),
      optional: true,
      title: 'Plantilla de la presentación',
      description: 'Si eliges Presentación, debes seleccionar una plantilla para definir el diseño de las diapositivas.'
    },
    {
      target: '#soundtrackSection',
      tabletTarget: '#soundtrackSection .soundtrack-selection',
      optional: true,
      title: 'Música de fondo',
      description: 'La música es opcional. Elige una pista y usa Escuchar para previsualizarla, o selecciona Sin música de fondo.'
    },
    {
      target: '#voiceSelectionSection',
      optional: true,
      title: 'Voces del diálogo',
      description: 'Elige y previsualiza las voces del anfitrión y del invitado.'
    },
    {
      target: () => document.getElementById('enableCaptionsCheckbox')?.closest('.col-12'),
      tabletTarget: () => document.getElementById('enableCaptionsCheckbox')?.closest('.form-check'),
      title: 'Subtítulos',
      description: 'Activa esta opción si quieres incorporar subtítulos al video.'
    },
    {
      target: '#generateVideoPodcastButton',
      title: 'Genera el video',
      description: 'Cuando termines de configurar el contenido, este botón inicia la generación. El resultado queda disponible para reproducir y descargar.'
    },
    {
      target: '#video-podcasts-history',
      optional: true,
      title: 'Historial de videos',
      description: 'Tus videos anteriores aparecen aquí para reproducirlos, renombrarlos, descargarlos, copiar su enlace, volver a abrir sus documentos de contexto o eliminarlos.'
    }
  ].filter(Boolean);
}

function buildSlidesSteps(authState) {
  if (!authState.paid) {
    return planGateSteps(
      '#slidesGeneratorModal',
      'Presentaciones',
      'Con un plan puedes crear una presentación de PowerPoint desde documentos o instrucciones y elegir una plantilla de diseño.'
    );
  }
  return [
    {
      title: 'Dónde abrir Presentaciones',
      description: withFeatureMenuIcon('Abre el menú de tres puntos y selecciona Generar presentación de PowerPoint.')
    },
    sharedDocumentContextStep(),
    {
      target: '#slidesGeneratorContext',
      title: 'Contexto de la presentación',
      description: 'Abre un contenido o selecciona archivos para usarlos como contexto. También puedes generar desde tus instrucciones.'
    },
    {
      target: '#slidesGeneratorInstructions',
      title: 'Personaliza el contenido',
      description: 'Escribe las instrucciones obligatorias: describe el enfoque, el nivel de detalle o la cantidad de diapositivas que necesitas.'
    },
    {
      target: '#slideTemplatesSection',
      mobileTarget: () => findVisible(
        '#slideTemplatesSection .slide-template-card.selected, '
        + '#slideTemplatesSection .slide-template-card, '
        + '#slideTemplatesSection > label'
      ),
      title: 'Elige una plantilla',
      description: 'Selecciona una plantilla obligatoria para definir el diseño visual de las diapositivas.'
    },
    {
      target: '#generateSlidesButton',
      title: 'Crea el PowerPoint',
      description: 'Este botón inicia la generación. Al terminar, puedes descargar la presentación y consultar su historial.'
    },
    {
      target: '#slides-generator-history',
      optional: true,
      title: 'Historial de presentaciones',
      description: 'Aquí puedes renombrar, descargar o eliminar presentaciones anteriores y volver a abrir sus documentos de contexto.'
    }
  ].filter(Boolean);
}

function buildInteractivePagesSteps(authState) {
  if (!authState.paid) {
    return planGateSteps(
      '#interactivePagesModal',
      'Páginas Interactivas',
      'Con un plan y la Sincronización en la nube activa puedes crear una página web interactiva desde instrucciones y documentos, y compartirla mediante un enlace público.'
    );
  }
  if (window.sync_enabled !== true) {
    return [
      {
        target: '#interactivePagesSyncRequired',
        title: 'Activa la sincronización',
        description: 'Páginas Interactivas requiere la Sincronización en la nube. Usa Activar sincronización en este aviso o configúrala desde Ajustes.'
      },
      {
        title: 'Qué puedes crear',
        description: 'Esta función transforma instrucciones y documentos en una página web interactiva. Al terminar puedes abrirla o copiar su enlace público.'
      }
    ];
  }
  return [
    {
      title: 'Dónde abrir Páginas Interactivas',
      description: withFeatureMenuIcon('Abre el menú de tres puntos y selecciona Páginas Interactivas.')
    },
    sharedDocumentContextStep(),
    {
      target: '#interactivePagesContext',
      title: 'Contexto de la página',
      description: 'Usa una sentencia, texto legal o archivo como contexto, o crea la página solo desde tus instrucciones.'
    },
    {
      target: '#interactivePagesInstructions',
      title: 'Describe la página',
      description: 'Escribe las instrucciones obligatorias: indica la audiencia, la información que quieres destacar, la organización y el estilo visual.'
    },
    {
      target: '#generateInteractivePageButton',
      title: 'Crea y comparte',
      description: 'Este botón inicia la generación. El resultado se puede abrir y compartir mediante un enlace público.'
    },
    {
      target: '#interactive-pages-history',
      optional: true,
      title: 'Historial de páginas',
      description: 'Abre o comparte cada página, solicita cambios para crear una versión nueva y consulta versiones anteriores. Restaurar una versión crea una nueva versión a partir de ella, sin borrar el historial.'
    }
  ].filter(Boolean);
}

function buildSongsSteps(authState) {
  if (!authState.paid) {
    return planGateSteps(
      '#songsModal',
      'Canciones con IA',
      'Con un plan y la Sincronización en la nube activa puedes generar una canción desde una idea o documentos jurídicos; también puedes definir estilo, sentimiento, tempo y una preferencia de voz.'
    );
  }
  if (window.sync_enabled !== true) {
    return [
      {
        target: '#songsSyncRequired',
        title: 'Activa la sincronización',
        description: 'Canciones con IA requiere la Sincronización en la nube. Usa Activar sincronización en este aviso o configúrala desde Ajustes.'
      },
      {
        title: 'Qué puedes generar',
        description: 'Esta función investiga tu idea y el contexto jurídico seleccionado para crear la letra y el audio de una canción.'
      }
    ];
  }
  return [
    {
      title: 'Dónde abrir Canciones con IA',
      description: withFeatureMenuIcon('Abre el menú de tres puntos y selecciona Canciones.')
    },
    sharedDocumentContextStep(),
    {
      target: '#songsContext',
      title: 'Idea y contexto',
      description: 'Puedes generar sin documentos o agregar sentencias, textos legales y archivos para fundamentar la investigación y la letra.'
    },
    {
      target: '#songsInstructions',
      title: 'Describe la canción',
      description: 'Escribe las instrucciones obligatorias: indica el tema, mensaje, tono, estructura o cualquier restricción para la letra.'
    },
    {
      target: () => document.getElementById('songTitleInput')?.closest('.col-md-6'),
      tabletTarget: '#songTitleInput',
      title: 'Ponle un título',
      description: 'El título es opcional. Si lo dejas vacío, LEXIUS genera uno corto automáticamente.'
    },
    {
      target: () => document.getElementById('songStyleInput')?.closest('.col-md-6'),
      tabletTarget: '#songStyleInput',
      title: 'Estilo musical',
      description: 'Elige un estilo de la lista o selecciona Otro para escribir uno propio.'
    },
    {
      target: () => document.getElementById('songMoodSelect')?.closest('.col-md-6'),
      tabletTarget: '#songMoodSelect',
      title: 'Sentimiento',
      description: 'Puedes indicar el sentimiento que debe transmitir la canción o dejarlo sin preferencia.'
    },
    {
      target: () => document.getElementById('songTempoSelect')?.closest('.col-md-6'),
      tabletTarget: '#songTempoSelect',
      title: 'Tempo',
      description: 'Elige un tempo lento, medio o rápido, o deja que la IA lo decida.'
    },
    {
      target: () => document.querySelector('.songs-vocal-gender-group')?.closest('.col-md-6'),
      tabletTarget: '.songs-vocal-gender-group',
      title: 'Preferencia de voz',
      description: 'Indica una preferencia de voz o selecciona Sin preferencia. La IA intenta respetarla, pero el tipo de voz final puede variar.'
    },
    {
      target: '#generateSongButton',
      title: 'Genera letra y audio',
      description: 'Este botón inicia la generación. El resultado incluye la canción y su letra.'
    },
    {
      target: '#songs-history',
      optional: true,
      title: 'Historial de canciones',
      description: 'Reproduce o descarga el audio, sigue la letra sincronizada, copia el enlace, vuelve al contexto utilizado o elimina una canción.'
    }
  ].filter(Boolean);
}

function buildAlertsSteps(authState) {
  if (!authState.paid) {
    return planGateSteps(
      '#alertsModal',
      'Alertas Inteligentes',
      'Con un plan y la Sincronización en la nube activa puedes crear alertas por consulta, carpeta o ambas y recibir avisos cuando se incorporen contenidos relevantes.'
    );
  }
  if (window.sync_enabled !== true) {
    return [
      {
        target: '#alertsSyncRequired',
        title: 'Activa la sincronización',
        description: 'Alertas Inteligentes requiere la Sincronización en la nube. Usa Activar sincronización en este aviso o configúrala desde Ajustes.'
      },
      {
        title: 'Cómo funcionan las alertas',
        description: 'Define una consulta, una carpeta o ambas y elige una frecuencia. Recibirás un correo cuando existan novedades relevantes.'
      }
    ];
  }
  return [
    {
      title: 'Dónde abrir Alertas Inteligentes',
      description: withFeatureMenuIcon('Abre el menú de tres puntos de la aplicación y selecciona Alertas Inteligentes. Cada alerta activa revisa las nuevas sentencias y textos legales que LEXIUS incorpora después de crearla.')
    },
    {
      target: '#alertsPushCard',
      optional: true,
      title: 'Avisos en el dispositivo',
      description: 'El correo forma parte de cada alerta. Además, si el navegador lo permite, puedes autorizar notificaciones push para este dispositivo; son opcionales, se configuran por dispositivo y no sustituyen el correo.'
    },
    {
      target: '.alerts-create-card .alerts-card-header',
      title: 'Define qué quieres vigilar',
      description: 'Debes indicar al menos una consulta o una carpeta. Si completas ambas, LEXIUS busca el tema solo dentro de esa carpeta. Puedes mantener hasta 10 alertas activas.'
    },
    {
      target: () => document.getElementById('alertQuery')?.closest('.col-12'),
      tabletTarget: '#alertQuery',
      title: 'Describe el tema',
      description: 'Escribe el asunto jurídico en lenguaje natural, por ejemplo “contrato de arrendamiento” o “debido proceso”. La IA usa la consulta para identificar novedades relevantes; este campo es opcional si eliges una carpeta.'
    },
    {
      target: '.alerts-folder-row',
      title: 'Delimita por carpeta',
      description: 'Elegir abre la estructura documental del país. La ruta seleccionada aparece aquí y puedes limpiarla después. Si dejas la consulta vacía, la alerta seguirá todas las novedades incorporadas dentro de esa carpeta.'
    },
    {
      target: () => document.getElementById('alertFrequency')?.closest('.col-12'),
      tabletTarget: '#alertFrequency',
      title: 'Elige la frecuencia',
      description: 'Diaria, semanal o mensual indica cuándo se procesan las coincidencias acumuladas. No recibirás un correo en cada período: solo se envía si hay novedades que puedan incluirse.'
    },
    {
      target: '.alerts-form-actions',
      tabletTarget: '#saveAlertButton',
      title: 'Guarda la alerta',
      description: 'Guardar crea la alerta y programa su primera revisión; no envía nada de inmediato. Cuando corresponda, el correo de tu cuenta recibirá el análisis generado por IA y enlaces a los resultados.'
    },
    {
      target: '.alerts-table-card',
      mobileTarget: '.alerts-table-card .alerts-card-header',
      tabletTarget: '.alerts-table-card .alerts-card-header',
      title: 'Revisa tus alertas',
      description: 'El contador y cada fila muestran el criterio, la carpeta, la frecuencia, el correo, el próximo envío y el estado. También verás la última ejecución y coincidencias pendientes cuando existan. Si aún no tienes alertas, la fila “Ejemplo no guardado” solo se muestra durante este recorrido.'
    },
    {
      target: () => findVisible('#alertsTable tbody tr .alert-frequency-cell')
        || findVisible('.alerts-table-card .alerts-card-header'),
      title: 'Ajusta la frecuencia',
      description: 'Editar en Frecuencia cambia por separado la periodicidad de los envíos futuros. Puedes elegir diaria, semanal o mensual sin crear otra alerta.'
    },
    {
      target: () => findVisible('#alertsTable tbody tr .alert-next-run-cell')
        || findVisible('.alerts-table-card .alerts-card-header'),
      title: 'Programa la próxima revisión',
      description: 'Editar en Próximo cambia la fecha y hora de la siguiente ejecución sin alterar la frecuencia. La hora se interpreta según la hora local del país activo.'
    },
    {
      target: () => findVisible('#alertsTable tbody tr .alert-actions-group')
        || findVisible('.alerts-table-card .alerts-card-header'),
      title: 'Consulta el historial',
      description: 'Historial muestra hasta los 10 envíos más recientes, su estado y un resumen. Cuando un envío tiene resultados, Ver resultados abre las coincidencias guardadas en LEXIUS.'
    },
    {
      target: () => findVisible('#alertsTable tbody tr .alert-actions-group')
        || findVisible('.alerts-table-card .alerts-card-header'),
      title: 'Prueba antes de esperar',
      description: 'Probar busca coincidencias de inmediato. Si las encuentra, realiza un envío real por correo y push habilitado, lo registra en el historial y puede incluir documentos anteriores; confirma solo cuando quieras recibir esa prueba.'
    },
    {
      target: () => findVisible('#alertsTable tbody tr .alert-actions-group')
        || findVisible('.alerts-table-card .alerts-card-header'),
      title: 'Pausa, reanuda o elimina',
      description: 'Pausar detiene las revisiones y los envíos sin borrar la alerta; las novedades incorporadas mientras esté pausada no se agregan a ella. Reanudar la activa de nuevo. Eliminar pide confirmación y la quita de la lista.'
    }
  ];
}

function buildSettingsSteps() {
  const structureStep = isTouchLayout() ? null : {
    target: '.structure-setting',
    tabletTarget: '.structure-setting .items',
    optional: true,
    title: 'Estructura',
    description: 'En pantallas amplias puedes elegir a qué lado aparece el panel de búsqueda.'
  };
  return [
    {
      target: '#guidedToursSettingsCard',
      title: 'Ayuda cuando la necesites',
      description: 'Desde esta tarjeta puedes abrir el centro y repetir cualquier recorrido.'
    },
    {
      target: '#usageGuidesSettingsCard',
      mobileTarget: '#usageGuidesSettingsCard .usage-guides-settings-card__actions',
      title: 'Guías de uso',
      description: 'Abre el manual completo para consultar todas las funciones o la guía rápida para comenzar en pocos minutos.'
    },
    {
      target: '#syncSettingsCard',
      mobileTarget: '#syncSettingsCard .sync-info',
      title: 'Sincronización en la nube',
      description: 'Actívala con el código de 6 dígitos enviado a tu correo para respaldar y sincronizar tus datos. Después, migra los datos locales de cada dispositivo donde ya usabas LEXIUS. En cada uno suele bastar una migración; repítela solo para recuperar elementos locales que falten.'
    },
    {
      target: '#font-size-row',
      mobileTarget: '#font-size-row .card-body',
      title: 'Tamaño de letra',
      description: 'Ajusta el tamaño del texto de la aplicación según tu preferencia.'
    },
    {
      target: () => findVisible('.dark-mode-toogle'),
      title: 'Modo claro u oscuro',
      description: 'Alterna entre la visualización clara y oscura de la interfaz.'
    },
    structureStep,
    {
      target: () => findVisible('.themes-color'),
      tabletTarget: () => findVisible('.themes-color .items'),
      title: 'Colores y temas',
      description: 'Elige el color principal o aplica uno de los temas personalizados de la interfaz.'
    },
    {
      target: '#chatInstructionsCard',
      mobileTarget: '#chatCustomInstructions',
      title: 'Instrucciones del Abogado Virtual',
      description: 'Define el tono y los requisitos que se aplican a todos tus chats. Requiere sincronización y no se aplica a las llamadas de voz.'
    },
    {
      target: '.mindmap-mode-toggle',
      title: 'Mapas mentales',
      description: 'Decide si al abrir una rama del mapa mental también se expanden automáticamente todas sus subramas.'
    },
    {
      target: '#sync-backup-warning',
      mobileTarget: () => findVisible('#sync-backup-warning + .theme-bar-content')
        || findVisible('#sync-backup-warning strong'),
      title: 'Copia local de seguridad',
      description: 'Descarga o restaura una copia de los datos guardados en este dispositivo y elige qué contenidos incluir. Si la sincronización está activa, estos datos ya se respaldan en la nube y la copia local normalmente no es necesaria.'
    },
    {
      target: '#delete-chats-section',
      tabletTarget: '#delete-chats-section .inner-title',
      title: 'Elimina chats locales',
      description: 'Puedes borrar todos los chats locales o solo los anteriores a cierta fecha. LEXIUS pide una confirmación escrita y no elimina los chats sincronizados.'
    },
    {
      target: '.performance-mode-toggle',
      title: 'Modo de Alto Rendimiento',
      description: 'En dispositivos antiguos o con recursos limitados, este modo agiliza la apertura de documentos extensos, pero deshabilita las notas.'
    }
  ].filter(Boolean);
}

function prepareFeatureModalSurface(selector, featureAvailable) {
  const element = document.querySelector(selector);
  const alreadyOpen = !!(element?.classList.contains('show') && isVisible(element));
  return featureAvailable || alreadyOpen ? prepareModalSurface(selector) : () => { };
}

async function prepareSyncGatedFeatureModal(selector, bannerSelector, authState) {
  const banner = document.querySelector(bannerSelector);
  const initialBannerClass = banner?.className || '';
  const cleanupModal = await prepareFeatureModalSurface(selector, authState.paid);
  if (authState.paid && window.sync_enabled !== true && banner) {
    banner.classList.remove('d-none');
    await wait(20);
  }
  return async () => {
    if (banner) banner.className = initialBannerClass;
    await cleanupModal();
  };
}

function formatAlertsDemoNextRun() {
  const nextRun = new Date();
  nextRun.setDate(nextRun.getDate() + 1);
  const pad = value => String(value).padStart(2, '0');
  return `${pad(nextRun.getDate())}-${pad(nextRun.getMonth() + 1)}-${nextRun.getFullYear()} 09:00`;
}

function createAlertsDemoRow(authState) {
  const row = document.createElement('tr');
  const accountEmail = authState?.email || 'correo de tu cuenta';
  row.className = 'lexius-tour-alert-demo';
  row.dataset.lexiusTourDemoAlert = 'true';
  row.setAttribute('aria-label', 'Alerta de demostración no guardada');
  row.innerHTML = `
    <td data-label="Consulta">debido proceso</td>
    <td data-label="Carpeta">Sala Civil</td>
    <td data-label="Frecuencia" class="alert-frequency-cell">
      <div class="alert-frequency-body">
        <div class="alert-frequency-content">
          <div class="alert-frequency-display">
            <span class="alert-frequency-value">Semanal</span>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary alert-frequency-toggle" tabindex="-1" aria-disabled="true" title="Control de ejemplo no interactivo">
            <i class="anticon anticon-edit"></i>
            <span>Editar</span>
          </button>
        </div>
      </div>
    </td>
    <td data-label="Correos">${escapeHtml(accountEmail)}</td>
    <td data-label="Próximo" class="alert-next-run-cell">
      <div class="alert-next-run-body">
        <div class="alert-next-run-content">
          <div class="alert-next-run-display">
            <span class="alert-next-run-value">${escapeHtml(formatAlertsDemoNextRun())}</span>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary alert-next-run-toggle" tabindex="-1" aria-disabled="true" title="Control de ejemplo no interactivo">
            <i class="anticon anticon-edit"></i>
            <span>Editar</span>
          </button>
        </div>
      </div>
    </td>
    <td data-label="Estado">
      <span>Activa</span>
      <span class="badge bg-info text-dark ms-1">Ejemplo no guardado</span>
      <div class="text-muted small">Sin envíos</div>
    </td>
    <td class="alert-actions-cell" data-label="Acciones">
      <div class="alert-actions-group">
        <button type="button" class="btn btn-sm btn-outline-primary" tabindex="-1" aria-disabled="true" title="Control de ejemplo no interactivo">
          <i class="anticon anticon-history"></i>
          <span>Historial</span>
        </button>
        <button type="button" class="btn btn-sm btn-outline-success" tabindex="-1" aria-disabled="true" title="Control de ejemplo no interactivo">
          <i class="anticon anticon-experiment"></i>
          <span>Probar</span>
        </button>
        <button type="button" class="btn btn-sm btn-secondary" tabindex="-1" aria-disabled="true" title="Control de ejemplo no interactivo">
          <i class="anticon anticon-pause"></i>
          <span>Pausar</span>
        </button>
        <button type="button" class="btn btn-sm btn-danger" tabindex="-1" aria-disabled="true" title="Control de ejemplo no interactivo">
          <i class="anticon anticon-delete"></i>
          <span>Eliminar</span>
        </button>
      </div>
    </td>
  `;
  return row;
}

function prepareAlertsDemoPreview(authState) {
  const tableBody = document.querySelector('#alertsTable tbody');
  if (!tableBody) return () => { };

  const hiddenPlaceholderRows = new Map();
  let demoRow = null;
  let cleaned = false;

  const syncDemoRow = () => {
    if (cleaned) return;
    const configuredAlert = tableBody.querySelector(
      'tr[data-alert-id]:not([data-lexius-tour-demo-alert])'
    );
    if (configuredAlert) {
      demoRow?.remove();
      demoRow = null;
      return;
    }

    Array.from(tableBody.children).forEach(row => {
      if (row === demoRow || row.dataset.lexiusTourDemoAlert === 'true') return;
      if (!hiddenPlaceholderRows.has(row)) hiddenPlaceholderRows.set(row, row.hidden);
      row.hidden = true;
    });

    if (!demoRow?.isConnected) {
      demoRow = createAlertsDemoRow(authState);
      tableBody.appendChild(demoRow);
    }
  };

  const observer = new MutationObserver(syncDemoRow);
  observer.observe(tableBody, { childList: true });
  syncDemoRow();

  return () => {
    cleaned = true;
    observer.disconnect();
    tableBody.querySelectorAll('[data-lexius-tour-demo-alert]').forEach(row => row.remove());
    hiddenPlaceholderRows.forEach((wasHidden, row) => {
      if (row.isConnected) row.hidden = wasHidden;
    });
  };
}

async function ensureAlertsTourTableReady(timeoutMilliseconds = 3500) {
  const tableBody = document.querySelector('#alertsTable tbody');
  if (!tableBody) return;

  if (window.alertsInitialized !== true && typeof window.initializeAlerts === 'function') {
    window.initializeAlerts();
    window.alertsInitialized = true;
  }

  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMilliseconds) {
    const hasRenderedRow = tableBody.children.length > 0;
    const isLoading = tableBody.textContent?.trim() === 'Cargando...';
    if (hasRenderedRow && !isLoading) return;
    await wait(50);
  }
}

async function prepareAlertsTourSurface(authState) {
  const cleanupSurface = await prepareSyncGatedFeatureModal(
    '#alertsModal',
    '#alertsSyncRequired',
    authState
  );
  if (authState.paid && window.sync_enabled === true) {
    await ensureAlertsTourTableReady();
  }
  const cleanupDemo = authState.paid && window.sync_enabled === true
    ? prepareAlertsDemoPreview(authState)
    : () => { };
  return async () => {
    cleanupDemo();
    await cleanupSurface();
  };
}

const TOUR_DEFINITIONS = [
  {
    id: 'overview',
    title: 'Primeros pasos',
    description: 'Conoce cómo se organiza tu espacio de trabajo y aprende a recorrer la biblioteca, buscar contenido y volver a páginas anteriores. Ubica también el Abogado Virtual, el menú de funciones y los ajustes.',
    icon: 'anticon-compass',
    buildSteps: buildOverviewSteps
  },
  {
    id: 'search',
    title: 'Buscar y explorar',
    description: 'Aprende a recorrer la biblioteca y buscar dentro de la ubicación abierta con términos y filtros de fecha. También verás cómo comprobar el alcance, reutilizar consultas recientes y abrir resultados por fuente.',
    icon: 'anticon-search',
    suggestion: {
      type: 'search',
      description: 'Conoce la navegación por carpetas, la búsqueda, los filtros y el historial.'
    },
    prepare: prepareSearchSurface,
    buildSteps: buildSearchSteps
  },
  {
    id: 'search-results',
    title: 'Resultados de búsqueda',
    description: 'Aprende a distinguir resultados por fuente, ordenar cada lista y evaluar sus extractos. También verás el análisis general, cómo analizar un resultado, abrir el texto completo, paginar y usar búsquedas sugeridas.',
    icon: 'anticon-table',
    requiresSearchResults: true,
    suggestion: {
      type: 'search-results',
      description: 'Aprende a interpretar las fuentes, los extractos, el orden y las acciones de cada resultado.'
    },
    prepare: prepareSearchResultsSurface,
    buildSteps: buildSearchResultsSteps
  },
  {
    id: 'lawyer',
    title: 'Abogado Virtual',
    description: 'Aprende a iniciar, buscar y administrar chats, escribir consultas y trabajar con documentos como contexto. También verás cómo colaborar con otras personas, realizar llamadas de voz y consultar sus transcripciones.',
    icon: 'anticon-message',
    suggestion: {
      type: 'modal',
      selector: '#chatModal',
      description: 'Conoce los chats, el contexto documental, la colaboración y las llamadas.'
    },
    prepare: prepareLawyerSurface,
    buildSteps: buildLawyerSteps
  },
  {
    id: 'chat-response',
    title: 'Respuesta del Abogado Virtual',
    description: 'Aprende a revisar una respuesta, sus acciones, fuentes y el proceso de investigación que la sustenta. También verás cómo copiar o descargar la respuesta o un documento generado y continuar con nuevas preguntas.',
    icon: 'anticon-solution',
    requiresChatResponse: true,
    suggestion: {
      type: 'chat-response',
      selector: '#chatModal',
      description: 'Conoce la respuesta, sus acciones, fuentes, documentos y preguntas sugeridas.'
    },
    prepare: prepareChatResponseSurface,
    buildSteps: buildChatResponseSteps
  },
  {
    id: 'document-edition',
    title: 'Edición de documentos',
    description: 'Pide ajustes en el mismo chat sin perder el trabajo anterior. Aprende a consultar cada versión, comprobar los cambios en contexto y seguir mejorando el documento antes de copiarlo o descargarlo.',
    icon: 'anticon-diff',
    requiresDocumentEdition: true,
    suggestion: {
      type: 'document-edition',
      selector: '[data-tour="document-edition-workspace"]',
      description: 'Revisa la nueva versión y comprueba qué cambió antes de continuar.'
    },
    prepare: prepareDocumentEditionSurface,
    buildSteps: buildDocumentEditionSteps
  },
  {
    id: 'document-review',
    title: 'Revisión de Documentos',
    description: 'Aprende a revisar los hallazgos, comprobar sus fundamentos y elegir qué recomendaciones aplicar. En tus documentos verás cómo aplicar solo las aprobadas. La jurisprudencia y la legislación siempre quedan sin cambios.',
    icon: 'anticon-audit',
    requiresDocumentReview: true,
    suggestion: {
      type: 'document-review',
      selector: '[data-tour="document-review-workspace"]',
      description: 'Conoce los hallazgos, sus fundamentos y las decisiones disponibles.'
    },
    buildSteps: buildDocumentReviewSteps
  },
  {
    id: 'voice-calls',
    title: 'Llamadas con el Abogado Virtual',
    description: 'Aprende a preparar una llamada de voz con documentos y una voz elegida, controlar la conexión y seguir búsquedas o textos consultados en vivo. También verás cómo la llamada continúa en segundo plano.',
    icon: 'anticon-phone',
    planRequired: true,
    suggestion: {
      type: 'modal',
      selector: '#audioCallModal',
      description: 'Conoce el contexto, las voces, los controles y las consultas jurídicas en vivo sin iniciar una llamada.'
    },
    prepare: prepareVoiceCallsTourSurface,
    buildSteps: buildVoiceCallsSteps
  },
  {
    id: 'document',
    title: 'Trabajar con contenido',
    description: 'Aprende a buscar dentro del texto, crear y consultar notas, analizar fragmentos y obtener una síntesis. También verás cómo usar el contenido en chats, llamadas y generadores, descargarlo o compartir su enlace.',
    icon: 'anticon-file-text',
    requiresDocument: true,
    suggestion: {
      type: 'document',
      description: 'Descubre cómo buscar dentro del texto, crear notas, analizar y descargar.'
    },
    prepare: prepareDocumentSurface,
    buildSteps: buildDocumentSteps
  },
  {
    id: 'virtual-assistant',
    title: 'Asistente Virtual',
    description: 'Aprende a obtener una síntesis completa de una sentencia, texto legal, archivo o transcripción. También verás cómo revisar y verificar el resultado, copiarlo, exportarlo, abrir un mapa mental y continuar con el mismo documento.',
    icon: 'anticon-robot',
    requiresDocument: true,
    suggestion: {
      type: 'modal',
      selector: '#documentSummaryModal',
      description: 'Conoce el alcance de la síntesis completa, sus controles y las formas de continuar.'
    },
    prepare: prepareVirtualAssistantSurface,
    buildSteps: buildVirtualAssistantSteps
  },
  {
    id: 'intelligent-analysis',
    title: 'Análisis Inteligente',
    description: 'Aprende a analizar un fragmento seleccionado del documento abierto sin confundirlo con el contenido completo. También verás cómo verificar el alcance y el resultado, exportarlo y pasar al Asistente Virtual.',
    icon: 'anticon-experiment',
    requiresDocument: true,
    suggestion: {
      type: 'modal',
      selector: '#intelligentSummaryModal',
      description: 'Distingue el fragmento analizado, revisa el resultado y conoce sus opciones de exportación.'
    },
    prepare: prepareIntelligentAnalysisSurface,
    buildSteps: buildIntelligentAnalysisSteps
  },
  {
    id: 'notes',
    title: 'Notas',
    description: 'Aprende a vincular comentarios con fragmentos concretos y encontrarlos después en la biblioteca de notas. También verás cómo buscar, volver al texto marcado, eliminar anotaciones y sincronizarlas entre dispositivos.',
    icon: 'anticon-book',
    requiresDocument: true,
    suggestion: {
      type: 'modal',
      selector: '#libraryModal',
      description: 'Conoce cómo crear, buscar y administrar anotaciones vinculadas a cada documento.'
    },
    prepare: prepareNotesSurface,
    buildSteps: buildNotesSteps
  },
  {
    id: 'files',
    title: 'Mis archivos',
    description: 'Aprende a subir documentos y archivos multimedia, elegir quién puede acceder a ellos y autorizar su uso por la IA. También verás cómo administrarlos y usar varios archivos en chats, llamadas, síntesis o generadores.',
    icon: 'anticon-folder',
    planRequired: true,
    suggestion: {
      type: 'modal',
      selector: '#userFilesSelectModal',
      description: 'Aprende a subir archivos, administrar tu biblioteca y trabajar con uno o varios a la vez.'
    },
    prepare: authState => prepareFeatureModalSurface(
      '#userFilesSelectModal',
      hasTourPlanAccess(authState, 'files')
    ),
    buildSteps: buildFilesSteps
  },
  {
    id: 'user-file-folders',
    title: 'Carpetas',
    description: 'Crea carpetas, organiza tus archivos y encuéntralos fácilmente.',
    icon: 'anticon-folder-open',
    planRequired: true,
    // Manual only: no suggestion trigger for the folders tour.
    prepare: prepareUserFileFoldersSurface,
    buildSteps: buildUserFileFoldersSteps
  },
  {
    id: 'video',
    title: 'Video Podcast',
    description: 'Aprende a crear un video narrado a partir de documentos o instrucciones y a elegir su enfoque, formato visual y plantilla. También verás cómo configurar voces, música y subtítulos, y administrar los videos generados.',
    icon: 'anticon-video-camera',
    planRequired: true,
    suggestion: {
      type: 'modal',
      selector: '#videoPodcastModal',
      description: 'Conoce el contexto, el formato visual, las voces, la música y los subtítulos.'
    },
    prepare: authState => prepareFeatureModalSurface('#videoPodcastModal', authState.paid),
    buildSteps: buildVideoSteps
  },
  {
    id: 'slides',
    title: 'Presentaciones',
    description: 'Aprende a crear una presentación de PowerPoint a partir de documentos o instrucciones, definir su enfoque y elegir una plantilla. También verás cómo descargar el resultado y administrar presentaciones anteriores.',
    icon: 'anticon-file-ppt',
    planRequired: true,
    suggestion: {
      type: 'modal',
      selector: '#slidesGeneratorModal',
      description: 'Aprende a crear un PowerPoint con contexto, instrucciones y una plantilla.'
    },
    prepare: authState => prepareFeatureModalSurface('#slidesGeneratorModal', authState.paid),
    buildSteps: buildSlidesSteps
  },
  {
    id: 'interactive',
    title: 'Páginas Interactivas',
    description: 'Aprende a crear una página web interactiva desde instrucciones o documentos, definiendo su contenido, organización y estilo. También verás cómo compartirla, solicitar cambios y consultar o restaurar versiones.',
    icon: 'anticon-layout',
    planRequired: true,
    syncRequired: true,
    suggestion: {
      type: 'modal',
      selector: '#interactivePagesModal',
      description: 'Conoce cómo crear y compartir una página desde instrucciones y documentos.'
    },
    prepare: authState => prepareSyncGatedFeatureModal(
      '#interactivePagesModal',
      '#interactivePagesSyncRequired',
      authState
    ),
    buildSteps: buildInteractivePagesSteps
  },
  {
    id: 'songs',
    title: 'Canciones con IA',
    description: 'Aprende a generar la letra y el audio de una canción a partir de una idea o documentos jurídicos. También verás cómo definir título, estilo, sentimiento, tempo y voz, y administrar tus canciones.',
    icon: 'anticon-audio',
    planRequired: true,
    syncRequired: true,
    suggestion: {
      type: 'modal',
      selector: '#songsModal',
      description: 'Aprende a generar letra y audio desde una idea y contexto jurídico.'
    },
    prepare: authState => prepareSyncGatedFeatureModal(
      '#songsModal',
      '#songsSyncRequired',
      authState
    ),
    buildSteps: buildSongsSteps
  },
  {
    id: 'alerts',
    title: 'Alertas Inteligentes',
    description: 'Aprende a vigilar nuevas sentencias y textos legales por consulta, carpeta y frecuencia, con avisos por correo o dispositivo. También verás cómo probar, pausar o reprogramar alertas y consultar su historial y resultados.',
    icon: 'anticon-bell',
    planRequired: true,
    syncRequired: true,
    suggestion: {
      type: 'modal',
      selector: '#alertsModal',
      description: 'Conoce cómo seguir novedades por consulta, carpeta y frecuencia.'
    },
    prepare: prepareAlertsTourSurface,
    buildSteps: buildAlertsSteps
  },
  {
    id: 'settings',
    title: 'Datos y personalización',
    description: 'Aprende a acceder a recorridos y guías, sincronizar tus datos y personalizar la letra, la apariencia, los mapas mentales y el Abogado Virtual. También verás cómo gestionar copias locales, chats y rendimiento.',
    icon: 'anticon-setting',
    suggestion: {
      type: 'settings',
      description: 'Descubre la sincronización, la apariencia y las instrucciones del Abogado Virtual.'
    },
    prepare: prepareSettingsSurface,
    buildSteps: buildSettingsSteps
  }
];

function getTourDefinition(tourId) {
  return TOUR_DEFINITIONS.find(item => item.id === tourId) || null;
}

function getHelpTourBinding(trigger) {
  if (!trigger?.querySelector?.('.anticon-question')) return null;
  return HELP_TOUR_SURFACES.find(binding => trigger.closest(binding.selector)) || null;
}

function hideHelpPopover(trigger) {
  try {
    const instance = window.bootstrap?.Popover?.getInstance?.(trigger);
    if (instance?.hide) instance.hide();
  } catch (error) { }
  try {
    if (window.jQuery && typeof window.jQuery(trigger).popover === 'function') {
      window.jQuery(trigger).popover('hide');
    }
  } catch (error) { }
}

async function startTourFromHelp(trigger, binding) {
  const definition = getTourDefinition(binding?.tourId);
  if (!definition) return;
  if (binding.missingDocumentMessage && definition.requiresDocument && !pageHasDocument()) {
    const popoverId = trigger?.getAttribute?.('aria-describedby');
    const popover = popoverId ? document.getElementById(popoverId) : null;
    const action = popover?.querySelector?.('.lexius-help-tour-action');
    if (action) {
      let feedback = action.querySelector('[data-help-tour-feedback]');
      if (!feedback) {
        feedback = document.createElement('p');
        feedback.className = 'lexius-help-tour-action__feedback';
        feedback.setAttribute('data-help-tour-feedback', '');
        feedback.setAttribute('role', 'status');
        feedback.setAttribute('aria-live', 'polite');
        action.appendChild(feedback);
      }
      feedback.textContent = binding.missingDocumentMessage;
    }
    announce(binding.missingDocumentMessage);
    return;
  }
  hideHelpPopover(trigger);
  if (binding.closeSurface) {
    const sourceSurface = trigger.closest('.modal');
    if (sourceSurface) await hideModal(sourceSurface);
  }
  await startTour(definition.id, { fromHelp: true });
}

function updateHelpTourButtonAvailability(button, authState = getAuthState()) {
  if (!button) return;
  const definition = getTourDefinition(button.getAttribute('data-help-tour-start'));
  if (!definition) return;
  const enabled = toursAreEnabled(authState);
  button.disabled = !enabled;
  button.setAttribute(
    'aria-label',
    enabled
      ? `Iniciar recorrido guiado: ${definition.title}`
      : `Recorridos desactivados: ${definition.title}`
  );
  const label = button.querySelector('span');
  if (label) label.textContent = enabled ? 'Iniciar recorrido' : 'Recorridos desactivados';
}

function updateHelpPopoverPosition(trigger) {
  let instance = null;
  try {
    instance = window.bootstrap?.Popover?.getInstance?.(trigger) || null;
  } catch (error) { }
  if (!instance) {
    try {
      instance = window.jQuery?.(trigger)?.data?.('bs.popover') || null;
    } catch (error) { }
  }
  try {
    if (typeof instance?.update === 'function') instance.update();
    else if (typeof instance?._popper?.update === 'function') instance._popper.update();
  } catch (error) { }
}

function refreshHelpTourAvailability(authState = getAuthState()) {
  document.querySelectorAll('[data-help-tour-start]').forEach(button => {
    updateHelpTourButtonAvailability(button, authState);
  });
}

function enhanceHelpPopover(trigger) {
  const binding = getHelpTourBinding(trigger);
  const definition = binding && getTourDefinition(binding.tourId);
  const popoverId = trigger?.getAttribute?.('aria-describedby');
  const popover = popoverId ? document.getElementById(popoverId) : null;
  if (!definition || !popover?.classList.contains('popover')) return false;
  if (popover.querySelector('[data-help-tour-start]')) return true;
  popover.classList.add(`lexius-help-popover--${definition.id}`);
  popover.setAttribute('role', 'dialog');
  popover.setAttribute('aria-label', `Ayuda: ${definition.title}`);

  const action = document.createElement('div');
  action.className = 'lexius-help-tour-action';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'lexius-help-tour-action__button';
  button.setAttribute('data-help-tour-start', definition.id);
  button.setAttribute('aria-label', `Iniciar recorrido guiado: ${definition.title}`);
  button.innerHTML = '<i class="anticon anticon-play-circle" aria-hidden="true"></i><span>Iniciar recorrido</span>';
  button.addEventListener('pointerdown', event => event.preventDefault());
  button.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    void startTourFromHelp(trigger, binding);
  });
  if (definition.id === 'document-edition') {
    button.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      hideHelpPopover(trigger);
      trigger.focus({ preventScroll: true });
    });
  }
  updateHelpTourButtonAvailability(button);
  action.appendChild(button);
  popover.appendChild(action);
  window.requestAnimationFrame(() => updateHelpPopoverPosition(trigger));
  window.setTimeout(() => updateHelpPopoverPosition(trigger), 80);
  return true;
}

function scheduleHelpPopoverEnhancement(trigger) {
  if (!getHelpTourBinding(trigger)) return;
  [0, 60, 220].forEach(delay => {
    window.setTimeout(() => enhanceHelpPopover(trigger), delay);
  });
}

function getTourDefinitionForModal(modal) {
  if (!modal?.id) return null;
  return TOUR_DEFINITIONS.find(tour => {
    return tour.suggestion?.type === 'modal' && tour.suggestion.selector === `#${modal.id}`;
  }) || null;
}

function cancelPendingSuggestion() {
  if (!pendingSuggestion) return;
  window.clearTimeout(pendingSuggestion.timer);
  pendingSuggestion = null;
}

function dismissFeatureSuggestion({ restoreFocus = true } = {}) {
  cancelPendingSuggestion();
  const suggestion = activeSuggestion;
  const element = document.getElementById('lexiusTourSuggestion');
  if (element) {
    element.classList.remove('is-visible');
    element.hidden = true;
    element.removeAttribute('data-tour-id');
    if (element.parentElement !== document.body) document.body.appendChild(element);
  }
  activeSuggestion = null;
  refreshTourUiState();
  if (restoreFocus && isMeaningfulFocus(suggestion?.previousFocus)) {
    try { suggestion.previousFocus.focus({ preventScroll: true }); } catch (error) { }
  }
  scheduleDocumentEditionSuggestionCheck();
  scheduleDocumentReviewSuggestionCheck();
  scheduleChatResponseSuggestionCheck();
}

function suggestionSourceIsReady(definition) {
  const suggestion = definition?.suggestion;
  if (!suggestion) return false;
  if (suggestion.type === 'modal') {
    const modal = document.querySelector(suggestion.selector);
    return !!(modal?.classList.contains('show') && isVisible(modal));
  }
  if (suggestion.type === 'document') return pageHasDocument() && !!findVisible('#main-container');
  if (suggestion.type === 'search') return !!findVisible('#search-form');
  if (suggestion.type === 'search-results') return pageHasSearchResults();
  if (suggestion.type === 'settings') return !!findVisible('.custom-theme-bar');
  if (suggestion.type === 'document-edition') return pageHasDocumentEditionSuggestionWorkspace();
  if (suggestion.type === 'document-review') return pageHasDocumentReviewWorkspace();
  if (suggestion.type === 'chat-response') {
    const modal = document.querySelector(suggestion.selector);
    return !!(
      modal?.classList.contains('show')
      && isVisible(modal)
      && latestCompletedChatResponse({ visibleOnly: true })
    );
  }
  return false;
}

function hasSuggestionConflict(definition, options = {}) {
  if (
    document.hidden
    || (!options.allowRecordingSuggestion && shouldSuppressAutomaticTour())
    || activeRun
    || tourStartPending
    || hasOpenTourModal()
    || autoPromptPending
  ) return true;

  const suggestion = definition.suggestion;
  const sourceElement = suggestion.type === 'document-edition'
    ? latestDocumentEditionWorkspace({ visibleOnly: true, latestResponseOnly: true })
    : document.querySelector(suggestion.selector);
  const sourceModal = ['modal', 'chat-response'].includes(suggestion.type)
    ? sourceElement
    : ['document-edition', 'document-review'].includes(suggestion.type)
      ? sourceElement?.closest('.modal.show')
      : null;
  const competingModal = Array.from(document.querySelectorAll('.modal.show')).some(modal => {
    return modal !== sourceModal && !TOUR_MODAL_IDS.has(modal.id);
  });
  const settingsCompete = suggestion.type !== 'settings' && isVisible(document.querySelector('.custom-theme-bar'));
  const contextMenuOpen = isVisible(document.getElementById('context-menu'));
  const countryEntryOpen = !!document.querySelector('.lexius-country-entry.is-visible:not([hidden])');
  const countryConfirmationOpen = !!document.querySelector('.lexius-country-confirmation.is-visible:not([hidden])');
  const unifiedCountryRequired = document.documentElement.classList.contains('lexius-unified-country-required');
  return competingModal
    || settingsCompete
    || contextMenuOpen
    || countryEntryOpen
    || countryConfirmationOpen
    || unifiedCountryRequired;
}

async function showFeatureSuggestion(definition, authState, options = {}) {
  const element = document.getElementById('lexiusTourSuggestion');
  if (!element || activeSuggestion) return false;
  const recordingSuggestion = options.recording === true
    && isDocumentReviewRecordingSuggestion(definition.id);
  if (
    !authState.authenticated
    || (!recordingSuggestion && !toursAreEnabled(authState))
    || (definition.requiresDocument && !pageHasDocument())
    || !suggestionSourceIsReady(definition)
    || hasSuggestionConflict(definition, { allowRecordingSuggestion: recordingSuggestion })
    || (!recordingSuggestion && hasTourBeenSuggestedOrCompleted(authState, definition.id))
  ) return false;

  const claimed = recordingSuggestion
    ? true
    : await claimTourSuggestion(authState, definition.id);
  if (
    !claimed
    || getAuthState().subject !== authState.subject
    || (!recordingSuggestion && !toursAreEnabled(authState))
    || (!recordingSuggestion && getTourProgress(authState, definition.id).completed)
    || activeSuggestion
    || activeRun
  ) return false;
  if (
    !suggestionSourceIsReady(definition)
    || hasSuggestionConflict(definition, { allowRecordingSuggestion: recordingSuggestion })
  ) return false;

  const needsPlan = definition.planRequired && !hasTourPlanAccess(authState, definition.id);
  const title = element.querySelector('[data-tour-suggestion-title]');
  const description = element.querySelector('[data-tour-suggestion-description]');
  const eyebrow = element.querySelector('[data-tour-suggestion-eyebrow]');
  const startButton = element.querySelector('[data-tour-suggestion-start]');
  if (!title || !description || !eyebrow || !startButton) return false;

  eyebrow.textContent = needsPlan ? 'Recorrido opcional · Función con plan' : 'Recorrido opcional';
  title.textContent = `${definition.title}, paso a paso`;
  description.textContent = `${definition.suggestion.description} No inicia ninguna acción y puedes repetirlo desde Ajustes.`;
  startButton.textContent = needsPlan ? 'Conocer función' : 'Ver recorrido';
  startButton.setAttribute(
    'aria-label',
    `${needsPlan ? 'Conocer función' : 'Ver recorrido'}: ${definition.title}`
  );
  element.setAttribute('data-tour-id', definition.id);
  const sourceElement = definition.suggestion.type === 'document-edition'
    ? latestDocumentEditionWorkspace({ visibleOnly: true, latestResponseOnly: true })
    : document.querySelector(definition.suggestion.selector);
  const sourceModal = ['modal', 'chat-response'].includes(definition.suggestion.type)
    ? sourceElement
    : ['document-edition', 'document-review'].includes(definition.suggestion.type)
      ? sourceElement?.closest('.modal.show')
      : null;
  if (sourceModal && !sourceModal.contains(element)) sourceModal.appendChild(element);
  element.hidden = false;
  activeSuggestion = {
    tourId: definition.id,
    authState,
    sourceSelector: definition.suggestion.selector || '',
    previousFocus: isMeaningfulFocus(document.activeElement) ? document.activeElement : null,
    recording: recordingSuggestion
  };
  refreshTourUiState();
  window.requestAnimationFrame(() => element.classList.add('is-visible'));
  return true;
}

async function showRecordingFeatureSuggestion(tourId) {
  if (!isDocumentReviewRecordingSuggestion(tourId)) return false;
  if (activeSuggestion?.tourId === tourId && activeSuggestion.recording === true) return true;
  if (activeSuggestion || activeRun || tourStartPending) return false;
  cancelPendingSuggestion();
  const definition = getTourDefinition(tourId);
  if (!definition?.suggestion || !suggestionSourceIsReady(definition)) return false;
  const authState = await waitForAuthState();
  return showFeatureSuggestion(definition, authState, { recording: true });
}

function queueFeatureSuggestion(tourId) {
  const definition = getTourDefinition(tourId);
  if (!definition?.suggestion || shouldSuppressAutomaticTour() || activeSuggestion || activeRun) return;
  if (definition.requiresDocument && !pageHasDocument()) return;
  const immediateState = getAuthState();
  if (
    immediateState.status !== 'loading'
    && (
      !immediateState.authenticated
      || !toursAreEnabled(immediateState)
      || hasTourBeenSuggestedOrCompleted(immediateState, tourId)
    )
  ) return;
  if (pendingSuggestion?.tourId === tourId) return;

  cancelPendingSuggestion();
  const request = { tourId, timer: null };
  request.timer = window.setTimeout(async () => {
    if (pendingSuggestion !== request) return;
    pendingSuggestion = null;
    const authState = await waitForAuthState();
    const stateLoaded = await ensureTourStateLoaded(authState);
    if (!stateLoaded || !toursAreEnabled(authState)) return;
    await showFeatureSuggestion(definition, authState);
  }, SUGGESTION_DELAY_MS);
  pendingSuggestion = request;
}

function scheduleDocumentSuggestionCheck() {
  if (documentContextFrame) window.cancelAnimationFrame(documentContextFrame);
  documentContextFrame = window.requestAnimationFrame(() => {
    documentContextFrame = null;
    if (pageHasDocument()) queueFeatureSuggestion('document');
    else if (activeSuggestion?.tourId === 'document') dismissFeatureSuggestion({ restoreFocus: false });
  });
}

function scheduleChatResponseSuggestionCheck() {
  if (chatResponseFrame) window.cancelAnimationFrame(chatResponseFrame);
  chatResponseFrame = window.requestAnimationFrame(() => {
    chatResponseFrame = null;
    const editionHasPriority = pageHasDocumentEditionSuggestionWorkspace()
      && (
        activeSuggestion?.tourId === 'document-edition'
        || pendingSuggestion?.tourId === 'document-edition'
        || tourStillNeedsSuggestion('document-edition')
      );
    if (editionHasPriority || pageHasDocumentReviewWorkspace()) {
      if (activeSuggestion?.tourId === 'chat-response') {
        dismissFeatureSuggestion({ restoreFocus: false });
      }
      if (pendingSuggestion?.tourId === 'chat-response') cancelPendingSuggestion();
      return;
    }
    const visibleResponse = latestCompletedChatResponse({ visibleOnly: true });
    if (visibleResponse) queueFeatureSuggestion('chat-response');
    else if (activeSuggestion?.tourId === 'chat-response') {
      dismissFeatureSuggestion({ restoreFocus: false });
    }
  });
}

function scheduleDocumentEditionSuggestionCheck() {
  if (documentEditionFrame) window.cancelAnimationFrame(documentEditionFrame);
  documentEditionFrame = window.requestAnimationFrame(() => {
    documentEditionFrame = null;
    if (pageHasDocumentReviewWorkspace()) {
      if (activeSuggestion?.tourId === 'document-edition') {
        dismissFeatureSuggestion({ restoreFocus: false });
      }
      if (pendingSuggestion?.tourId === 'document-edition') cancelPendingSuggestion();
      return;
    }
    const editionCanTakePriority = activeSuggestion?.tourId === 'document-edition'
      || pendingSuggestion?.tourId === 'document-edition'
      || tourStillNeedsSuggestion('document-edition');
    if (pageHasDocumentEditionSuggestionWorkspace() && editionCanTakePriority) {
      if (activeSuggestion && activeSuggestion.tourId !== 'document-edition') {
        dismissFeatureSuggestion({ restoreFocus: false });
      }
      if (pendingSuggestion && pendingSuggestion.tourId !== 'document-edition') {
        cancelPendingSuggestion();
      }
      queueFeatureSuggestion('document-edition');
      return;
    }
    if (activeSuggestion?.tourId === 'document-edition') {
      dismissFeatureSuggestion({ restoreFocus: false });
    }
    if (pendingSuggestion?.tourId === 'document-edition') cancelPendingSuggestion();
  });
}

function scheduleDocumentReviewSuggestionCheck() {
  if (documentReviewFrame) window.cancelAnimationFrame(documentReviewFrame);
  documentReviewFrame = window.requestAnimationFrame(() => {
    documentReviewFrame = null;
    if (pageHasDocumentReviewWorkspace()) {
      if (activeSuggestion && activeSuggestion.tourId !== 'document-review') {
        dismissFeatureSuggestion({ restoreFocus: false });
      }
      if (pendingSuggestion && pendingSuggestion.tourId !== 'document-review') {
        cancelPendingSuggestion();
      }
      queueFeatureSuggestion('document-review');
      return;
    }
    if (activeSuggestion?.tourId === 'document-review') {
      dismissFeatureSuggestion({ restoreFocus: false });
    }
    if (pendingSuggestion?.tourId === 'document-review') cancelPendingSuggestion();
  });
}

function scheduleSearchResultsSuggestionCheck() {
  if (searchResultsContextFrame) window.cancelAnimationFrame(searchResultsContextFrame);
  searchResultsContextFrame = window.requestAnimationFrame(() => {
    searchResultsContextFrame = null;
    if (pageHasSearchResults()) {
      if (pendingSuggestion?.tourId === 'search') cancelPendingSuggestion();
      if (activeSuggestion?.tourId === 'search') {
        dismissFeatureSuggestion({ restoreFocus: false });
      }
      queueFeatureSuggestion('search-results');
      return;
    }
    if (activeSuggestion?.tourId === 'search-results') {
      dismissFeatureSuggestion({ restoreFocus: false });
    }
    if (pendingSuggestion?.tourId === 'search-results') cancelPendingSuggestion();
  });
}

function bindFeatureSuggestionTriggers() {
  document.addEventListener('shown.bs.modal', event => {
    const definition = getTourDefinitionForModal(event.target);
    if (definition) {
      if (activeSuggestion && activeSuggestion.tourId !== definition.id) {
        dismissFeatureSuggestion({ restoreFocus: false });
      }
      queueFeatureSuggestion(definition.id);
      scheduleDocumentEditionSuggestionCheck();
      scheduleDocumentReviewSuggestionCheck();
      scheduleChatResponseSuggestionCheck();
      return;
    }
    if (activeSuggestion && event.target?.id !== activeSuggestion.sourceSelector.replace(/^#/, '')) {
      dismissFeatureSuggestion({ restoreFocus: false });
    }
  });

  document.addEventListener('hidden.bs.modal', event => {
    const selector = event.target?.id ? `#${event.target.id}` : '';
    const disableConfirmation = document.getElementById('lexiusTourDisableConfirmation');
    if (activeTourDisableConfirmation && event.target?.contains?.(disableConfirmation)) {
      closeTourDisableConfirmation(false, { restoreFocus: false });
    }
    if (activeSuggestion?.sourceSelector === selector) dismissFeatureSuggestion({ restoreFocus: false });
    if (pendingSuggestion) {
      const definition = getTourDefinition(pendingSuggestion.tourId);
      if (definition?.suggestion?.selector === selector) cancelPendingSuggestion();
    }
    scheduleDocumentSuggestionCheck();
    scheduleDocumentEditionSuggestionCheck();
    scheduleDocumentReviewSuggestionCheck();
    scheduleChatResponseSuggestionCheck();
  });

  document.addEventListener('focusin', event => {
    if (event.target?.matches?.('#searchbx')) queueFeatureSuggestion('search');
  });

  document.addEventListener('click', event => {
    if (event.target.closest('[data-tour="mobile-navigation"], [data-tour="search-history"]')) {
      queueFeatureSuggestion('search');
    }
    if (event.target.closest('[data-tour="settings-trigger"]')) {
      window.setTimeout(() => queueFeatureSuggestion('settings'), 180);
    }
  });

  const pageContent = document.getElementById('page-content');
  if (pageContent && window.MutationObserver) {
    const observer = new MutationObserver(() => {
      scheduleDocumentSuggestionCheck();
      scheduleSearchResultsSuggestionCheck();
    });
    observer.observe(pageContent, { childList: true, subtree: true });
  }
  const settingsPanel = document.querySelector('.custom-theme-bar');
  if (settingsPanel && window.MutationObserver) {
    const observer = new MutationObserver(() => {
      if (isVisible(settingsPanel)) {
        queueFeatureSuggestion('settings');
        return;
      }
      if (activeSuggestion?.tourId === 'settings') dismissFeatureSuggestion({ restoreFocus: false });
      if (pendingSuggestion?.tourId === 'settings') cancelPendingSuggestion();
    });
    observer.observe(settingsPanel, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
  }
  const chatModal = document.getElementById('chatModal');
  if (chatModal && window.MutationObserver) {
    const observer = new MutationObserver(() => {
      scheduleDocumentEditionSuggestionCheck();
      scheduleDocumentReviewSuggestionCheck();
      scheduleChatResponseSuggestionCheck();
    });
    observer.observe(chatModal, {
      attributes: true,
      attributeFilter: ['data-tour'],
      childList: true,
      subtree: true
    });
  }
  window.addEventListener('popstate', scheduleDocumentSuggestionCheck);
  window.addEventListener('popstate', scheduleSearchResultsSuggestionCheck);
  window.addEventListener('lexius-document-review-opened', scheduleDocumentReviewSuggestionCheck);
  window.addEventListener('lexius-auth-ready', () => {
    const authState = getAuthState();
    const nextSubject = authState.authenticated ? authState.subject : '';
    const subjectChanged = nextSubject !== tourStateSubject;

    if (subjectChanged) {
      if (activeTourDisableConfirmation) {
        closeTourDisableConfirmation(false, { restoreFocus: false });
      }
      if (tourStateRefreshTimer) {
        window.clearTimeout(tourStateRefreshTimer);
        tourStateRefreshTimer = null;
      }
      if (autoPromptTimer) {
        window.clearInterval(autoPromptTimer);
        autoPromptTimer = null;
      }
      cancelPendingSuggestion();
      if (activeSuggestion) dismissFeatureSuggestion({ restoreFocus: false });
      if (activeRun) void finishActiveTour(false);
      autoPromptHandled = false;
      autoPromptPending = false;
      manualTourRequest = false;
      lastUserInteractionAt = Date.now();
      resetTourState(nextSubject);

      const welcome = document.getElementById('lexiusTourWelcomeModal');
      if (welcome?.classList.contains('show')) void hideModal(welcome);
      const center = document.getElementById('lexiusTourCenterModal');
      if (center?.classList.contains('show')) void hideModal(center);
    }

    if (!authState.authenticated) {
      if (window.LexiusTours) window.LexiusTours.ready = Promise.resolve(true);
      return;
    }

    const stateReady = ensureTourStateLoaded(authState, { force: subjectChanged });
    if (window.LexiusTours) window.LexiusTours.ready = stateReady;
    void stateReady.then(loaded => {
      if (!loaded || getAuthState().subject !== authState.subject) return;
      scheduleDocumentSuggestionCheck();
      scheduleChatResponseSuggestionCheck();
      scheduleSearchResultsSuggestionCheck();
      if (subjectChanged) scheduleAutomaticInvitation(authState);
    });
  });
  scheduleDocumentSuggestionCheck();
  scheduleChatResponseSuggestionCheck();
  scheduleSearchResultsSuggestionCheck();
}

function renderTourPreference(authState) {
  const section = document.querySelector('[data-tour-preference]');
  if (!section) return;
  const enabled = toursAreEnabled(authState);
  const status = section.querySelector('[data-tour-preference-status]');
  const description = section.querySelector('[data-tour-preference-description]');
  const button = section.querySelector('[data-tour-preference-toggle]');

  section.setAttribute('data-enabled', String(enabled));
  if (status) status.textContent = enabled ? 'Activados' : 'Desactivados';
  if (description) {
    description.textContent = enabled
      ? 'Los recorridos y sus sugerencias opcionales están disponibles en todos tus dispositivos.'
      : 'No se mostrarán recorridos ni sugerencias en ninguno de tus dispositivos. Tu progreso se conserva.';
  }
  if (!button) return;

  button.disabled = tourPreferencePending || tourResetPending;
  button.setAttribute('aria-pressed', String(enabled));
  button.classList.toggle('btn-outline-secondary', enabled);
  button.classList.toggle('btn-primary', !enabled);
  button.textContent = tourPreferencePending
    ? 'Guardando…'
    : (enabled ? 'Desactivar recorridos' : 'Activar recorridos');
  if (tourPreferencePending) button.setAttribute('aria-busy', 'true');
  else button.removeAttribute('aria-busy');
}

function setTourPreferencePending(pending, authState = getAuthState()) {
  tourPreferencePending = pending === true;
  renderTourPreference(authState);
  const resetButton = document.querySelector('[data-tour-reset]');
  if (resetButton) resetButton.disabled = tourPreferencePending || tourResetPending;
  const suggestionButton = document.querySelector('[data-tour-suggestion-disable]');
  if (suggestionButton) {
    suggestionButton.disabled = tourPreferencePending || tourResetPending;
    suggestionButton.textContent = tourPreferencePending ? 'Guardando…' : 'Desactivar recorridos';
    if (tourPreferencePending) suggestionButton.setAttribute('aria-busy', 'true');
    else suggestionButton.removeAttribute('aria-busy');
  }
}

function getTourDisableConfirmationFocusables() {
  const root = document.getElementById('lexiusTourDisableConfirmation');
  if (!root) return [];
  return Array.from(root.querySelectorAll('button:not([disabled])')).filter(isVisible);
}

function handleTourDisableConfirmationKeydown(event) {
  if (!activeTourDisableConfirmation) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeTourDisableConfirmation(false);
    return;
  }
  if (event.key !== 'Tab') return;

  const focusable = getTourDisableConfirmationFocusables();
  if (!focusable.length) {
    event.preventDefault();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function handleTourDisableConfirmationFocusin(event) {
  if (!activeTourDisableConfirmation) return;
  const root = document.getElementById('lexiusTourDisableConfirmation');
  if (!root || root.contains(event.target)) return;
  const focusTarget = getTourDisableConfirmationFocusables()[0]
    || root.querySelector('.lexius-tour-disable-confirmation__dialog');
  try { focusTarget?.focus({ preventScroll: true }); } catch (error) { }
}

function getTourDisableReturnFocusElement(trigger, confirmed) {
  if (!confirmed || !trigger?.closest?.('#lexiusTourSuggestion')) return trigger;
  if (isMeaningfulFocus(activeSuggestion?.previousFocus)) return activeSuggestion.previousFocus;

  const source = activeSuggestion?.sourceSelector
    ? document.querySelector(activeSuggestion.sourceSelector)
    : null;
  if (!source || !isVisible(source)) return null;
  if (source.matches('button, a[href], input, select, textarea, [tabindex]')) return source;
  return Array.from(source.querySelectorAll('button, a[href], input, select, textarea, [tabindex]'))
    .find(element => !element.disabled && isVisible(element)) || null;
}

function closeTourDisableConfirmation(confirmed = false, { restoreFocus = true } = {}) {
  const confirmation = activeTourDisableConfirmation;
  if (!confirmation) return;
  activeTourDisableConfirmation = null;
  document.removeEventListener('keydown', handleTourDisableConfirmationKeydown, true);
  document.removeEventListener('focusin', handleTourDisableConfirmationFocusin, true);

  const root = document.getElementById('lexiusTourDisableConfirmation');
  if (root) root.classList.remove('is-visible');
  document.body.classList.remove('lexius-tour-disable-confirmation-open');

  let restoredFocus = false;
  const focusTarget = restoreFocus
    ? getTourDisableReturnFocusElement(confirmation.returnFocusElement, confirmed === true)
    : null;
  if (
    focusTarget
    && focusTarget.isConnected
    && isVisible(focusTarget)
    && typeof focusTarget.focus === 'function'
  ) {
    try {
      focusTarget.focus({ preventScroll: true });
      restoredFocus = document.activeElement === focusTarget;
    } catch (error) { }
  }
  if (root) root.setAttribute('aria-hidden', 'true');
  if (confirmation.hostModal) {
    if (confirmation.hostModalZIndex) {
      confirmation.hostModal.style.setProperty(
        'z-index',
        confirmation.hostModalZIndex,
        confirmation.hostModalZIndexPriority
      );
    } else {
      confirmation.hostModal.style.removeProperty('z-index');
    }
  }

  if (tourDisableConfirmationHideTimer) window.clearTimeout(tourDisableConfirmationHideTimer);
  tourDisableConfirmationHideTimer = window.setTimeout(() => {
    tourDisableConfirmationHideTimer = null;
    if (!root || activeTourDisableConfirmation) return;
    root.hidden = true;
    if (root.parentElement !== document.body) document.body.appendChild(root);
  }, prefersReducedMotion() ? 0 : 180);

  confirmation.resolve(confirmed === true);
  refreshTourUiState();
  if (!restoredFocus) {
    window.setTimeout(() => {
      if (!activeTourDisableConfirmation) focusTourFallback();
    }, 0);
  }
}

function confirmToursDisable() {
  if (activeTourDisableConfirmation) return activeTourDisableConfirmation.promise;
  const root = document.getElementById('lexiusTourDisableConfirmation');
  if (!root) return Promise.resolve(false);

  if (tourDisableConfirmationHideTimer) {
    window.clearTimeout(tourDisableConfirmationHideTimer);
    tourDisableConfirmationHideTimer = null;
  }
  const activeElement = document.activeElement;
  const returnFocusElement = activeElement && activeElement !== document.body ? activeElement : null;
  const containingModal = returnFocusElement?.closest?.('.modal.show');
  const hostModalZIndex = containingModal?.style.getPropertyValue('z-index') || '';
  const hostModalZIndexPriority = containingModal?.style.getPropertyPriority('z-index') || '';
  if (containingModal) containingModal.style.setProperty('z-index', '2147483290', 'important');
  (containingModal || document.body).appendChild(root);

  let resolveConfirmation;
  const promise = new Promise(resolve => { resolveConfirmation = resolve; });
  activeTourDisableConfirmation = {
    promise,
    resolve: resolveConfirmation,
    returnFocusElement,
    hostModal: containingModal || null,
    hostModalZIndex,
    hostModalZIndexPriority
  };

  root.hidden = false;
  root.setAttribute('aria-hidden', 'false');
  document.body.classList.add('lexius-tour-disable-confirmation-open');
  document.addEventListener('keydown', handleTourDisableConfirmationKeydown, true);
  document.addEventListener('focusin', handleTourDisableConfirmationFocusin, true);
  setTourUiActive(true);
  window.requestAnimationFrame(() => {
    if (!activeTourDisableConfirmation || activeTourDisableConfirmation.promise !== promise) return;
    root.classList.add('is-visible');
    const cancelButton = root.querySelector('.lexius-tour-disable-confirmation__actions [data-tour-disable-cancel]');
    const dialog = root.querySelector('.lexius-tour-disable-confirmation__dialog');
    try {
      (cancelButton || dialog)?.focus({ preventScroll: true });
    } catch (error) { }
  });
  return promise;
}

async function updateToursEnabled(nextEnabled) {
  if (tourPreferencePending || tourResetPending || typeof nextEnabled !== 'boolean') return false;
  const authState = await waitForAuthState();
  if (!authState.authenticated || !authState.subject) return false;
  const stateLoaded = await ensureTourStateLoaded(authState);
  if (!stateLoaded || getAuthState().subject !== authState.subject) return false;

  const currentEnabled = toursAreEnabled(authState);
  if (currentEnabled === nextEnabled) return true;
  if (!nextEnabled) {
    const confirmed = await confirmToursDisable();
    if (!confirmed) return false;
    if (tourPreferencePending || tourResetPending || getAuthState().subject !== authState.subject) return false;
    const latestStateLoaded = await ensureTourStateLoaded(authState);
    if (!latestStateLoaded || getAuthState().subject !== authState.subject) return false;
    if (tourPreferencePending || tourResetPending) return false;
    if (!toursAreEnabled(authState)) return true;
  }

  const feedback = document.querySelector('[data-tour-preference-feedback]');
  if (feedback) feedback.textContent = '';
  setTourPreferencePending(true, authState);
  try {
    const response = await fetch(TOUR_STATE_ENDPOINT, {
      method: 'PATCH',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ tours_enabled: nextEnabled })
    });
    if (!response.ok) throw new Error(`tour_settings_${response.status}`);
    const result = await response.json();
    if (getAuthState().subject !== authState.subject) return false;
    if (!applyTourState(authState, result.state, { replace: true })) return false;

    const savedEnabled = toursAreEnabled(authState);
    if (feedback) {
      feedback.textContent = savedEnabled
        ? 'Los recorridos y sus sugerencias están activados en tu cuenta.'
        : 'Los recorridos y sus sugerencias están desactivados en tu cuenta.';
    }
    announce(savedEnabled ? 'Recorridos activados.' : 'Recorridos desactivados.');
    return savedEnabled === nextEnabled;
  } catch (error) {
    console.error('No se pudo guardar la configuración de los recorridos:', error);
    if (feedback) feedback.textContent = 'No se pudo guardar el cambio. Inténtalo de nuevo.';
    announce('No se pudo guardar la configuración de los recorridos.');
    return false;
  } finally {
    setTourPreferencePending(false, authState);
  }
}

function renderTourCenter(authState) {
  const grid = document.querySelector('[data-tour-grid]');
  if (!grid) return;
  const toursEnabled = toursAreEnabled(authState);
  renderTourPreference(authState);
  grid.classList.toggle('is-disabled', !toursEnabled);
  grid.setAttribute('aria-disabled', String(!toursEnabled));
  grid.innerHTML = TOUR_DEFINITIONS.map(tour => {
    const progress = getTourProgress(authState, tour.id);
    const unavailableContext = tour.requiresDocument && !pageHasDocument();
    const unavailableChatResponse = tour.requiresChatResponse && !pageHasChatResponse();
    const unavailableDocumentEdition = tour.requiresDocumentEdition && !pageHasDocumentEditionWorkspace();
    const unavailableDocumentReview = tour.requiresDocumentReview && !pageHasDocumentReviewWorkspace();
    const unavailableSearchResults = tour.requiresSearchResults && !pageHasSearchResults();
    const sharedContextUnavailable = unavailableContext && window.shared_chat_document === true;
    const needsPlan = tour.planRequired && !hasTourPlanAccess(authState, tour.id);
    const needsSync = tour.syncRequired && window.sync_enabled !== true;
    const badges = [];
    if (progress.completed) badges.push('<span class="lexius-tour-badge lexius-tour-badge--completed">Completado</span>');
    if (needsPlan) badges.push('<span class="lexius-tour-badge lexius-tour-badge--plan">Disponible con un plan</span>');
    if (needsSync) badges.push('<span class="lexius-tour-badge">Requiere sincronización</span>');
    if (unavailableContext) {
      badges.push(`<span class="lexius-tour-badge">${sharedContextUnavailable ? 'Documento compartido: solo lectura' : 'Abre un documento'}</span>`);
    }
    if (unavailableChatResponse) {
      badges.push('<span class="lexius-tour-badge">Abre un chat con respuesta</span>');
    }
    if (unavailableDocumentEdition) {
      badges.push('<span class="lexius-tour-badge">Abre un documento con versiones</span>');
    }
    if (unavailableDocumentReview) {
      badges.push('<span class="lexius-tour-badge">Abre una revisión</span>');
    }
    if (unavailableSearchResults) {
      badges.push('<span class="lexius-tour-badge">Realiza una búsqueda</span>');
    }
    const startLabel = needsPlan ? 'Conocer función' : (progress.completed ? 'Repetir recorrido' : 'Iniciar recorrido');
    const unavailableLabel = unavailableChatResponse
      ? 'Abre un chat con respuesta'
      : unavailableDocumentEdition
        ? 'Abre un documento con versiones'
        : unavailableDocumentReview
          ? 'Abre una revisión'
          : unavailableSearchResults
            ? 'Realiza una búsqueda'
            : (sharedContextUnavailable ? 'No disponible en este documento' : 'Abre un documento');
    const startButton = !toursEnabled
      ? `<button type="button" class="btn btn-outline-secondary" disabled aria-label="Recorridos desactivados: ${escapeHtml(tour.title)}">Recorridos desactivados</button>`
      : unavailableContext || unavailableChatResponse || unavailableDocumentEdition
        || unavailableDocumentReview || unavailableSearchResults
        ? `<button type="button" class="btn btn-outline-secondary" disabled aria-label="${escapeHtml(unavailableLabel)}: ${escapeHtml(tour.title)}">${escapeHtml(unavailableLabel)}</button>`
        : `<button type="button" class="btn btn-primary" data-start-tour="${escapeHtml(tour.id)}" aria-label="${escapeHtml(startLabel)}: ${escapeHtml(tour.title)}">${startLabel}</button>`;
    const planButton = needsPlan && toursEnabled
      ? `<button type="button" class="btn btn-outline-primary" data-tour-plan aria-label="Ver planes para: ${escapeHtml(tour.title)}">Ver planes</button>`
      : '';
    return `
      <article class="lexius-tour-card" data-tour-card="${escapeHtml(tour.id)}">
        <div class="lexius-tour-card__top">
          <span class="lexius-tour-card__icon" aria-hidden="true"><i class="anticon ${escapeHtml(tour.icon)}"></i></span>
          <div>
            <h3>${escapeHtml(tour.title)}</h3>
            <div class="lexius-tour-card__badges">${badges.join('')}</div>
          </div>
        </div>
        <p>${escapeHtml(tour.description)}</p>
        <div class="lexius-tour-card__actions">${startButton}${planButton}</div>
      </article>
    `;
  }).join('');
}

function setTourResetPending(pending) {
  tourResetPending = pending === true;
  const button = document.querySelector('[data-tour-reset]');
  if (!button) return;
  button.disabled = tourResetPending || tourPreferencePending;
  button.textContent = tourResetPending
    ? 'Eliminando progreso…'
    : 'Eliminar progreso de recorridos';
  if (tourResetPending) button.setAttribute('aria-busy', 'true');
  else button.removeAttribute('aria-busy');
  renderTourPreference(getAuthState());
}

async function clearTourProgress() {
  if (tourResetPending || tourPreferencePending) return false;
  const confirmed = window.confirm(
    '¿Eliminar todo tu progreso de recorridos? Los recorridos completados y sugeridos volverán a aparecer como nuevos.'
  );
  if (!confirmed) return false;

  const authState = await waitForAuthState();
  if (!authState.authenticated || !authState.subject) return false;
  const feedback = document.querySelector('[data-tour-reset-feedback]');
  setTourResetPending(true);
  if (feedback) feedback.textContent = '';
  try {
    const response = await fetch(`${TOUR_STATE_ENDPOINT}?version=${ONBOARDING_VERSION}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`tour_state_${response.status}`);
    const result = await response.json();
    if (getAuthState().subject !== authState.subject) return false;
    cancelPendingSuggestion();
    if (activeSuggestion) dismissFeatureSuggestion({ restoreFocus: false });
    applyTourState(authState, result.state || createEmptyTourState(), { replace: true });
    if (feedback) feedback.textContent = 'Tu progreso se eliminó. Los recorridos vuelven a estar como nuevos.';
    announce('Progreso de recorridos eliminado.');
    return true;
  } catch (error) {
    console.error('No se pudo eliminar el progreso de los recorridos:', error);
    if (feedback) feedback.textContent = 'No se pudo eliminar el progreso. Inténtalo de nuevo.';
    announce('No se pudo eliminar el progreso de los recorridos.');
    return false;
  } finally {
    setTourResetPending(false);
  }
}

function closeSettingsPanel() {
  const panel = document.querySelector('.custom-theme-bar');
  if (panel && isVisible(panel)) panel.style.display = 'none';
}

async function prepareModalSurface(selector, waitForSelector = '') {
  const element = document.querySelector(selector);
  if (!element) return () => { };
  const initial = {
    className: element.className,
    style: element.getAttribute('style'),
    ariaHidden: element.getAttribute('aria-hidden'),
    ariaModal: element.getAttribute('aria-modal'),
    role: element.getAttribute('role')
  };
  const wasOpen = element.classList.contains('show') && window.getComputedStyle(element).display !== 'none';
  const existingInstance = wasOpen && window.bootstrap?.Modal?.getInstance
    ? window.bootstrap.Modal.getInstance(element)
    : null;
  const focusTrap = wasOpen && existingInstance ? existingInstance._focustrap : null;
  const wasFocusTrapActive = focusTrap?._isActive === true;
  if (focusTrap && typeof focusTrap.deactivate === 'function') focusTrap.deactivate();

  element.classList.add('show', 'lexius-tour-surface-preview');
  element.style.display = 'block';
  element.setAttribute('aria-hidden', 'true');
  element.removeAttribute('aria-modal');
  element.removeAttribute('role');
  if (waitForSelector) {
    const startedAt = Date.now();
    while (!document.querySelector(waitForSelector) && Date.now() - startedAt < 3500) {
      await wait(50);
    }
  }
  await wait(30);
  return () => {
    element.className = initial.className;
    if (initial.style == null) element.removeAttribute('style');
    else element.setAttribute('style', initial.style);
    if (initial.ariaHidden == null) element.removeAttribute('aria-hidden');
    else element.setAttribute('aria-hidden', initial.ariaHidden);
    if (initial.ariaModal == null) element.removeAttribute('aria-modal');
    else element.setAttribute('aria-modal', initial.ariaModal);
    if (initial.role == null) element.removeAttribute('role');
    else element.setAttribute('role', initial.role);
    // Docked chat windows intentionally allow focus in the underlying workspace.
    if (wasFocusTrapActive && typeof focusTrap.activate === 'function') focusTrap.activate();
  };
}

function isMobileChatLayout() {
  return !!(window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches);
}

function openChatSidebarForTour() {
  const panel = document.querySelector('[data-testid="chat-sidebar-panel"]');
  if (panel) panel.classList.toggle('lexius-tour-sidebar-preview', isMobileChatLayout());
}

function closeChatSidebarForTour() {
  const panel = document.querySelector('[data-testid="chat-sidebar-panel"]');
  if (panel) panel.classList.remove('lexius-tour-sidebar-preview');
}

function showLawyerChatSurfaceForTour() {
  document.body.classList.remove('lexius-tour-lawyer-main-surface');
}

function showLawyerChatSidebarForTour() {
  showLawyerChatSurfaceForTour();
  openChatSidebarForTour();
}

function showLawyerMainPageForTour() {
  closeChatSidebarForTour();
  hideDocumentMenuForTour();
  document.body.classList.add('lexius-tour-lawyer-main-surface');
}

function cloneVoiceCallSurfaceForTour(source, targetAttribute) {
  if (!source) return null;

  const clone = source.cloneNode(true);
  [clone, ...clone.querySelectorAll('*')].forEach(element => {
    if (element.id) {
      element.dataset.lexiusTourSourceId = element.id;
      element.removeAttribute('id');
    }
    element.removeAttribute('data-tour');
    element.removeAttribute('data-testid');
  });
  clone.dataset.lexiusTourDemoCall = 'true';
  clone.setAttribute(targetAttribute, '');
  return clone;
}

function findVoiceCallDemoPart(surface, sourceId) {
  if (!surface) return null;
  if (surface.dataset.lexiusTourSourceId === sourceId) return surface;
  return surface.querySelector(`[data-lexius-tour-source-id="${sourceId}"]`);
}

function insertVoiceCallDemoAfter(source, demo) {
  if (!source?.parentElement || !demo) return;
  source.parentElement.insertBefore(demo, source.nextSibling);
}

function prepareVoiceCallsDemoPreview() {
  const modal = document.getElementById('audioCallModal');
  if (!modal) return () => { };

  const modalBody = modal.querySelector('.modal-body');
  const initialScrollTop = modalBody?.scrollTop || 0;
  const hadDemoClass = modal.classList.contains('lexius-tour-call-demo');
  modal.querySelectorAll('[data-lexius-tour-demo-call]').forEach(element => element.remove());

  const contextSource = modal.querySelector('[data-tour="voice-call-context"]');
  const contextDemo = cloneVoiceCallSurfaceForTour(
    contextSource,
    'data-lexius-tour-demo-call-context'
  );
  const noContextMessage = findVoiceCallDemoPart(contextDemo, 'noAudioCallContextMessage');
  const contextList = findVoiceCallDemoPart(contextDemo, 'audioCallContextDocumentsList');
  const contextItems = findVoiceCallDemoPart(contextDemo, 'audioCallContextDocumentsItems');
  if (noContextMessage) noContextMessage.style.display = 'none';
  contextList?.classList.remove('d-none');
  if (contextItems) {
    contextItems.innerHTML = `
      <li class="list-group-item d-flex justify-content-between align-items-center bg-transparent border-secondary dynamic-color">
        <span class="text-truncate" title="Decisión judicial 123-2026 — Tribunal nacional">Decisión judicial 123-2026 — Tribunal nacional</span>
        <button class="btn btn-sm btn-outline-danger remove-audiocall-context-doc" title="Eliminar del contexto" disabled style="pointer-events:none;opacity:0.5;">
          <i class="anticon anticon-delete"></i>
        </button>
      </li>
    `;
  }
  insertVoiceCallDemoAfter(contextSource, contextDemo);

  const voiceSource = document.getElementById('callVoiceSelectionSection');
  const voiceDemo = cloneVoiceCallSurfaceForTour(
    voiceSource,
    'data-lexius-tour-demo-call-voice'
  );
  const voiceValue = findVoiceCallDemoPart(voiceDemo, 'callVoiceSelect');
  const voiceLabel = findVoiceCallDemoPart(voiceDemo, 'callVoiceSelectedLabel');
  const voiceButton = findVoiceCallDemoPart(voiceDemo, 'openCallVoicePickerBtn');
  const previewButton = findVoiceCallDemoPart(voiceDemo, 'previewCallVoiceBtn');
  if (voiceValue) voiceValue.value = 'lexius-tour-demo-voice';
  if (voiceLabel) voiceLabel.textContent = 'Lucía · Español latinoamericano';
  if (voiceButton) voiceButton.disabled = false;
  if (previewButton) {
    previewButton.disabled = false;
    previewButton.setAttribute('data-playing', 'false');
    previewButton.innerHTML = '<i class="anticon anticon-sound"></i>';
  }
  insertVoiceCallDemoAfter(voiceSource, voiceDemo);

  const controlsSource = modal.querySelector('[data-tour="voice-call-real-controls"]');
  const controlsDemo = cloneVoiceCallSurfaceForTour(
    controlsSource,
    'data-lexius-tour-demo-call-controls'
  );
  const callStatus = findVoiceCallDemoPart(controlsDemo, 'audio-call-status');
  const searchStatus = findVoiceCallDemoPart(controlsDemo, 'audio-call-search-status');
  const elapsedTime = findVoiceCallDemoPart(controlsDemo, 'audio-call-elapsed-time');
  const idleNotice = findVoiceCallDemoPart(controlsDemo, 'audio-call-idle-notice');
  const startButton = findVoiceCallDemoPart(controlsDemo, 'start-call-btn');
  const stopButton = findVoiceCallDemoPart(controlsDemo, 'stop-call-btn');
  if (callStatus) {
    callStatus.innerHTML = `
      <div class="text-green-500 font-semibold flex items-center justify-center text-lg">
        <i class="anticon anticon-sound mr-2 animate-pulse text-xl"></i>
        Llamada en curso...
      </div>
    `;
    callStatus.style.display = 'block';
  }
  if (searchStatus) searchStatus.style.display = 'none';
  if (elapsedTime) elapsedTime.textContent = '03:18';
  if (idleNotice) idleNotice.style.display = 'none';
  if (startButton) {
    startButton.disabled = true;
    startButton.classList.add('opacity-50');
    startButton.innerHTML = '<i class="anticon anticon-phone text-3xl"></i>';
  }
  if (stopButton) {
    stopButton.disabled = false;
    stopButton.classList.add('stop-button-active');
    stopButton.classList.remove('bg-gray-400', 'text-gray-700');
    stopButton.querySelector('i.anticon')?.classList.remove('text-gray-700');
  }
  insertVoiceCallDemoAfter(controlsSource, controlsDemo);

  const documentSource = document.getElementById('document-content-container');
  const documentDemo = cloneVoiceCallSurfaceForTour(
    documentSource,
    'data-lexius-tour-demo-call-document'
  );
  if (documentDemo) {
    documentDemo.innerHTML = `
      <div class="bg-fourth rounded-lg overflow-hidden shadow-lg border border-theme-color border-opacity-30 mb-5">
        <div class="p-4 bg-theme-color bg-opacity-10 border-b border-theme-color border-opacity-20">
          <h4 class="fs-4 fw-semibold d-flex align-items-center text-primary">
            <i class="anticon anticon-audit me-2"></i>
            Jurisprudencia Analizada
          </h4>
          <div class="text-sm text-gray-600 mt-1">ID: 123-2026</div>
        </div>
        <div class="metadata-section p-4 border-b border-theme-color border-opacity-20">
          <h5 class="font-semibold mb-3">Metadatos</h5>
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div class="metadata-item mb-2"><dt class="font-medium text-theme-color">Tribunal:</dt><dd>Tribunal nacional</dd></div>
            <div class="metadata-item mb-2"><dt class="font-medium text-theme-color">Fecha:</dt><dd>15/01/2026</dd></div>
            <div class="metadata-item mb-2"><dt class="font-medium text-theme-color">Tema:</dt><dd>Debido proceso</dd></div>
          </dl>
        </div>
        <div class="content-section p-4 text-center">
          <a href="#" class="btn btn-primary btn-lg">
            <i class="anticon anticon-file-text me-2"></i>Abrir Documento Completo
          </a>
        </div>
      </div>
    `;
    documentDemo.querySelector('.bg-fourth > div')
      ?.setAttribute('data-lexius-tour-demo-call-document-target', '');
  }
  documentSource?.parentElement?.insertBefore(documentDemo, documentSource);

  const searchDemo = document.createElement('div');
  searchDemo.className = 'w-100';
  searchDemo.dataset.lexiusTourDemoCall = 'true';
  searchDemo.setAttribute('data-lexius-tour-demo-call-search', '');
  const searchNavSource = document.getElementById('search-history-nav');
  const searchNavDemo = cloneVoiceCallSurfaceForTour(searchNavSource, 'data-lexius-tour-demo-call-part');
  const resultsSource = document.getElementById('jurisprudence-results-container');
  const resultsDemo = cloneVoiceCallSurfaceForTour(resultsSource, 'data-lexius-tour-demo-call-part');
  if (searchNavDemo) {
    searchNavDemo.removeAttribute('data-lexius-tour-demo-call');
    searchNavDemo.style.display = 'flex';
    const searchLabel = findVoiceCallDemoPart(searchNavDemo, 'search-history-label');
    const previousSearch = findVoiceCallDemoPart(searchNavDemo, 'prev-search-btn');
    const nextSearch = findVoiceCallDemoPart(searchNavDemo, 'next-search-btn');
    if (searchLabel) {
      searchLabel.innerHTML = `
        <span class="search-query-text" title="garantías del debido proceso y tutela judicial efectiva">Resultados para "garantías del debido proceso y tutela judicial efectiva"</span>
        <span class="search-counter">(2/2)</span>
      `;
    }
    if (previousSearch) previousSearch.disabled = false;
    if (nextSearch) nextSearch.disabled = true;
    searchDemo.appendChild(searchNavDemo);
  }
  if (resultsDemo) {
    resultsDemo.removeAttribute('data-lexius-tour-demo-call');
    resultsDemo.style.display = 'block';
    resultsDemo.querySelector('.bg-fourth > div')
      ?.setAttribute('data-lexius-tour-demo-call-search-target', '');
    const resultsCount = findVoiceCallDemoPart(resultsDemo, 'jurisprudence-results-count');
    const resultsTable = findVoiceCallDemoPart(resultsDemo, 'jurisprudenceResultsTable');
    const resultsHead = findVoiceCallDemoPart(resultsDemo, 'jurisprudenceResultsTableHead');
    const resultsBody = findVoiceCallDemoPart(resultsDemo, 'jurisprudenceResultsTableBody');
    if (resultsCount) resultsCount.textContent = '2';
    resultsTable?.classList.add('border', 'border-collapse', 'border-fourth');
    if (resultsHead) {
      resultsHead.innerHTML = `
        <tr>
          <th class="px-4 py-3 border border-fourth">Tribunal</th>
          <th class="px-4 py-3 border border-fourth">Fecha</th>
          <th class="px-4 py-3 border border-fourth" style="min-width: 35%; max-width: 50%;">Extracto</th>
          <th class="px-4 py-3 border border-fourth">Enlace</th>
        </tr>
      `;
    }
    if (resultsBody) {
      resultsBody.innerHTML = `
        <tr class="transition-colors duration-150 ease-in-out">
          <td class="px-4 py-3 border border-fourth"><div class="whitespace-normal">Tribunal nacional</div></td>
          <td class="px-4 py-3 border border-fourth"><div class="whitespace-normal">15/01/2026</div></td>
          <td class="px-4 py-3 border border-fourth" style="min-width: 35%; max-width: 50%;"><div class="whitespace-normal">La decisión desarrolla las garantías del debido proceso y la tutela judicial efectiva.</div></td>
          <td class="px-4 py-3 border border-fourth"><a href="#" class="inline-flex items-center px-3 py-1 rounded-full bg-theme-color text-white hover:bg-opacity-90 transition-colors font-medium"><i class="anticon anticon-link mr-1"></i> Ver</a></td>
        </tr>
        <tr class="transition-colors duration-150 ease-in-out">
          <td class="px-4 py-3 border border-fourth"><div class="whitespace-normal">Tribunal nacional</div></td>
          <td class="px-4 py-3 border border-fourth"><div class="whitespace-normal">08/10/2025</div></td>
          <td class="px-4 py-3 border border-fourth" style="min-width: 35%; max-width: 50%;"><div class="whitespace-normal">La providencia explica el derecho de defensa y la debida motivación.</div></td>
          <td class="px-4 py-3 border border-fourth"><a href="#" class="inline-flex items-center px-3 py-1 rounded-full bg-theme-color text-white hover:bg-opacity-90 transition-colors font-medium"><i class="anticon anticon-link mr-1"></i> Ver</a></td>
        </tr>
      `;
    }
    searchDemo.appendChild(resultsDemo);
  }
  searchNavSource?.parentElement?.insertBefore(searchDemo, searchNavSource);

  const messagesSource = document.getElementById('audio-call-messages');
  const messagesDemo = cloneVoiceCallSurfaceForTour(
    messagesSource,
    'data-lexius-tour-demo-call-messages'
  );
  if (messagesDemo) messagesDemo.innerHTML = '';
  messagesSource?.parentElement?.insertBefore(messagesDemo, messagesSource);

  modal.classList.add('lexius-tour-call-demo');
  if (modalBody) modalBody.scrollTop = 0;

  return () => {
    modal.querySelectorAll('[data-lexius-tour-demo-call]').forEach(element => element.remove());
    if (!hadDemoClass) modal.classList.remove('lexius-tour-call-demo');
    if (modalBody) modalBody.scrollTop = initialScrollTop;
  };
}

async function prepareVoiceCallsTourSurface(authState) {
  const cleanupSurface = await prepareFeatureModalSurface('#audioCallModal', authState.paid);
  const cleanupDemo = authState.paid ? prepareVoiceCallsDemoPreview() : () => { };
  return async () => {
    cleanupDemo();
    await cleanupSurface();
  };
}

async function prepareLawyerSurface() {
  const cleanupDocumentMenu = await prepareDocumentMenuSurface();
  const cleanupModal = await prepareModalSurface('#chatModal', '[data-tour="chat-header"]');
  showLawyerChatSurfaceForTour();
  closeChatSidebarForTour();
  return async () => {
    showLawyerChatSurfaceForTour();
    closeChatSidebarForTour();
    await cleanupModal();
    cleanupDocumentMenu();
  };
}

async function prepareChatResponseSurface() {
  const cleanupModal = await prepareModalSurface('#chatModal', '[data-tour="chat-response"]');
  showLawyerChatSurfaceForTour();
  closeChatSidebarForTour();
  return async () => {
    showLawyerChatSurfaceForTour();
    closeChatSidebarForTour();
    await cleanupModal();
  };
}

async function waitForDocumentEditionWorkspaceState(workspace, predicate, timeoutMilliseconds = 15000) {
  const startedAt = Date.now();
  while (workspace?.isConnected && Date.now() - startedAt < timeoutMilliseconds) {
    if (predicate(workspace)) return true;
    await wait(50);
  }
  return false;
}

async function prepareDocumentEditionSurface() {
  const cleanupChatResponse = await prepareChatResponseSurface();
  const workspace = latestDocumentEditionWorkspace({ visibleOnly: true });
  if (!workspace) {
    await cleanupChatResponse();
    throw new Error('document_edition_workspace_unavailable');
  }
  activeDocumentEditionWorkspace = workspace;
  const initialVersion = Number(
    workspace.getAttribute('data-document-edition-selected-version')
  );
  const initialButton = workspace.querySelector('[data-testid="artifact-diff-open"]');
  const wasOpen = initialButton?.getAttribute('aria-expanded') === 'true';

  const restoreSurfaceState = async () => {
    if (!workspace.isConnected) return;
    const openButton = workspace.querySelector('[data-testid="artifact-diff-open"]');
    if (openButton?.getAttribute('aria-expanded') === 'true') {
      openButton.click();
      await wait(30);
    }
    const selectedVersion = Number(
      workspace.getAttribute('data-document-edition-selected-version')
    );
    if (Number.isInteger(initialVersion) && selectedVersion !== initialVersion) {
      const versionButton = workspace.querySelector(
        `[data-document-edition-version="${initialVersion}"]`
      );
      if (versionButton && !versionButton.disabled) {
        versionButton.click();
        await waitForDocumentEditionWorkspaceState(
          workspace,
          current => Number(
            current.getAttribute('data-document-edition-selected-version')
          ) === initialVersion,
          7000
        );
      }
    }
    if (wasOpen) {
      workspace.dispatchEvent(new CustomEvent(
        'lexius-tour-set-document-edition-comparison',
        {
          detail: { open: wasOpen },
          bubbles: true,
          cancelable: false
        }
      ));
      await wait(40);
    }
  };

  const cleanup = async () => {
    await restoreSurfaceState();
    try {
      await cleanupChatResponse();
    } finally {
      await wait(40);
      await restoreSurfaceState();
      if (activeDocumentEditionWorkspace === workspace) {
        activeDocumentEditionWorkspace = null;
      }
    }
  };

  try {
    if (wasOpen && initialButton && !initialButton.disabled) {
      initialButton.click();
      await wait(30);
    }
    const tourVersionButton = workspace.querySelector(
      '[data-document-edition-tour-version="true"]'
    );
    const tourVersion = Number(
      tourVersionButton?.getAttribute('data-document-edition-version')
    );
    if (!Number.isInteger(tourVersion) || tourVersion < 2) {
      throw new Error('document_edition_comparable_version_unavailable');
    }
    if (Number(
      workspace.getAttribute('data-document-edition-selected-version')
    ) !== tourVersion) {
      tourVersionButton.click();
      const selected = await waitForDocumentEditionWorkspaceState(
        workspace,
        current => Number(
          current.getAttribute('data-document-edition-selected-version')
        ) === tourVersion,
        7000
      );
      if (!selected) throw new Error('document_edition_version_load_timeout');
    }

    const comparisonButton = workspace.querySelector('[data-testid="artifact-diff-open"]');
    if (!comparisonButton || comparisonButton.disabled) {
      throw new Error('document_edition_comparison_unavailable');
    }
    if (comparisonButton.getAttribute('aria-expanded') !== 'true') comparisonButton.click();
    await wait(30);
    const comparisonReady = await waitForDocumentEditionWorkspaceState(
      workspace,
      current => ['changes', 'unchanged', 'error'].includes(
        current.getAttribute('data-document-edition-comparison-state')
      )
    );
    const comparisonState = workspace.getAttribute('data-document-edition-comparison-state');
    if (!comparisonReady || comparisonState === 'error') {
      throw new Error('document_edition_comparison_load_failed');
    }
    const closeButton = workspace.querySelector('[data-testid="artifact-diff-open"]');
    if (closeButton?.getAttribute('aria-expanded') === 'true') {
      closeButton.click();
      await wait(30);
    }
    return cleanup;
  } catch (error) {
    await cleanup();
    throw error;
  }
}

function refreshSearchSurfaceForLayout() {
  if (!activeSearchSurface) return;
  const { menu, right, header, initial } = activeSearchSurface;
  if (isTouchLayout()) {
    menu.classList.add('active');
    if (header) header.setAttribute('style', 'position: static!important;');
  } else {
    menu.classList.toggle('active', initial.active);
    menu.classList.remove('width-0');
    if (right) right.classList.remove('width-100');
    if (header) {
      if (initial.headerStyle == null) header.removeAttribute('style');
      else header.setAttribute('style', initial.headerStyle);
    }
  }
}

async function prepareSearchSurface() {
  const menu = document.querySelector('.left-side-menu');
  const right = document.querySelector('.right-side-container');
  const header = document.querySelector('.body-header');
  if (!menu) return () => { };
  const initial = {
    active: menu.classList.contains('active'),
    collapsed: menu.classList.contains('width-0'),
    rightWide: !!(right && right.classList.contains('width-100')),
    headerStyle: header ? header.getAttribute('style') : null,
    menuNoTransition: menu.classList.contains('lexius-tour-no-transition'),
    rightNoTransition: !!(right && right.classList.contains('lexius-tour-no-transition'))
  };

  activeSearchSurface = { menu, right, header, initial };
  menu.classList.add('lexius-tour-no-transition');
  if (right) right.classList.add('lexius-tour-no-transition');
  refreshSearchSurfaceForLayout();
  await wait(prefersReducedMotion() ? 20 : 430);

  return () => {
    menu.classList.toggle('active', initial.active);
    menu.classList.toggle('width-0', initial.collapsed);
    if (right) right.classList.toggle('width-100', initial.rightWide);
    menu.classList.toggle('lexius-tour-no-transition', initial.menuNoTransition);
    if (right) right.classList.toggle('lexius-tour-no-transition', initial.rightNoTransition);
    if (header) {
      if (initial.headerStyle == null) header.removeAttribute('style');
      else header.setAttribute('style', initial.headerStyle);
    }
    if (activeSearchSurface?.menu === menu) activeSearchSurface = null;
  };
}

function prepareSearchResultsSurface() {
  const surface = { expandedRows: new Set() };
  activeSearchResultsSurface = surface;
  [
    SEARCH_RESULT_EXTRACT_SELECTOR,
    SEARCH_RESULT_ANALYSIS_SELECTOR,
    SEARCH_RESULT_OPEN_SELECTOR
  ].forEach(revealSearchResultTourTarget);

  return () => {
    surface.expandedRows.forEach(row => {
      if (!row.isConnected || !isResponsiveSearchResultRowExpanded(row)) return;
      const control = findVisible(
        'td.dtr-control, th.dtr-control, td.control, th.control',
        row
      );
      control?.click();
    });
    if (activeSearchResultsSurface === surface) activeSearchResultsSurface = null;
  };
}

function setDocumentFeatureModalVisibility(visible) {
  const modal = activeDocumentFeatureSurface?.modal;
  if (!modal) return;
  modal.style.display = visible ? 'block' : 'none';
  document.body.classList.toggle('lexius-tour-document-feature-page', !visible);
}

function showDocumentFeaturePageForTour() {
  hideDocumentTourTransientSurfaces();
  setDocumentFeatureModalVisibility(false);
}

function showDocumentFeatureMenuForTour(highlightSelector = '') {
  hideDocumentAnnotationForTour();
  setDocumentFeatureModalVisibility(false);
  revealDocumentMenuForTour(highlightSelector);
}

function showDocumentFeatureSelectionForTour() {
  hideDocumentMenuForTour();
  setDocumentFeatureModalVisibility(false);
  revealDocumentAnnotationForTour();
}

function showDocumentFeatureModalForTour() {
  hideDocumentTourTransientSurfaces();
  setDocumentFeatureModalVisibility(true);
}

async function prepareDocumentFeatureSurface(modalSelector, { previewSelection = false } = {}) {
  const cleanupDocumentMenu = await prepareDocumentMenuSurface();
  const documentRunningInitially = document.body.classList.contains('lexius-tour-document-running');
  document.body.classList.add('lexius-tour-document-running');
  const cleanupModal = await prepareModalSurface(modalSelector);
  const surface = {
    modal: document.querySelector(modalSelector)
  };
  activeDocumentFeatureSurface = surface;
  showDocumentFeaturePageForTour();
  const cleanupAnnotationPreview = previewSelection && pageHasDocument()
    ? await prepareDocumentAnnotationPreview()
    : async () => { };
  showDocumentFeaturePageForTour();

  return async () => {
    if (activeDocumentFeatureSurface === surface) activeDocumentFeatureSurface = null;
    document.body.classList.remove('lexius-tour-document-feature-page');
    hideDocumentTourTransientSurfaces();
    await cleanupAnnotationPreview();
    await cleanupModal();
    document.body.classList.toggle('lexius-tour-document-running', documentRunningInitially);
    cleanupDocumentMenu();
  };
}

function prepareVirtualAssistantSurface() {
  return prepareDocumentFeatureSurface('#documentSummaryModal');
}

function prepareIntelligentAnalysisSurface() {
  return prepareDocumentFeatureSurface('#intelligentSummaryModal', { previewSelection: true });
}

function prepareNotesSurface() {
  return prepareDocumentFeatureSurface('#libraryModal', { previewSelection: true });
}

async function prepareDocumentSurface() {
  const cleanupDocumentMenu = await prepareDocumentMenuSurface();
  document.body.classList.add('lexius-tour-document-running');
  const cleanupAnnotationPreview = await prepareDocumentAnnotationPreview();
  return async () => {
    await cleanupAnnotationPreview();
    document.body.classList.remove('lexius-tour-document-running');
    cleanupDocumentMenu();
  };
}

async function prepareDocumentMenuSurface() {
  const menu = document.getElementById('context-menu');
  const initialClass = menu?.className || '';
  const initialStyle = menu?.getAttribute('style');
  hideDocumentMenuForTour();
  await wait(20);
  return () => {
    if (!menu) return;
    clearDocumentMenuOptionHighlightsForTour();
    menu.className = initialClass;
    if (initialStyle == null) menu.removeAttribute('style');
    else menu.setAttribute('style', initialStyle);
  };
}

function buildDocumentTourSelectionRange(root, maxCharacters = 200) {
  if (!root) return null;
  const textNodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (
        !parent
        || !node.nodeValue?.trim()
        || parent.closest('script, style, noscript, .r6o-editor, .r6o-selection')
        || !isVisible(parent)
      ) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  if (!textNodes.length) return null;

  const firstNode = textNodes.find(node => node.nodeValue.trim().length >= 30) || textNodes[0];
  const startOffset = firstNode.nodeValue.search(/\S/);
  if (startOffset < 0) return null;
  const endOffset = Math.min(firstNode.nodeValue.length, startOffset + maxCharacters);
  const range = document.createRange();
  range.setStart(firstNode, startOffset);
  range.setEnd(firstNode, endOffset);

  return range.toString().trim() ? range : null;
}

function dispatchDocumentTourSelection(range) {
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range.cloneRange());
  document.dispatchEvent(new Event('selectionchange', { bubbles: true }));

  const rects = Array.from(range.getClientRects()).filter(rect => rect.width > 0 && rect.height > 0);
  const lastRect = rects[rects.length - 1] || range.getBoundingClientRect();
  const target = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
    ? range.commonAncestorContainer.parentElement
    : range.commonAncestorContainer;
  (target || document).dispatchEvent(new MouseEvent('mouseup', {
    bubbles: true,
    cancelable: true,
    clientX: Math.max(0, Math.min(window.innerWidth - 1, lastRect.right - 2)),
    clientY: Math.max(0, Math.min(window.innerHeight - 1, lastRect.top + (lastRect.height / 2)))
  }));
}

async function prepareDocumentAnnotationPreview() {
  const existingEditor = findVisible('.r6o-editor');
  const selection = window.getSelection();
  const previousRanges = selection
    ? Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index).cloneRange())
    : [];
  let editor = existingEditor;
  let ownsEditor = false;
  let analysisButton = null;
  let selectedFragments = [];

  if (!editor) {
    for (let attempt = 0; attempt < 20 && !window.lexiusAnnotation; attempt += 1) await wait(50);
    const content = document.querySelector('[data-testid="document-content"]');
    const range = buildDocumentTourSelectionRange(content);
    if (range && window.lexiusAnnotation) {
      const startElement = range.startContainer.parentElement;
      startElement?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      dispatchDocumentTourSelection(range);
      for (let attempt = 0; attempt < 20; attempt += 1) {
        editor = document.querySelector('.r6o-editor');
        analysisButton = editor?.querySelector('.intelligent-summary-annot-button') || null;
        selectedFragments = Array.from(document.querySelectorAll('.r6o-selection'));
        if (editor && analysisButton && selectedFragments.length) break;
        await wait(25);
      }
      ownsEditor = !!editor;
    }
  }

  for (let attempt = 0; editor && attempt < 20; attempt += 1) {
    analysisButton = editor.querySelector('.intelligent-summary-annot-button');
    selectedFragments = Array.from(document.querySelectorAll('.r6o-selection'));
    if (analysisButton && selectedFragments.length) break;
    await wait(25);
  }
  const initialAnalysisButtonAttributes = analysisButton ? {
    ariaLabel: analysisButton.getAttribute('aria-label'),
    title: analysisButton.getAttribute('title')
  } : null;
  if (editor && analysisButton) {
    editor.dataset.lexiusTourPreview = 'true';
    analysisButton.dataset.tour = 'selection-analysis-popup';
    analysisButton.setAttribute('aria-label', 'Abrir Análisis Inteligente para el texto seleccionado');
    analysisButton.setAttribute('title', 'Análisis Inteligente');
    selectedFragments.forEach(fragment => { fragment.dataset.lexiusTourPreview = 'true'; });
  }

  return async () => {
    document.body.classList.remove('lexius-tour-recogito-visible');
    if (analysisButton) {
      analysisButton.removeAttribute('data-tour');
      if (initialAnalysisButtonAttributes.ariaLabel == null) analysisButton.removeAttribute('aria-label');
      else analysisButton.setAttribute('aria-label', initialAnalysisButtonAttributes.ariaLabel);
      if (initialAnalysisButtonAttributes.title == null) analysisButton.removeAttribute('title');
      else analysisButton.setAttribute('title', initialAnalysisButtonAttributes.title);
    }
    selectedFragments.forEach(fragment => fragment.removeAttribute('data-lexius-tour-preview'));
    editor?.removeAttribute('data-lexius-tour-preview');
    if (ownsEditor && editor?.isConnected) {
      editor.querySelector('.annot-cancel-button')?.click();
      await wait(20);
    }
    if (selection) {
      selection.removeAllRanges();
      previousRanges.forEach(range => {
        try { selection.addRange(range); } catch (error) { }
      });
    }
  };
}

function hideDocumentAnnotationForTour() {
  document.body.classList.remove('lexius-tour-recogito-visible');
}

function revealDocumentAnnotationForTour() {
  hideDocumentMenuForTour();
  document.body.classList.add('lexius-tour-recogito-visible');
}

function hideDocumentTourTransientSurfaces() {
  hideDocumentAnnotationForTour();
  hideDocumentMenuForTour();
}

function hideDocumentMenuForTour() {
  const menu = document.getElementById('context-menu');
  if (!menu) return;
  clearDocumentMenuOptionHighlightsForTour();
  menu.classList.remove('show', 'lexius-tour-context-menu');
  menu.style.display = 'none';
}

function clearDocumentMenuOptionHighlightsForTour() {
  document.querySelectorAll('#context-menu .lexius-tour-menu-option-highlight').forEach(option => {
    option.classList.remove('lexius-tour-menu-option-highlight');
  });
}

function revealDocumentMenuForTour(highlightSelector = '') {
  hideDocumentAnnotationForTour();
  const menu = document.getElementById('context-menu');
  if (!menu) return;
  const shortCompactGeneratorStep = highlightSelector === '[data-tour="document-generator"]'
    && isCompactTourLayout()
    && window.innerHeight <= 700;
  clearDocumentMenuOptionHighlightsForTour();
  if (highlightSelector) {
    menu.querySelectorAll(highlightSelector).forEach(option => {
      option.classList.add('lexius-tour-menu-option-highlight');
    });
  }
  menu.classList.add('show', 'lexius-tour-context-menu');
  menu.style.display = 'block';
  menu.style.top = shortCompactGeneratorStep ? '10px' : (isTouchLayout() ? '90px' : '76px');
  if (isTouchLayout()) {
    menu.style.left = '10px';
    menu.style.right = '10px';
  } else {
    const trigger = findVisible('[data-tour="feature-menu"]');
    const triggerRect = trigger ? trigger.getBoundingClientRect() : { left: window.innerWidth - 340 };
    const width = Math.min(360, Math.max(260, menu.getBoundingClientRect().width || 320));
    menu.style.left = `${Math.max(12, Math.min(triggerRect.left, window.innerWidth - width - 12))}px`;
    menu.style.right = 'auto';
  }
}

async function prepareSettingsSurface() {
  const panel = document.querySelector('.custom-theme-bar');
  if (!panel) return () => { };
  const initialDisplay = panel.style.display;
  const initialScrollTop = panel.scrollTop;
  panel.style.display = 'block';
  panel.scrollTop = 0;
  await wait(prefersReducedMotion() ? 20 : 140);
  return () => {
    panel.style.display = initialDisplay;
    panel.scrollTop = initialScrollTop;
  };
}

function clearCompactTourFocus(except = null) {
  document.querySelectorAll('.lexius-tour-mobile-focus').forEach(element => {
    if (element !== except) element.classList.remove('lexius-tour-mobile-focus');
  });
}

function visibleAnnotationTourContext(fallbackElement = null) {
  const elements = Array.from(document.querySelectorAll(
    '.r6o-selection[data-lexius-tour-preview="true"], '
    + '.r6o-editor[data-lexius-tour-preview="true"]'
  )).filter(isVisible);
  if (fallbackElement && isVisible(fallbackElement) && !elements.includes(fallbackElement)) {
    elements.push(fallbackElement);
  }
  return elements;
}

function elementsBoundingRect(elements) {
  const rects = elements
    .map(element => element?.getBoundingClientRect?.())
    .filter(rect => rect && rect.width > 0 && rect.height > 0);
  if (!rects.length) return null;
  const left = Math.min(...rects.map(rect => rect.left));
  const top = Math.min(...rects.map(rect => rect.top));
  const right = Math.max(...rects.map(rect => rect.right));
  const bottom = Math.max(...rects.map(rect => rect.bottom));
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top
  };
}

function stickyPageHeaderClearTop(element) {
  const scrollContainer = element?.closest('.right-side-container');
  const pageHeader = scrollContainer?.querySelector('.body-header');
  if (!scrollContainer || !pageHeader || !isVisible(pageHeader)) return 0;

  const headerPosition = window.getComputedStyle(pageHeader).position;
  if (headerPosition !== 'sticky' && headerPosition !== 'fixed') return 0;
  const headerRect = pageHeader.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  return Math.max(containerRect.top, headerRect.bottom);
}

function annotationContextHeaderGap(element) {
  const scrollContainer = element?.closest('.right-side-container');
  const pageTitle = scrollContainer?.querySelector('#page-title');
  const titleHeight = pageTitle && isVisible(pageTitle)
    ? pageTitle.getBoundingClientRect().height
    : 0;
  return Math.max(32, Math.min(48, 12 + (titleHeight / 2)));
}

function keepAnnotationContextBelowPageHeader(element) {
  const scrollContainer = element?.closest('.right-side-container');
  if (!scrollContainer) return;
  const contextRect = elementsBoundingRect(visibleAnnotationTourContext(element));
  if (!contextRect) return;

  const headerClearTop = stickyPageHeaderClearTop(element);
  const safeTop = Math.max(16, headerClearTop + annotationContextHeaderGap(element));
  const adjustment = contextRect.top - safeTop;
  if (Math.abs(adjustment) < 1) return;
  scrollContainer.scrollTop = Math.max(0, scrollContainer.scrollTop + adjustment);
}

function keepTourTargetBelowStickyPageHeader(element) {
  const scrollContainer = element?.closest('.right-side-container');
  const pageHeader = scrollContainer?.querySelector('.body-header');
  if (!scrollContainer || !pageHeader || !isVisible(pageHeader)) return;

  const headerPosition = window.getComputedStyle(pageHeader).position;
  if (headerPosition !== 'sticky' && headerPosition !== 'fixed') return;

  const targetRect = element.getBoundingClientRect();
  const headerRect = pageHeader.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const clearTop = Math.max(containerRect.top, headerRect.bottom) + 12;
  const coveredHeight = clearTop - targetRect.top;
  if (coveredHeight <= 0) return;

  scrollContainer.scrollTop = Math.max(0, scrollContainer.scrollTop - coveredHeight);
}

function scrollTourTargetIntoView(element, rawStep) {
  if (!element) return;
  const responsive = isResponsiveTourLayout();
  clearCompactTourFocus(responsive ? element : null);
  element.classList.toggle('lexius-tour-mobile-focus', responsive);
  element.scrollIntoView({
    block: responsive
      ? (rawStep.mobileScrollBlock || (rawStep.keepAnnotationContextVisible ? 'center' : 'start'))
      : 'center',
    inline: 'nearest',
    behavior: responsive || prefersReducedMotion() ? 'auto' : 'smooth'
  });
  if (responsive && rawStep.keepAnnotationContextVisible) {
    keepAnnotationContextBelowPageHeader(element);
  }
  if (responsive && rawStep.avoidStickyPageHeader) {
    keepTourTargetBelowStickyPageHeader(element);
  }
  if (responsive) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const wrapper = document.querySelector('.driver-popover.lexius-tour-popover');
        settleCompactPopoverPlacement(wrapper);
      });
    });
  }
}

function normalizeSteps(rawSteps) {
  return rawSteps.reduce((steps, rawStep) => {
    if (!rawStep) return steps;
    const allowHidden = typeof rawStep.reveal === 'function';
    const targetResolver = () => {
      if (isTabletTourLayout()) {
        const tabletTarget = rawStep.tabletTarget || rawStep.mobileTarget;
        const tabletElement = resolveTarget(tabletTarget, allowHidden);
        if (tabletElement) return tabletElement;
      }
      if (isCompactTourLayout() && rawStep.mobileTarget) {
        const mobileElement = resolveTarget(rawStep.mobileTarget, allowHidden);
        if (mobileElement) return mobileElement;
      }
      return resolveTarget(rawStep.target, allowHidden);
    };
    const element = targetResolver();
    if (rawStep.target && !element && rawStep.optional) return steps;
    const step = {
      popover: {
        title: rawStep.title,
        description: rawStep.description,
        side: rawStep.side || 'bottom',
        align: rawStep.align || 'center'
      }
    };
    if (element) {
      step.element = typeof rawStep.target === 'function' || rawStep.mobileTarget || rawStep.tabletTarget
        ? targetResolver
        : element;
    }
    if (element || typeof rawStep.beforeHighlight === 'function' || typeof rawStep.reveal === 'function') {
      step.onHighlightStarted = highlightedElement => {
        if (activeRun) {
          activeRun.keepAnnotationContextVisible = isResponsiveTourLayout()
            && rawStep.keepAnnotationContextVisible === true;
        }
        try {
          if (typeof rawStep.beforeHighlight === 'function') rawStep.beforeHighlight();
          if (typeof rawStep.reveal === 'function') rawStep.reveal();
        } catch (error) { }
        try {
          const currentElement = highlightedElement || targetResolver();
          scrollTourTargetIntoView(currentElement, rawStep);
        } catch (error) { }
        if (typeof rawStep.reveal === 'function') {
          window.requestAnimationFrame(() => {
            const driver = activeRun?.driver;
            if (!driver?.isActive()) return;
            const revealedElement = targetResolver();
            if (revealedElement && highlightedElement && revealedElement !== highlightedElement) {
              const activeIndex = driver.getActiveIndex();
              if (Number.isInteger(activeIndex)) {
                driver.moveTo(activeIndex);
                return;
              }
            }
            driver.refresh();
          });
        }
      };
      step.onDeselected = deselectedElement => {
        deselectedElement?.classList.remove('lexius-tour-mobile-focus');
      };
    }
    steps.push(step);
    return steps;
  }, []);
}

function clearCompactPopoverPlacement(wrapper) {
  if (!wrapper) return;
  wrapper.classList.remove(
    'lexius-tour-popover--compact',
    'lexius-tour-popover--tablet',
    'lexius-tour-popover--edge-top',
    'lexius-tour-popover--edge-bottom'
  );
  wrapper.style.removeProperty('--lexius-tour-available-height');
  wrapper.style.removeProperty('--lexius-tour-edge-top');
  wrapper.removeAttribute('data-mobile-placement');
}

function currentHighlightedTourTarget(activeElement = null) {
  if (
    activeElement
    && activeElement.id !== 'driver-dummy-element'
    && isVisible(activeElement)
  ) return activeElement;
  return findVisible('.driver-active-element:not(#driver-dummy-element)')
    || document.querySelector('.driver-active-element');
}

function settleSearchResultsTargetAfterResize(run, activeIndex) {
  if (!Number.isInteger(activeIndex)) return;
  (run.responsiveRetargetTimers || []).forEach(timer => window.clearTimeout(timer));
  run.responsiveRetargetTimers = [];
  const retarget = () => {
    const activeStep = run.driver?.getActiveStep();
    if (
      activeRun !== run
      || !run.driver?.isActive()
      || run.driver.getActiveIndex() !== activeIndex
      || !activeStep?.element
    ) return;

    const expectedTarget = resolveTarget(activeStep.element, true);
    if (!expectedTarget) return;
    if (
      run.driver.getActiveElement() === expectedTarget
      && expectedTarget.id !== 'driver-dummy-element'
    ) run.driver.refresh();
    else run.driver.moveTo(activeIndex);
    window.requestAnimationFrame(refreshCompactPopoverPlacement);
  };

  retarget();
  run.responsiveRetargetTimers = [350, 700, 1050, 1400, 1750].map(delay => (
    window.setTimeout(retarget, delay)
  ));
}

function updateCompactPopoverPlacement(wrapper, activeElement = null) {
  if (!wrapper) return;
  const hidden = wrapper.style.display === 'none';
  clearCompactPopoverPlacement(wrapper);
  const compact = isCompactTourLayout();
  const tablet = isTabletTourLayout();
  if (!compact && !tablet) {
    if (!hidden) wrapper.style.display = 'block';
    return;
  }
  if (hidden) return;

  wrapper.classList.add(tablet ? 'lexius-tour-popover--tablet' : 'lexius-tour-popover--compact');
  wrapper.style.display = 'flex';
  const highlightedTarget = currentHighlightedTourTarget(activeElement);
  if (!highlightedTarget || highlightedTarget.id === 'driver-dummy-element') return;
  const annotationEditor = (compact || tablet)
    && activeRun?.definition?.id === 'document'
    && activeRun.driver?.getActiveIndex() === 1
    ? findVisible('.r6o-editor[data-lexius-tour-preview="true"]')
    : null;
  const target = annotationEditor
    || (tablet && highlightedTarget.matches('[data-tour="selection-analysis-popup"]')
      ? highlightedTarget.closest('.r6o-editor[data-lexius-tour-preview="true"]') || highlightedTarget
      : highlightedTarget);

  // The responsive annotation preview is created before Driver highlights it,
  // while Driver may render or refresh the popover before the step callback
  // has published its flag. Infer this one known context from the active tour
  // and step as well, so a late refresh cannot leave the popover over the note
  // editor on short mobile viewports.
  const keepAnnotationContextVisible = activeRun?.keepAnnotationContextVisible === true
    || !!annotationEditor;
  if (keepAnnotationContextVisible) keepAnnotationContextBelowPageHeader(target);
  const targetRect = keepAnnotationContextVisible
    ? elementsBoundingRect(visibleAnnotationTourContext(target))
    : target.getBoundingClientRect();
  if (!targetRect) return;
  if (targetRect.width <= 0 || targetRect.height <= 0) return;
  const popoverRect = wrapper.getBoundingClientRect();
  const popoverHeight = popoverRect.height;
  const viewportWidth = Math.min(
    window.innerWidth,
    document.documentElement.clientWidth || window.innerWidth,
    window.visualViewport?.width || window.innerWidth
  );
  const viewportHeight = Math.min(
    window.innerHeight,
    document.documentElement.clientHeight || window.innerHeight,
    window.visualViewport?.height || window.innerHeight
  );
  const layoutViewportOffsetTop = Math.max(0, window.visualViewport?.offsetTop || 0);
  const edgeInset = 15;
  const targetGap = 20;
  const spaceAbove = Math.max(0, targetRect.top - edgeInset - targetGap);
  const spaceBelow = Math.max(0, viewportHeight - targetRect.bottom - edgeInset - targetGap);

  if (compact && keepAnnotationContextVisible && spaceBelow >= 112) {
    wrapper.classList.add('lexius-tour-popover--edge-bottom');
    wrapper.style.setProperty('--lexius-tour-available-height', `${Math.floor(spaceBelow)}px`);
    const adjustedPopoverHeight = wrapper.getBoundingClientRect().height;
    const edgeTop = layoutViewportOffsetTop + viewportHeight - adjustedPopoverHeight - 10;
    wrapper.style.setProperty(
      '--lexius-tour-edge-top',
      `${Math.max(layoutViewportOffsetTop + 10, Math.floor(edgeTop))}px`
    );
    wrapper.dataset.mobilePlacement = 'bottom';
    return;
  }
  const overlapWidth = Math.max(
    0,
    Math.min(popoverRect.right, targetRect.right) - Math.max(popoverRect.left, targetRect.left)
  );
  const overlapHeight = Math.max(
    0,
    Math.min(popoverRect.bottom, targetRect.bottom) - Math.max(popoverRect.top, targetRect.top)
  );
  const overlapsTarget = overlapWidth > 1 && overlapHeight > 1;
  const exceedsViewport = popoverRect.left < 0 || popoverRect.top < 0
    || popoverRect.right > viewportWidth || popoverRect.bottom > viewportHeight;
  if (
    !overlapsTarget
    && !exceedsViewport
    && (spaceAbove >= popoverHeight || spaceBelow >= popoverHeight)
  ) return;

  const useBottomEdge = spaceBelow >= spaceAbove;
  const availableHeight = Math.floor(useBottomEdge ? spaceBelow : spaceAbove);
  if (availableHeight < 112) return;
  wrapper.classList.add(
    useBottomEdge ? 'lexius-tour-popover--edge-bottom' : 'lexius-tour-popover--edge-top'
  );
  wrapper.style.setProperty('--lexius-tour-available-height', `${Math.max(0, availableHeight)}px`);
  const adjustedPopoverHeight = wrapper.getBoundingClientRect().height;
  const edgeTop = useBottomEdge
    ? layoutViewportOffsetTop + viewportHeight - adjustedPopoverHeight - 10
    : layoutViewportOffsetTop + 10;
  wrapper.style.setProperty(
    '--lexius-tour-edge-top',
    `${Math.max(layoutViewportOffsetTop + 10, Math.floor(edgeTop))}px`
  );
  wrapper.dataset.mobilePlacement = useBottomEdge ? 'bottom' : 'top';
}

function refreshCompactPopoverPlacement() {
  const wrapper = document.querySelector('.driver-popover.lexius-tour-popover');
  updateCompactPopoverPlacement(wrapper, currentHighlightedTourTarget());
}

function settleCompactPopoverPlacement(wrapper, remainingAttempts = 40) {
  if (!wrapper?.isConnected || remainingAttempts <= 0) return;
  updateCompactPopoverPlacement(wrapper, currentHighlightedTourTarget());
  if (remainingAttempts === 1) return;
  window.setTimeout(() => {
    settleCompactPopoverPlacement(wrapper, remainingAttempts - 1);
  }, 100);
}

function customizePopover(popover, { state } = {}) {
  popover.wrapper.setAttribute('role', 'dialog');
  popover.wrapper.setAttribute('aria-label', popover.title.textContent || 'Recorrido guiado');
  popover.closeButton.setAttribute('aria-label', 'Salir del recorrido');
  popover.closeButton.setAttribute('title', 'Salir del recorrido');
  popover.previousButton.setAttribute('aria-label', 'Paso anterior');
  popover.nextButton.setAttribute(
    'aria-label',
    popover.nextButton.classList.contains('driver-popover-done-btn') ? 'Finalizar recorrido' : 'Paso siguiente'
  );

  const skip = document.createElement('button');
  skip.type = 'button';
  skip.className = 'lexius-tour-skip';
  skip.textContent = 'Salir';
  skip.setAttribute('aria-label', 'Salir del recorrido');
  skip.addEventListener('click', () => finishActiveTour(false));
  popover.footer.insertBefore(skip, popover.footerButtons);
  updateCompactPopoverPlacement(popover.wrapper, state?.activeElement);
  window.requestAnimationFrame(() => settleCompactPopoverPlacement(popover.wrapper));
}

function setTourStartPending(pending) {
  tourStartPending = pending === true;
  document.querySelectorAll('[data-start-tour]').forEach(button => {
    button.disabled = tourStartPending;
    if (tourStartPending) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  });
  refreshTourUiState();
}

async function startTour(tourId, options = {}) {
  if (tourStartPending) return false;
  const definition = getTourDefinition(tourId);
  if (!definition) return false;
  const recordingTour = options.recording === true
    && isDocumentReviewRecordingSuggestion(tourId);
  if (activeSuggestion) dismissFeatureSuggestion({ restoreFocus: true });
  else cancelPendingSuggestion();
  setTourStartPending(true);
  setTourUiActive(true);
  rememberTourReturnFocus();
  let authState = null;
  let cleanup = () => { };
  let run = null;
  try {
    authState = await waitForAuthState();
    if (!authState.authenticated) {
      await showAuthGate();
      return false;
    }
    const stateLoaded = recordingTour || await ensureTourStateLoaded(authState);
    if (!recordingTour && (!stateLoaded || !toursAreEnabled(authState))) {
      announce('Los recorridos están desactivados en tu cuenta. Puedes activarlos desde Recorridos guiados.');
      return false;
    }
    if (definition.requiresDocument && !pageHasDocument()) {
      announce('Abre un documento para iniciar este recorrido.');
      await openTourCenter();
      return false;
    }
    if (definition.requiresChatResponse && !pageHasChatResponse()) {
      announce('Abre un chat que tenga una respuesta del Abogado Virtual para iniciar este recorrido.');
      await openTourCenter();
      return false;
    }
    if (definition.requiresDocumentEdition && !pageHasDocumentEditionWorkspace()) {
      announce('Abre un documento que tenga al menos dos versiones para iniciar este recorrido.');
      await openTourCenter();
      return false;
    }
    if (definition.requiresDocumentReview && !pageHasDocumentReviewWorkspace()) {
      announce('Abre la revisión de un documento para iniciar este recorrido.');
      await openTourCenter();
      return false;
    }
    if (definition.requiresSearchResults && !pageHasSearchResults()) {
      announce('Realiza una búsqueda con resultados para iniciar este recorrido.');
      await openTourCenter();
      return false;
    }
    if (definition.syncRequired) await waitForSyncState();
    if (activeRun) await finishActiveTour(false);

    await hideModal(document.getElementById('lexiusTourCenterModal'));
    closeSettingsPanel();

    if (typeof definition.prepare === 'function') {
      cleanup = await definition.prepare(authState) || cleanup;
    }
    if (
      getAuthState().subject !== authState.subject
      || (!recordingTour && !toursAreEnabled(authState))
    ) {
      await cleanup();
      announce('Los recorridos están desactivados en tu cuenta.');
      return false;
    }
    const steps = normalizeSteps(definition.buildSteps(authState));
    if (!steps.length) {
      await cleanup();
      announce('Este recorrido no está disponible en la pantalla actual.');
      return false;
    }

    run = {
      definition,
      authState,
      recording: recordingTour,
      automatic: options.automatic === true,
      completed: false,
      cleaned: false,
      cleanupPromise: null,
      cleanup,
      driver: null,
      responsiveRetargetTimers: [],
      keepAnnotationContextVisible: false,
      layoutKey: responsiveLayoutKey()
    };

    const driverInstance = driver({
      steps,
      animate: !prefersReducedMotion(),
      duration: prefersReducedMotion() ? 0 : 260,
      overlayColor: '#071f44',
      overlayOpacity: 0.67,
      smoothScroll: !prefersReducedMotion(),
      allowClose: true,
      allowScroll: true,
      overlayClickBehavior: 'close',
      stagePadding: 8,
      stageRadius: 12,
      disableActiveInteraction: true,
      allowKeyboardControl: true,
      popoverClass: 'lexius-tour-popover',
      popoverOffset: 12,
      showButtons: ['next', 'previous', 'close'],
      showProgress: true,
      progressText: '{{current}} de {{total}}',
      nextBtnText: 'Siguiente',
      prevBtnText: 'Anterior',
      doneBtnText: 'Finalizar',
      onPopoverRender: customizePopover,
      onCloseClick: () => finishActiveTour(false),
      onDoneClick: () => finishActiveTour(true),
      onDestroyStarted: () => { void finishActiveTour(false); },
      onDestroyed: () => { void cleanupRun(run); }
    });

    run.driver = driverInstance;
    activeRun = run;
    announce(`Recorrido ${definition.title} iniciado.`);
    driverInstance.drive();
    return true;
  } catch (error) {
    console.error('No se pudo iniciar el recorrido guiado:', error);
    if (run) {
      try {
        if (run.driver?.isActive()) run.driver.destroy();
        await cleanupRun(run);
      } catch (cleanupError) { }
    } else {
      try { await cleanup(); } catch (cleanupError) { }
    }
    announce('No se pudo iniciar el recorrido. Inténtalo nuevamente.');
    return false;
  } finally {
    setTourStartPending(false);
  }
}

async function finishActiveTour(completed) {
  const run = activeRun;
  if (!run) return Promise.resolve();
  run.completed = completed === true;
  if (run.completed && !run.recording) {
    void markTourCompleted(run.authState, run.definition.id);
  }
  if (run.driver && run.driver.isActive()) run.driver.destroy();
  return cleanupRun(run);
}

async function cleanupRun(run) {
  if (!run) return;
  if (run.cleanupPromise) return run.cleanupPromise;
  run.cleanupPromise = (async () => {
    if (run.cleaned) return;
    run.cleaned = true;
    let restoredCompletedSurface = false;
    (run.responsiveRetargetTimers || []).forEach(timer => window.clearTimeout(timer));
    run.responsiveRetargetTimers = [];
    try { await run.cleanup(); } catch (error) { }
    if (run.completed && run.definition.id === 'lawyer') {
      const chatModal = document.getElementById('chatModal');
      try {
        await showModal(chatModal);
        restoredCompletedSurface = !!chatModal?.classList.contains('show');
      } catch (error) { }
    }
    clearCompactTourFocus();
    if (activeRun === run) activeRun = null;
    refreshTourUiState();
    if (run.completed && run.definition.id === 'search-results') scrollMainContentToTop();
    announce(run.completed ? `Recorrido ${run.definition.title} finalizado.` : 'Recorrido cerrado.');
    scheduleDocumentEditionSuggestionCheck();
    scheduleDocumentReviewSuggestionCheck();
    scheduleChatResponseSuggestionCheck();
    if (!restoredCompletedSurface) window.setTimeout(focusTourFallback, 120);
  })();
  return run.cleanupPromise;
}

async function showAuthGate() {
  const element = document.getElementById('lexiusTourAuthModal');
  closeSettingsPanel();
  await showModal(element);
}

async function openTourCenter() {
  if (activeSuggestion) dismissFeatureSuggestion({ restoreFocus: true });
  else cancelPendingSuggestion();
  manualTourRequest = true;
  setTourUiActive(true);
  if (autoPromptTimer) window.clearInterval(autoPromptTimer);
  const authState = await waitForAuthState();
  if (!authState.authenticated) {
    await showAuthGate();
    return;
  }
  await ensureTourStateLoaded(authState, { force: true });
  await waitForSyncState();
  closeSettingsPanel();
  renderTourCenter(authState);
  await showModal(document.getElementById('lexiusTourCenterModal'));
}

async function showPlansFromTourCenter() {
  if (tourStartPending) return;
  setTourStartPending(true);
  try {
    await hideModal(document.getElementById('lexiusTourCenterModal'));
    const planButton = findVisible('.adquire-plan');
    if (planButton) planButton.click();
  } finally {
    setTourStartPending(false);
  }
}

function hasBlockingSurface() {
  const blockingModal = Array.from(document.querySelectorAll('.modal.show'))
    .some(modal => !TOUR_MODAL_IDS.has(modal.id));
  const settingsOpen = isVisible(document.querySelector('.custom-theme-bar'));
  const contextMenuOpen = isVisible(document.getElementById('context-menu'));
  const countryEntryOpen = !!document.querySelector('.lexius-country-entry.is-visible:not([hidden])');
  const countryConfirmationOpen = !!document.querySelector('.lexius-country-confirmation.is-visible:not([hidden])');
  const unifiedCountryRequired = document.documentElement.classList.contains('lexius-unified-country-required');
  return !!activeSuggestion
    || !!activeTourDisableConfirmation
    || blockingModal
    || settingsOpen
    || contextMenuOpen
    || countryEntryOpen
    || countryConfirmationOpen
    || unifiedCountryRequired;
}

function isDocumentReviewRecordingSuggestion(tourId) {
  if (tourId !== 'document-review' || !pageHasDocumentReviewWorkspace()) return false;
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.get('demo_recording') === '1'
      && params.get('demo_feature') === 'chats'
      && (
        params.get('demo_chat_start_from_document') === '1'
        || params.get('demo_chat_recording_flow') === 'document-review-tour'
      );
  } catch (error) {
    return false;
  }
}

function shouldSuppressAutomaticTour() {
  if (
    window.LEXIUS_SUPPRESS_AUTO_TOURS === true
    || window.__lexiusDisableDocDetailsPopup === true
    || !!window.__lexiusDemoRecording
    || window.__lexiusSearchDemoRecording === true
    || window.__lexiusIntelligentSummaryDemoRecording === true
  ) return true;
  const authState = getAuthState();
  if (authState.authenticated && !toursAreEnabled(authState)) return true;
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.get('demo_recording') === '1';
  } catch (error) {
    return false;
  }
}

function scheduleAutomaticInvitation(authState) {
  if (
    shouldSuppressAutomaticTour()
    || !toursAreEnabled(authState)
    || window.is_onboarding_first_open !== true
    || manualTourRequest
    || readAutoDecision(authState)
  ) return;
  let clearChecks = 0;
  let attempts = 0;
  if (autoPromptTimer) window.clearInterval(autoPromptTimer);
  autoPromptTimer = window.setInterval(async () => {
    attempts += 1;
    if (manualTourRequest || !toursAreEnabled(authState) || readAutoDecision(authState)) {
      window.clearInterval(autoPromptTimer);
      autoPromptTimer = null;
      return;
    }
    const pageContent = document.getElementById('page-content');
    const pageReady = pageContent && isVisible(pageContent) && !document.hidden;
    const userIsIdle = Date.now() - lastUserInteractionAt >= 2500;
    if (pageReady && userIsIdle && !hasBlockingSurface() && !activeRun) clearChecks += 1;
    else clearChecks = 0;
    if (clearChecks >= 3) {
      window.clearInterval(autoPromptTimer);
      autoPromptHandled = false;
      autoPromptPending = true;
      await showModal(document.getElementById('lexiusTourWelcomeModal'));
    } else if (attempts >= 180) {
      window.clearInterval(autoPromptTimer);
    }
  }, 500);
}

async function initializeAutomaticInvitation() {
  const authState = await waitForAuthState();
  if (!authState.authenticated) return;
  const stateLoaded = await ensureTourStateLoaded(authState);
  if (!stateLoaded || !toursAreEnabled(authState)) return;
  scheduleAutomaticInvitation(authState);
}

function bindEvents() {
  document.addEventListener('click', async event => {
    const helpTrigger = event.target.closest('[data-toggle="popover"]');
    if (helpTrigger) scheduleHelpPopoverEnhancement(helpTrigger);

    if (event.target.closest('[data-tour-disable-confirm]')) {
      event.preventDefault();
      closeTourDisableConfirmation(true);
      return;
    }

    if (event.target.closest('[data-tour-disable-cancel], [data-tour-disable-dismiss]')) {
      event.preventDefault();
      closeTourDisableConfirmation(false);
      return;
    }

    if (event.target.closest('[data-tour-suggestion-dismiss]')) {
      event.preventDefault();
      dismissFeatureSuggestion({ restoreFocus: true });
      return;
    }

    if (event.target.closest('[data-tour-suggestion-start]')) {
      event.preventDefault();
      const suggestion = activeSuggestion;
      if (!suggestion) return;
      const tourId = suggestion.tourId;
      const recording = suggestion.recording === true;
      dismissFeatureSuggestion({ restoreFocus: true });
      await startTour(tourId, { suggested: true, recording });
      return;
    }

    if (event.target.closest('[data-tour-suggestion-disable]')) {
      event.preventDefault();
      await updateToursEnabled(false);
      return;
    }

    const launcher = event.target.closest('[data-tour-launcher]');
    if (launcher) {
      event.preventDefault();
      lastLauncher = launcher;
      manualTourRequest = true;
      setTourUiActive(true);
      const containingModal = launcher.closest('.modal');
      if (containingModal) await hideModal(containingModal);
      else await wait(120);
      await openTourCenter();
      return;
    }

    const startButton = event.target.closest('[data-start-tour]');
    if (startButton) {
      event.preventDefault();
      await startTour(startButton.getAttribute('data-start-tour'));
      return;
    }

    const preferenceButton = event.target.closest('[data-tour-preference-toggle]');
    if (preferenceButton) {
      event.preventDefault();
      const authState = getAuthState();
      await updateToursEnabled(!toursAreEnabled(authState));
      return;
    }

    if (event.target.closest('[data-tour-plan]')) {
      event.preventDefault();
      await showPlansFromTourCenter();
      return;
    }

    if (event.target.closest('[data-tour-reset]')) {
      event.preventDefault();
      await clearTourProgress();
      return;
    }

    if (event.target.closest('[data-tour-auto-dismiss]')) {
      event.preventDefault();
      const authState = await waitForAuthState();
      autoPromptHandled = true;
      autoPromptPending = false;
      void saveAutoDecision(authState, 'dismissed');
      await hideModal(document.getElementById('lexiusTourWelcomeModal'));
      return;
    }

    if (event.target.closest('[data-tour-auto-start]')) {
      event.preventDefault();
      const authState = await waitForAuthState();
      autoPromptHandled = true;
      autoPromptPending = false;
      void saveAutoDecision(authState, 'dismissed');
      await hideModal(document.getElementById('lexiusTourWelcomeModal'));
      await startTour('overview', { automatic: true });
    }
  });

  document.addEventListener('focusin', event => {
    const helpTrigger = event.target.closest?.('[data-toggle="popover"]');
    if (helpTrigger) scheduleHelpPopoverEnhancement(helpTrigger);
  });

  const welcome = document.getElementById('lexiusTourWelcomeModal');
  welcome.addEventListener('hidden.bs.modal', async () => {
    if (!autoPromptPending || autoPromptHandled) return;
    autoPromptPending = false;
    const authState = await waitForAuthState();
    void saveAutoDecision(authState, 'dismissed');
  });

  TOUR_MODAL_IDS.forEach(modalId => {
    const modal = document.getElementById(modalId);
    if (!modal) return;
    modal.addEventListener('hidden.bs.modal', () => {
      refreshTourUiState();
      window.setTimeout(() => {
        if (!activeRun && !tourStartPending && !hasOpenTourModal()) focusTourFallback();
      }, 80);
    });
  });

  ['pointerdown', 'keydown', 'input', 'touchstart'].forEach(eventName => {
    document.addEventListener(eventName, () => {
      lastUserInteractionAt = Date.now();
    }, { capture: true, passive: true });
  });

  window.addEventListener('sync-status-updated', () => {
    const center = document.getElementById('lexiusTourCenterModal');
    if (!center?.classList.contains('show')) return;
    const authState = getAuthState();
    if (authState.authenticated) renderTourCenter(authState);
  });

  window.addEventListener('lexius-tour-state-invalidated', event => {
    scheduleTourStateRefresh(150, 0, event.detail?.reset === true);
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && tourStateLoaded) scheduleTourStateRefresh(50);
  });

  window.addEventListener('resize', () => {
    if (tourResizeFrame) window.cancelAnimationFrame(tourResizeFrame);
    tourResizeFrame = window.requestAnimationFrame(() => {
      tourResizeFrame = null;
      const run = activeRun;
      if (!run?.driver?.isActive()) return;
      const nextLayoutKey = responsiveLayoutKey();
      const activeIndex = run.driver.getActiveIndex();
      if (run.layoutKey === nextLayoutKey) {
        run.driver.refresh();
        window.requestAnimationFrame(refreshCompactPopoverPlacement);
        if (run.definition.id === 'search-results') {
          settleSearchResultsTargetAfterResize(run, activeIndex);
        }
        return;
      }

      run.layoutKey = nextLayoutKey;
      if (run.definition.id === 'search') refreshSearchSurfaceForLayout();
      if (run.definition.id === 'lawyer') {
        if ([1, 2, 3].includes(activeIndex)) openChatSidebarForTour();
        else closeChatSidebarForTour();
      }
      if (Number.isInteger(activeIndex)) {
        run.driver.moveTo(activeIndex);
        if (run.definition.id === 'search-results') {
          settleSearchResultsTargetAfterResize(run, activeIndex);
        }
      }
      else {
        run.driver.refresh();
        window.requestAnimationFrame(refreshCompactPopoverPlacement);
      }
    });
  });

  document.addEventListener('scroll', () => {
    if (tourScrollFrame) window.cancelAnimationFrame(tourScrollFrame);
    tourScrollFrame = window.requestAnimationFrame(() => {
      tourScrollFrame = null;
      const run = activeRun;
      if (!run?.driver?.isActive()) return;
      run.driver.refresh();
      window.requestAnimationFrame(refreshCompactPopoverPlacement);
    });
  }, { capture: true, passive: true });
}

function initializeTourState() {
  return (async () => {
    const authState = await waitForAuthState();
    if (!authState.authenticated) {
      resetTourState();
      return true;
    }
    return ensureTourStateLoaded(authState);
  })();
}

function initialize() {
  installModals();
  bindEvents();
  bindFeatureSuggestionTriggers();
  const stateReady = initializeTourState();
  window.LexiusTours = {
    version: ONBOARDING_VERSION,
    ready: stateReady,
    whenStateReady: () => initializeTourState(),
    refreshState: async () => {
      const authState = await waitForAuthState();
      const refreshPromise = ensureTourStateLoaded(authState, { force: true });
      window.LexiusTours.ready = refreshPromise;
      return refreshPromise;
    },
    openCenter: openTourCenter,
    start: startTour,
    suggestForRecording: showRecordingFeatureSuggestion,
    close: () => finishActiveTour(false),
    isActive: () => !!activeRun,
    isSuggestionVisible: () => !!activeSuggestion,
    isEnabled: () => toursAreEnabled(getAuthState()),
    setEnabled: enabled => updateToursEnabled(enabled),
    getProgress: tourId => getTourProgress(getAuthState(), tourId),
    getAuthState,
    definitions: TOUR_DEFINITIONS.map(({
      id,
      title,
      planRequired,
      syncRequired,
      requiresDocument,
      requiresChatResponse,
      requiresDocumentEdition,
      requiresDocumentReview,
      requiresSearchResults
    }) => ({
      id,
      title,
      planRequired: !!planRequired,
      syncRequired: !!syncRequired,
      requiresDocument: !!requiresDocument,
      requiresChatResponse: !!requiresChatResponse,
      requiresDocumentEdition: !!requiresDocumentEdition,
      requiresDocumentReview: !!requiresDocumentReview,
      requiresSearchResults: !!requiresSearchResults
    }))
  };
  window.dispatchEvent(new CustomEvent('lexius-tours-ready'));
  initializeAutomaticInvitation();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
